import axios from 'axios';
import * as XLSX from 'xlsx';
import { url, urlV2, endpoints, isLocalEnv, aiPilotUrl } from './config';
import jwt_decode from 'jwt-decode';
import moment from 'moment';
import { getIsV2 } from 'utils/versionToggle';
import { EventSourcePolyfill } from "event-source-polyfill";
import { apiRequest } from './apiRequestManager';
import { FILE_LABELS } from 'utils/common-util';
import { DashboardFilterConfig, DashboardFilterResponse } from 'hooks/constants/dashboardConfig';

const sessionCookie = 'id_token';

export const getAuthorazieHeader = () => {
  const headers = {
    'Content-Type': 'application/json;charset=UTF-8',
    authorization: `Bearer`
  };
  const jwtToken = getCookie(sessionCookie);
  if (jwtToken) {
    headers.authorization = 'Bearer ' + jwtToken;
  }
  return headers;
};



export const setCookie = (cname: string, cvalue: string) => {
  if (isLocalEnv) {
    document.cookie = cname + '=' + cvalue + ';path=/;Secure';
  } else {
    document.cookie = cname + '=' + cvalue + ';path=/;HttpOnly;Secure';
  }
};

export const getCookie = (cname: string) => {
  const name = cname + '=';
  const ca = document.cookie.split(';');
  for (let c of ca) {
    while (c.startsWith(' ')) {
      c = c.substring(1);
    }
    if (c.startsWith(name)) {
      return c.substring(name.length, c.length);
    }
  }
  return '';
};

export const deleteCookie = (name: string) => {
  localStorage.removeItem('jobState');
  localStorage.removeItem('alertJobState');
  localStorage.removeItem('jobExecutionsState');
  localStorage.removeItem('alertJobExecutionsState');
  localStorage.removeItem('selectedAlert');
  localStorage.removeItem('jobRunsState');
  localStorage.removeItem('sso-login');
  localStorage.removeItem('filterSectors');
  localStorage.removeItem('sectorsMasterData');
  // Clear all AI chat session data from both storages
  const toRemoveSession: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k?.startsWith('pepwise_')) toRemoveSession.push(k);
  }
  toRemoveSession.forEach((k) => sessionStorage.removeItem(k));

  localStorage.removeItem('selectedSector');
  let redirect: any = localStorage.getItem('redirectAfterLogin')
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && (!key.includes('_user_col_pref') && !key.includes('query_search_history') && !key.includes('pepwise_tour_seen') && !key.includes('theme'))) {
      localStorage.removeItem(key);
      // Adjust the index since localStorage shrinks when items are removed
      i--;
    }
  }
  localStorage.setItem('redirectAfterLogin', redirect)
  document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;';
  window.location.href = '/';
  return 'Cookie Deleted Successfully';
};

export const getDecodedToken = () => {
  const jwtToken = getCookie('id_token');
  if (!jwtToken) {
    return false;
  }
  const decoded: any = jwt_decode(jwtToken);
  localStorage.removeItem('defaultSector');
  localStorage.setItem('defaultSector', decoded.sector?.name);
  if (decoded.email.toLowerCase() == 'amlanmaitrabroadcast@pepsico.com' || decoded.email.toLowerCase() == 'amlan_maitra@pepsico.com'
  ) {
    console.log('inside demo user changes');
    decoded.role = [{ "name": "D&AI Executive", "capabilities": ['Data Estate', 'Data Ops'] }]
  }
  return decoded;
};

export const landingPage: any = {
  'Platform Operations Admin/SRE': '/LandingPage',
  'PepWise System User': '/LandingPage',
  'Data Operations Admin': '/LandingPage',
  'Data Operations User': '/LandingPage',
  'D&AI Executive User': '/dataestate/executive-summary',
  'Program Executive': '/dashboard/programExecutiveLanding',
  'FinOps User': '/finops/executive'
};

export const login = async (data: { email: string; password: string }) => {
  try {
    const res = await axios.post(`${url}${endpoints.login}`, data);
    if (res?.data?.success) {
      localStorage.setItem('defaultSector', res.data.data.user.sector['name']);
      const token = res.data.data.id_token;
      setCookie('id_token', token);
    }
    return res.data;
  } catch (err) {
    return err;
  }
};

const extractSemanticData = (parsed: any) => {
  if (parsed?.semantic_result) {
    return typeof parsed.semantic_result === 'string' ? JSON.parse(parsed.semantic_result) : parsed.semantic_result;
  }
  if (parsed?.semanticResult) {
    return typeof parsed.semanticResult === 'string' ? JSON.parse(parsed.semanticResult) : parsed.semanticResult;
  }
  if (parsed?.semantic_data) {
    return typeof parsed.semantic_data === 'string' ? JSON.parse(parsed.semantic_data) : parsed.semantic_data;
  }
  if (parsed?.visualization || parsed?.clarification_options || parsed?.follow_up_suggestions) {
    return parsed;
  }
  return null;
};

const parseSingleSseLine = (
  line: string,
  onSemanticResult?: (result: any) => void
): { content?: string; rawData?: string } | null => {
  const trimmed = line.trim();
  if (!trimmed.startsWith('data:')) return null;

  const dataPart = trimmed.replace(/^data:\s*/, '');
  if (!dataPart || dataPart === '[DONE]') return null;

  try {
    const parsed = JSON.parse(dataPart);
    const content = parsed?.content || parsed?.delta || parsed?.message || '';
    const semanticData = extractSemanticData(parsed);

    if (semanticData && onSemanticResult) {
      onSemanticResult(semanticData);
    }
    return { content };
  } catch {
    return { rawData: dataPart };
  }
};

const handleSseStream = async (
  body: any,
  parseSseEventChunk: (block: string) => string,
  options?: { onChunk?: (chunk: string) => void }
): Promise<string> => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let aggregate = '';
  let pending = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    pending += decoder.decode(value, { stream: true });
    const events = pending.split('\n\n');
    pending = events.pop() || '';

    events.forEach((eventBlock) => {
      const chunk = parseSseEventChunk(eventBlock);
      if (chunk) {
        aggregate += chunk;
        options?.onChunk?.(chunk);
      }
    });
  }

  if (pending.trim()) {
    const finalChunk = parseSseEventChunk(pending);
    if (finalChunk) {
      aggregate += finalChunk;
      options?.onChunk?.(finalChunk);
    }
  }

  return aggregate.trim();
};

const conversationalChatAiPilot = async (
  aiPilotBaseUrl: string,
  conversationalPath: string,
  headers: any,
  normalizedRequest: any,
  parseSseEventChunk: (block: string) => string,
  options?: { onChunk?: (chunk: string) => void; onSemanticResult?: (result: any) => void }
) => {
  const response = await fetch(`${aiPilotBaseUrl}${conversationalPath}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(normalizedRequest),
  });
  const contentType = response.headers.get('content-type') || '';

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'AI Pilot request failed');
  }

  if (contentType.includes('text/event-stream')) {
    if (response.body) {
      return await handleSseStream(response.body, parseSseEventChunk, options);
    }

    const fallbackSseText = await response.text();
    const parsedFallbackSse = parseSseEventChunk(fallbackSseText);
    if (parsedFallbackSse) {
      options?.onChunk?.(parsedFallbackSse);
    }
    return parsedFallbackSse || fallbackSseText;
  }

  const responseText = await response.text();

  try {
    const parsed = JSON.parse(responseText);
    const semanticData = extractSemanticData(parsed);

    if (semanticData && options?.onSemanticResult) {
      options.onSemanticResult(semanticData);
    }
    return parsed;
  } catch {
    return responseText;
  }
};

const conversationalChatAxios = async (
  conversationalPath: string,
  normalizedRequest: any,
  headers: any,
  options?: { onSemanticResult?: (result: any) => void }
) => {
  const res = await axios.post(`${url}${conversationalPath}`, normalizedRequest, { headers });
  const resData = res.data;
  let resSemanticData = null;
  if (resData?.semantic_result) resSemanticData = typeof resData.semantic_result === 'string' ? JSON.parse(resData.semantic_result) : resData.semantic_result;
  else if (resData?.semanticResult) resSemanticData = typeof resData.semanticResult === 'string' ? JSON.parse(resData.semanticResult) : resData.semanticResult;
  else if (resData?.semantic_data) resSemanticData = typeof resData.semantic_data === 'string' ? JSON.parse(resData.semantic_data) : resData.semantic_data;
  else if (resData?.visualization) resSemanticData = resData;

  if (resSemanticData && options?.onSemanticResult) {
    options.onSemanticResult(resSemanticData);
  }
  return res.data;
};

export const conversationalChat = async (
  sessionId: string,
  requestBody: any,
  options?: { onChunk?: (chunk: string) => void; onSemanticResult?: (result: any) => void }
) => {
  const headers = getAuthorazieHeader();
  const conversationalPath = `${endpoints.conversationalChat}/${sessionId}/conversational.chat`;
  const aiPilotBaseUrl = aiPilotUrl;
  const normalizedRequest = {
    chatId: requestBody?.chatId || sessionId,
    messages: Array.isArray(requestBody?.messages)
      ? requestBody.messages
      : [
        {
          role: 'user',
          content: [{ type: 'text', text: requestBody?.query || '' }],
        },
      ],
  };

  const parseSseEventChunk = (eventBlock: string) => {
    let responseText = '';
    const lines = eventBlock.split('\n');

    lines.forEach((line) => {
      const parsedLine = parseSingleSseLine(line, options?.onSemanticResult);
      if (parsedLine) {
        if (parsedLine.content) {
          responseText += parsedLine.content;
        }
        if (parsedLine.rawData) {
          responseText += parsedLine.rawData;
        }
      }
    });

    return responseText;
  };

  try {
    if (aiPilotBaseUrl) {
      return await conversationalChatAiPilot(
        aiPilotBaseUrl,
        conversationalPath,
        headers,
        normalizedRequest,
        parseSseEventChunk,
        options
      );
    }

    return await conversationalChatAxios(
      conversationalPath,
      normalizedRequest,
      headers,
      options
    );
  } catch (err) {
    return err;
  }
};

export interface SessionFeedbackPayload {
  rating: number;
  comment?: string;
  query_count?: number;
  query_types?: string[];
}

/**
 * Submit a 1–5 Likert rating (and optional comment) for a completed session.
 * Throws on network or server error so the caller can show a retry UI.
 */
export const submitSessionFeedback = async (
  sessionId: string,
  payload: SessionFeedbackPayload
): Promise<void> => {
  const headers = getAuthorazieHeader();
  headers['Content-Type'] = 'application/json';
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return;

  const res = await fetch(
    `${aiPilotBaseUrl}/sessions/${encodeURIComponent(sessionId)}/feedback`,
    {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    }
  );

  if (!res.ok) {
    const errText = await res.text().catch(() => res.statusText);
    throw new Error(errText || 'Feedback submission failed');
  }
};

/**
 * Log a skip event for a session.
 * Non-fatal — callers should always allow the user to exit even if this fails.
 */
export const skipSessionFeedback = async (sessionId: string): Promise<void> => {
  const headers = getAuthorazieHeader();
  headers['Content-Type'] = 'application/json';
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return;

  try {
    await fetch(
      `${aiPilotBaseUrl}/sessions/${encodeURIComponent(sessionId)}/feedback/skip`,
      { method: 'POST', headers, body: JSON.stringify({}) }
    );
  } catch {
    // Non-fatal — skip events are best-effort
  }
};

export const submitTurnFeedback = async (
  sessionId: string,
  turnId: string,
  feedback: 'up' | 'down' | null
): Promise<void> => {
  const headers = getAuthorazieHeader();
  headers['Content-Type'] = 'application/json';
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return;

  const res = await fetch(
    `${aiPilotBaseUrl}/sessions/${encodeURIComponent(sessionId)}/turns/${encodeURIComponent(turnId)}/feedback`,
    {
      method: 'PATCH',
      headers,
      body: JSON.stringify({ feedback }),
    }
  );

  if (!res.ok) {
    const errText = await res.text().catch(() => res.statusText);
    throw new Error(errText || 'Turn feedback submission failed');
  }
};

export const jobMapping = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.jobMapping}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const jobListingFilter = async (page: string, size: string, sortingInfo: any, filterInfo: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobListingFilter}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const jobListing = async (page: string, size: string, sortingInfo: any, filterInfo: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobListing}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobSearch = async (searchDetails: any, filterInfo: any, columnKey: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    sortBy: '',
    orderBy: '',
    filter: filterInfo,
    queryText: searchDetails,
    columnKey: columnKey
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobListing}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobFilterList = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.jobFilter}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getJobOverview = async (jobId: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.jobOverview}?jobId=${jobId}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getJobRunHistory = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  jobId: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    jobId: jobId,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobRunHistory}`, requestBody, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getJobLineage = async (sortingInfo: any, jobId: string) => {
  const headers = getAuthorazieHeader();
  let queryString = '';
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.jobDependencies}?jobId=${jobId}${queryString}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const updateJob = async (patchData: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.jobUpdate}`, patchData, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const updateJobRunHistory = async (patchData: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.patch(`${url}${endpoints.jobRunUpdate}`, patchData, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getJobExecutionsFilter = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobExecutionsFilter}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getJobExecutions = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  isDataestate?: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    isDataEstate: isDataestate
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobExecutions}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const postUserFeedback = async (data: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.userFeedback}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobSearchForExecutions = async (
  searchDetails: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 50,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    sortBy: 'name',
    orderBy: 'asc',
    filter: filterInfo,
    jobSearch: searchDetails
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobExecutions}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getJobFutureExecutions = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    fromDateTime: moment(fromDateTime),
    toDateTime: moment(toDateTime),
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobFutureExecutions}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getJobFutureExecutionsFilter = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    fromDateTime: moment(fromDateTime),
    toDateTime: moment(toDateTime),
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobFutureExecutionsFilter}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const jobSearchForSchedules = async (
  searchDetails: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    fromDateTime: moment(fromDateTime),
    toDateTime: moment(toDateTime),
    sortBy: 'name',
    orderBy: 'asc',
    filter: filterInfo,
    jobSearch: searchDetails
  };
  try {
    const res = await axios.post(`${url}${endpoints.jobFutureExecutions}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getjobRunSchedules = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime: any,
  toDateTime: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&fromDateTime=${fromDateTime}&toDateTime=${toDateTime}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  try {
    const res = await axios.get(`${url}${endpoints.jobRunSchedules}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobSearchForRunSchedules = async (
  searchDetails: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${1}&size=${100}&fromDateTime=${fromDateTime}&toDateTime=${toDateTime}`;
  queryString = `${queryString}&sortBy='name'`;
  queryString = `${queryString}&orderBy='asc'`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  if (searchDetails) queryString = `${queryString}&jobSearch=${searchDetails}`;
  try {
    const res = await axios.get(`${url}${endpoints.jobRunSchedules}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getJobExecutionsMetrices = async (fromDateTime: string, toDateTime: string) => {
  const headers = getAuthorazieHeader();
  const queryString = `fromDateTime=${fromDateTime}&toDateTime=${toDateTime}`;
  try {
    const res = await axios.get(`${url}${endpoints.jobExecutionsMetrices}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getBoxRunsList = async (boxName: any, fromDateTime: any, toDateTime: any, scheduleTime: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    boxName: boxName,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime
  };
  try {
    const res = await axios.post(`${url}${endpoints.boxRunsList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSector = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.sector}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSectorSql = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.sectorSql}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDatabricksSectors = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.databricksSectors}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getReasons = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.reasonsOfSla}?${queryString}`, { headers });
    console.log(res, 'resons');
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSystem = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.system}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getProcess = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.process}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getMachines = async (page: string, size: string, sortingInfo: any, filterInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  try {
    const res = await axios.get(`${url}${endpoints.machines}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getReports = async (forDate: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.reports}?forDate=${forDate}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getActionTakenData = async (fromDateTime: string, toDateTime: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.jobExeActionTaken}?fromDateTime=${fromDateTime}&toDateTime=${toDateTime}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const businessDashboard = async (forDate: any, sector: any, scheduler: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.get(
      `${url}${endpoints.business}?forDate=${forDate}&sector=${sector}&scheduler=${scheduler}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getProcessRuns = async (processId: any, forDate: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.processRuns}?processId=${processId}&forDate=${forDate}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const businessDashboardPieChart = async (forDate: any, sector: any, scheduler: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.businessPiechart}?forDate=${forDate}&sector=${sector}&scheduler=${scheduler}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const dailyCritialPieChart = async (forDate: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.dailyCritical}?forDate=${forDate}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getJobDependenciesChart = async (jobId: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.jobDependencyChart}?jobId=${jobId}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDependencyGraph = async (market: string, businessProgram?: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getDependencyGraph}`, { market, businessProgram }, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks summary
export const getDBSummaryDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;
  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    sortDir: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Summary date filter
export const jobSearchForDBSummary = async ({
  pageNumber,
  size,
  sortingInfo,
  fromDateTime,
  filterInfo,
  sector,
  searchQuery,
  searchField
}: {
  pageNumber: any;
  size: any;
  sortingInfo: any;
  fromDateTime: any;
  filterInfo: any;
  sector: any;
  searchQuery: any;
  searchField: any;
}) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;
  let storedSectorFilter = localStorage.getItem('ds-summary-sector-filter')
    ? JSON.parse(localStorage.getItem('ds-summary-sector-filter') ?? '')
    : null;
  if (storedSectorFilter) {
    sector = storedSectorFilter;
  }
  const requestBody = {
    pageNumber,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    sortDir: sortingInfo.sortDir,
    filter: filterInfo,
    searchQuery,
    searchField
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks workspace spent utilization
export const getDBWorkspaceSpentDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  
  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksWorkspaceSpent}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Cost summary Workspace spent
// export const jobSearchForDBWorkspace = async (
// 	searchDetails: any,
// 	filterInfo: any,
// 	fromDateTime: string,
// 	toDateTime: string
// ) => {
// 	const headers = getAuthorazieHeader();
// 	let queryString = `page=${1}&size=${100}&fromDateTime=${moment(fromDateTime).format('YYYY-MM-DD')}&toDateTime=${moment(toDateTime).format('YYYY-MM-DD')}`;
// 	queryString = `${queryString}&sortBy='name'`;
// 	queryString = `${queryString}&orderBy='asc'`;
// 	if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
// 	if (searchDetails) queryString = `${queryString}&jobSearch=${searchDetails}`;
// 	try {
// 		const res = await axios.get(`${url}${endpoints.databricksWorkspaceSpent}?${queryString}`, { headers });
// 		return res.data;
// 	} catch (err) {
// 		return err;
// 	}
// };

export const jobSearchForDBWorkspace = async (
  searchDetails: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string,
  searchQuery: any,
  searchField: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    toDateTime: moment(toDateTime).format('YYYY-MM-DD'),
    sortBy: 'name',
    orderBy: 'asc',
    filter: filterInfo,
    searchField: searchQuery,
    searchQuery: searchDetails
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksWorkspaceSpent}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks project spent utilization
export const getDBProjectSpentDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  
  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksProjectSpent}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Cost summary Project Spent
// export const jobSearchForProjectSpent = async (
// 	searchDetails: any,
// 	filterInfo: any,
// 	fromDateTime: string,
// 	toDateTime: string
// ) => {
// 	const headers = getAuthorazieHeader();
// 	let queryString = `page=${1}&size=${100}&fromDateTime=${moment(fromDateTime).format('YYYY-MM-DD')}&toDateTime=${moment(toDateTime).format('YYYY-MM-DD')}`;
// 	queryString = `${queryString}&sortBy='name'`;
// 	queryString = `${queryString}&orderBy='asc'`;
// 	if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
// 	if (searchDetails) queryString = `${queryString}&jobSearch=${searchDetails}`;
// 	try {
// 		const res = await axios.get(`${url}${endpoints.databricksProjectSpent}?${queryString}`, { headers });
// 		return res.data;
// 	} catch (err) {
// 		return err;
// 	}
// };

export const jobSearchForProjectSpent = async (
  searchDetails: any,
  filterInfo: any,
  fromDateTime: string,
  toDateTime: string,
  searchQuery: any,
  searchField: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    toDateTime: moment(toDateTime).format('YYYY-MM-DD'),
    sortBy: 'name',
    orderBy: 'asc',
    filter: filterInfo,
    searchField: searchQuery,
    searchQuery: searchDetails
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksProjectSpent}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Stale Users List
export const getStaleUsersDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  
  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.governanceStaleUsers}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Stale Users Search
export const StaleUsersSearch = async (
  page: any,
  size: any,
  filterInfo: any,
  fromDateTime?: any,
  sector?: any,
  searchQuery?: any,
  searchField?: any
) => {
  console.log(filterInfo);
  console.log(searchQuery, searchField);
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined
      ? localStorage.getItem('processedDate')
      : moment(fromDateTime).format('YYYY-MM-DD');
  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    filter: filterInfo,
    searchQuery: searchQuery,
    searchField: searchField
  };

  try {
    const res = await axios.post(`${url}${endpoints.governanceStaleUsers}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Governance Jobs List
export const getGovernanceRunningJobsDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  sector?: any,
  filterByColumnInfo?: any
) => {
  const headers = getAuthorazieHeader();
  
  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    filterBy: filterByColumnInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.governanceRunningJobs}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Governance Jobs Search
export const GovernanceRunningjobSearch = async ({
  page,
  size,
  fromDateTime,
  filterInfo,
  sector,
  searchQuery,
  searchField,
  filterByColumnInfo
}: {
  page: any;
  size: any;
  fromDateTime: string;
  filterInfo: any;
  sector: any;
  searchQuery: string;
  searchField: string;
  filterByColumnInfo?: any;
}) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined
      ? localStorage.getItem('processedDate')
      : moment(fromDateTime).format('YYYY-MM-DD');
  
  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    filter: filterInfo,
    filterBy: filterByColumnInfo,
    searchField: searchField,
    searchQuery: searchQuery
  };

  try {
    const res = await axios.post(`${url}${endpoints.governanceRunningJobs}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Jobs List
export const getDatabricksJobsDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  sector?: any,
  drillDownStatusFilter?: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    filter: {}
  };

  if (drillDownStatusFilter && Object.keys(drillDownStatusFilter).length) {
    requestBody['filter'] = drillDownStatusFilter;
  }

  try {
    const res = await axios.post(`${url}${endpoints.databricksJobs}`, requestBody, { headers });
    console.log(res.data, 'res.data==');
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Jobs date filter
export const databricksJobSearch = async (
  searchQuery: any,
  searchField: any,
  fromDateTime: string,
  filterInfo: any,
  sector: any,
  drillDownStatusFilter: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page: '1',
    size: '50',
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    filterBy: filterInfo,
    searchField: searchField,
    searchQuery: searchQuery,
    filter: {}
  };

  if (drillDownStatusFilter && Object.keys(drillDownStatusFilter).length) {
    requestBody['filter'] = drillDownStatusFilter;
  }

  try {
    const res = await axios.post(`${url}${endpoints.databricksJobs}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const getWorkbooks = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  drillDownFilter,
  fromDate,
  sector,
  instance,
  searchQuery,
  searchField
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  drillDownFilter: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
  searchQuery?: any;
  searchField?: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page,
    size,
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    drillDownFilter,
    searchQuery,
    searchField
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauWorkbooks}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getTableauUsers = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  fromDate,
  sector,
  instance,
  searchQuery,
  searchField
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
  searchQuery?: any;
  searchField?: any;
}) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&fromDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&sectors=${sector}&instance=${instance}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  if (searchField && searchQuery?.length >= 3)
    queryString = `${queryString}&searchField=${searchField}&searchQuery=${searchQuery}`;
  if (searchField) queryString = `${queryString}&searchField=${searchField}`;
  try {
    const res = await axios.get(`${url}${endpoints.tableauUsers}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// MLOBS SERVICES //
export const getModels = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  drillDownStatus: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  if (drillDownStatus) queryString = `${queryString}&drillDownStatus=${drillDownStatus}`;
  try {
    const res = await axios.get(`${url}${endpoints.models}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getModel = async (modelId: any) => {
  try {
    const res = await axios.get(`${url}${endpoints.model}/${modelId}`);
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getModelPerformanceMetrics = async (modelId: any, inferenceId: any) => {
  try {
    const res = await axios.get(`${url}${endpoints.modelPerformanceMetric}/${modelId}/${inferenceId}`);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getModelfeatureDriftMetrics = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  modelId: any,
  inferenceId: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  try {
    const res = await axios.get(
      `${url}${endpoints.modelfeatureDriftMetrics}/${modelId}/${inferenceId}?${queryString}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};
// MLOBS SERVICES //

// Databricks Resource Opt- CPU
export const getDBCPUUtilizationDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksCPUUtilization}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobSearchCPUUtilization = async (
  searchQuery: any,
  searchField: any,
  fromDateTime: any,
  filterInfo: any,
  sector: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page: '1',
    size: '50',
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    filter: filterInfo,
    searchField: searchField,
    searchQuery: searchQuery
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksCPUUtilization}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Resource opt Memory Utilization
export const getDBMemoryUtilizationDate = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page,
    size,
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksMemoryUtilization}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobSearchMemoryUtilization = async (
  searchQuery: any,
  searchField: any,
  fromDateTime: any,
  filterInfo: any,
  sector: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page: '1',
    size: '50',
    fromDateTime: moment(fromDateTime).format('YYYY-MM-DD'),
    sector: sector,
    filter: filterInfo,
    searchField: searchField,
    searchQuery: searchQuery
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksMemoryUtilization}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau Performance
export const getTableauJobs = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDate?: string,
  sector?: any,
  instance?: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauPerformance}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const jobSearchTableauJobs = async ({
  pageNumber,
  size,
  sortingInfo,
  fromDate,
  filterInfo,
  sector,
  instance,
  searchQuery,
  searchField
}: {
  pageNumber: any;
  size: any;
  sortingInfo: any;
  fromDate: string;
  filterInfo: any;
  sector: any;
  instance: any;
  searchQuery: any;
  searchField: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(pageNumber),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    searchQuery: searchQuery,
    searchField: searchField
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauPerformance}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau workbook search
export const SearchForTableauWorkbooks = async (searchDetails: any, filterInfo: any, fromDate: string) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${1}&size=${100}&fromDate=${moment(fromDate).format('YYYY-MM-DD')}`;
  queryString = `${queryString}&sortBy='name'`;
  queryString = `${queryString}&orderBy='asc'`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  if (searchDetails) queryString = `${queryString}&jobSearch=${searchDetails}`;
  try {
    const res = await axios.get(`${url}${endpoints.tableauWorkbooks}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// PCOBS SERVICES

export const getResourceGroups = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  drillDownTaggedToProject,
  drillDownSector,
  drillDownBySector,
  drillDownTaggedToSector,
  resource_group_id,
  whereFilter
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  drillDownTaggedToProject: any;
  drillDownSector: any;
  drillDownBySector: any;
  drillDownTaggedToSector: any;
  resource_group_id: any;
  whereFilter?: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    page,
    size,
    resource_group_id: resource_group_id,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    isResourceTaggedToProject: drillDownTaggedToProject === 'Resources not tagged' ? false : '',
    isResourceGroupTaggedToSector: drillDownTaggedToSector === 'Resource groups not tagged' ? false : '',
    whereFilter: whereFilter?.column ? encodeURIComponent(whereFilter.column) : ''
  };

  if (drillDownTaggedToProject) {
    requestBody.isResourceTaggedToProject = drillDownTaggedToProject !== 'Resources not tagged';
  }

  if (drillDownTaggedToSector) {
    requestBody.isResourceGroupTaggedToSector = drillDownTaggedToSector !== 'Resource groups not tagged';
  }

  const filter: any = { ...filterInfo };
  if (drillDownSector) {
    filter.DFSectorname = [drillDownSector];
  } else if (drillDownBySector) {
    filter.DFSectorname = [drillDownBySector];
  }

  // Add filter to requestBody if it's not empty
  if (Object.keys(filter).length) {
    requestBody.filter = filter;
  }
  try {
    const res = await axios.post(`${url}${endpoints.resourceGroups}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
// Search Resource List Filter column

export const getResourceSearchFilterColumn = async ({
  filterInfo,
  drillDownTaggedToProject,
  drillDownSector,
  drillDownBySector,
  drillDownTaggedToSector,
  resource_group_id,
  searchQuery,
  searchField
}: {
  filterInfo: any;
  drillDownTaggedToProject: any;
  drillDownSector: any;
  drillDownBySector: any;
  drillDownTaggedToSector: any;
  resource_group_id: any;
  searchQuery: string;
  searchField: string;
}) => {
  console.log(searchQuery + ' ' + searchField);
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    filter: filterInfo,
    resource_group_id,
    searchQuery,
    searchField,
    isResourceTaggedToProject: drillDownTaggedToProject === 'Resources not tagged' ? false : '',
    isResourceGroupTaggedToSector: drillDownTaggedToSector === 'Resource groups not tagged' ? false : '',
    page: '1',
    size: '100'
  };

  const filter: any = {};
  const sectorVal = drillDownSector || drillDownBySector;
  const hasFilterInfo = Object.keys(filterInfo).length > 0;

  if (hasFilterInfo) {
    Object.assign(filter, filterInfo);
    if (sectorVal) {
      filter.DFSectorname = [sectorVal];
    }
  } else if (sectorVal && sectorVal !== 'unknown') {
    filter.DFSectorname = [sectorVal];
  }

  // Add filter to requestBody if it's not empty
  if (Object.keys(filter).length) {
    requestBody.filter = filter;
  }

  try {
    const res = await axios.post(`${url}${endpoints.resourceGroups}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const patchResourceGroup = async (patchData: any) => {
  const headers = getAuthorazieHeader();
  patchData.id = [patchData.id];
  try {
    const res = await axios.post(`${url}${endpoints.resourceGroup}`, patchData, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getResourceGroupSectors = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page,
    size,
    sortingInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.resourceGroupSectors}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getResources = async (
  resourceGroupId: string,
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  drillDownStatus: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) queryString = `${queryString}&filter=${JSON.stringify(filterInfo)}`;
  if (drillDownStatus) queryString = `${queryString}&drillDownStatus=${drillDownStatus}`;
  try {
    const res = await axios.get(
      `${url}${endpoints.resourceGroup}?resource_group_id=${resourceGroupId}&&${queryString}`,
      {
        headers
      }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const patchResource = async (patchData: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.put(`${url}${endpoints.resource}`, patchData, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

// PCOBS SERVICES

export const getWorkspaceList = async (fromDate: any, sector?: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `fromDateTime=${fromDate}&sector=${JSON.stringify(sector)}`;
  try {
    const res = await axios.get(`${url}${endpoints.workspaceList}?${queryString}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getCostSummaryChart = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.costSummaryGrafana}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getDatabricksJobsChart = async (fromDateTime: any) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;
  let queryString = `fromDateTime=${date}`;
  try {
    const res = await axios.get(`${url}${endpoints.jobsGrafana}?${queryString}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getReasonChart = async (reasonFromDate: any, reasonRangeValue: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `forDate=${reasonFromDate}&filter=${reasonRangeValue}`;
  console.log(queryString, 'queryStringDEbug');
  try {
    const res = await axios.get(`${url}${endpoints.reasonChart}?${queryString}`, {
      headers
    });
    return res.data;
  } catch (err) {
    return err;
  }
};

// PCOBS FREE TEXT SEARCH

export const getResourceListMapping = async (
  page: string,
  pageSize: string,
  sortingInfo: any,
  filterInfo: any,
  searchQuery: any,
  whereFilter: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page,
    pageSize,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    // whereFilter
    // searchQuery,
    whereFilter: encodeURIComponent(whereFilter)
  };

  try {
    const res = await axios.post(`${url}${endpoints.resourceGroups}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

//Tableau Performance Drilldown
export const getExtractTrendDetails = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  drillDownStatus,
  fromDate,
  sector,
  serverFilter
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  drillDownStatus: any;
  fromDate: string;
  sector: any;
  serverFilter: string;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: serverFilter,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    status: drillDownStatus
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauPerformanceDrilldown}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Filter search
export const getExtractTrendDetailsSearch = async ({
  page,
  size,
  sortingInfo,
  fromDate,
  filterInfo,
  drillDownStatus,
  sector,
  serverFilter,
  searchQuery,
  searchField
}: {
  page: string;
  size: any;
  sortingInfo: any;
  fromDate: string;
  filterInfo: any;
  drillDownStatus: any;
  sector: any;
  serverFilter: string;
  searchQuery: string;
  searchField: string;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: serverFilter,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    status: drillDownStatus,
    searchQuery: searchQuery,
    searchField: searchField
  };

  try {
    const res = await axios.post(`${url}${endpoints.tableauPerformanceDrilldown}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

/* Notification Alert Rules */
export const getAlerts = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.notificationAlerts}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
/* Notification Alert Rules */

/* Databricks Grafana Date filter */
export const getDatabricksSummaryChart = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.summaryGrafana}`, {
      headers
    });
    console.log('response', res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDatabricksResourceOptimizationChart = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.resourceOptimizationGrafana}`, {
      headers
    });
    console.log('response', res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDatabricksGovernanceChart = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.governanceGrafana}`, {
      headers
    });
    console.log('response', res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

/* End of Databricks Grafana Date filter*/

/*Databricks Inital load API call */
export const getDBSummary = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string | null,
  sector?: any
) => {
  return getDBSummaryDate(page, size, sortingInfo, filterInfo, fromDateTime ?? undefined, undefined, sector);
};

// Databricks Resource opt CPU Utilization
export const getDBCPUUtilization = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;


  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksCPUUtilization}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Resource opt Memory Utilization
export const getDBMemoryUtilization = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  const date = localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : '';

  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksMemoryUtilization}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDatabricksJobs = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  sector?: any,
  drillDownStatusFilter?: any
) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;

  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    filter: {}
  };

  if (drillDownStatusFilter && Object.keys(drillDownStatusFilter).length) {
    requestBody['filter'] = drillDownStatusFilter;
  }

  try {
    const res = await axios.post(`${url}${endpoints.databricksJobs}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDBWorkspaceSpent = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  console.log(sector);
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;

  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksWorkspaceSpent}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDBProjectSpent = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;

  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.databricksProjectSpent}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getGovernanceRunningJobs = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  sector?: any,
  filterByColumnInfo?: any
) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;

  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    filterBy: filterByColumnInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.governanceRunningJobs}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getStaleUsers = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();
  const date =
    localStorage.getItem('processedDate') !== undefined ? localStorage.getItem('processedDate') : fromDateTime;

  const requestBody = {
    page,
    size,
    fromDateTime: date,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };

  try {
    const res = await axios.post(`${url}${endpoints.governanceStaleUsers}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

/*End of Databricks Inital load API call*/

// Tableau Governance Detail view
export const getTableauGovernanceDetailView = async ({
  page,
  size,
  sortingInfo,
  drillDownStatus,
  drillDownFilter,
  filterInfo,
  fromDate,
  sector,
  instance
}: {
  page: string;
  size: string;
  sortingInfo: any;
  drillDownStatus: any;
  drillDownFilter: any;
  filterInfo: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    drillDownStatus: drillDownStatus,
    filter: drillDownFilter
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauGovernanceDetailView}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau Governance Users

export const getTableauGovernanceUsers = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  fromDate,
  sector,
  instance,
  drillDownFilter
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
  drillDownFilter?: any;
}) => {
  console.log('Governance Users');
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    filter: drillDownFilter
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauGovernanceUsers}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau Governance Total Orphaned Users

export const getTableauGovernanceOrphanUsersDetails = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  fromDate,
  sector,
  instance,
  searchQuery,
  searchField
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo?: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
  searchQuery?: any;
  searchField?: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    searchQuery: searchQuery,
    searchField: searchField
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauGovernanceOrphanUsers}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau Governance Total Orphaned Extracts

export const getTableauGovernanceOrphanExtractDetails = async ({
  page,
  size,
  sortingInfo,
  filterInfo,
  fromDate,
  sector,
  instance,
  searchQuery,
  searchField
}: {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
  searchQuery?: any;
  searchField?: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    searchQuery: searchQuery,
    searchField: searchField
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauGovernanceOrphanExtract}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau governance user search
export const SearchForTableauGovernanceUser = async (
  searchQuery: any,
  searchField: any,
  filterInfo: any,
  fromDate: string,
  instance: any,
  drillDownFilter: any,
  sector: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    filterBy: filterInfo,
    filter: drillDownFilter,
    searchQuery: searchQuery,
    searchField: searchField
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauGovernanceUsers}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

//Tableau governance workbook search
export const SearchForTableauGovernanceDetail = async ({
  drillDownStatus,
  drillDownFilter,
  sector,
  searchQuery,
  searchField,
  filterInfo,
  fromDate,
  instance
}: {
  drillDownStatus: any;
  drillDownFilter: any;
  sector: any;
  searchQuery: any;
  searchField: any;
  filterInfo: any;
  fromDate: string;
  instance: any;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    fromDate: moment(fromDate).format('YYYY-MM-DD'),
    sectors: sector,
    instance: instance,
    filterBy: filterInfo,
    drillDownStatus: drillDownStatus,
    filter: drillDownFilter,
    searchQuery: searchQuery,
    searchField: searchField
  };

  try {
    const res = await axios.post(`${url}${endpoints.tableauGovernanceDetailView}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau goverance space utilized by unused extracts
export const getTableauGovernanceSpaceUtilized = async ({
  page,
  size,
  sortingInfo,
  drillDownStatus,
  drillDownFilter,
  filterInfo,
  fromDate,
  sector,
  instance
}: {
  page: string;
  size: string;
  sortingInfo: any;
  drillDownStatus: any;
  drillDownFilter: any;
  filterInfo: any;
  fromDate?: string;
  sector?: any;
  instance?: any;
}) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&fromDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&sectors=${sector}&instance=${instance}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (drillDownStatus) queryString = `${queryString}&drillDownStatus=${drillDownStatus}`;
  if (drillDownFilter) queryString = `${queryString}&filter=${drillDownFilter}`;
  if (Object.keys(filterInfo).length)
    queryString = `${queryString}&filterBy=${encodeURIComponent(JSON.stringify(filterInfo))}`;
  try {
    const res = await axios.get(`${url}${endpoints.tableauGovernanceDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau goverance space utilized by unused extracts
export const getTableauGovernanceSpaceUtilizedSearch = async (
  searchQuery: any,
  searchField: any,
  filterInfo: any,
  fromDate?: string,
  sector?: any,
  instance?: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${1}&size=${100}&fromDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&sectors=${sector}&instance=${instance}`;
  if (searchQuery) queryString = `${queryString}&searchQuery=${searchQuery}&searchField=${searchField}`;
  if (Object.keys(filterInfo).length)
    queryString = `${queryString}&filterBy=${encodeURIComponent(JSON.stringify(filterInfo))}`;
  try {
    const res = await axios.get(`${url}${endpoints.tableauGovernanceDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Tableau Rule Observation
export const getTableauObsRules = async (page: string, size: string, sortingInfo: any, filterInfo: any, name?: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&name=${name}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  if (Object.keys(filterInfo).length) {
    const filterString = JSON.stringify(filterInfo);
    const encodedFilterString = encodeURIComponent(filterString);
    queryString = `${queryString}&filter=${encodedFilterString}`;
  }
  try {
    const res = await axios.get(`${url}${endpoints.tableauExtractFailureRuleDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Start presto summary drilldown
// Batch Ids used
export const getPrestoSummaryBatchDrilldown = async (
  page: string,
  size: string,
  fromDate: string,
  onlyBatchId: boolean,
  sortingInfo: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&filterDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&onlyBatchId=${onlyBatchId}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Uesed Ids used
export const getPrestoSummaryUserDrilldown = async (
  page: string,
  size: string,
  fromDate: string,
  onlyUserId: boolean,
  sortingInfo: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&filterDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&onlyUserId=${onlyUserId}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Completed queries
export const getPrestoSummaryCompletedQueriesDrilldown = async (
  page: string,
  size: string,
  fromDate: string,
  onlyCompletedQueries: boolean,
  sortingInfo: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&filterDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&onlyCompletedQueries=${onlyCompletedQueries}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Failed queries
export const getPrestoSummaryFailedQueriesDrilldown = async (
  page: string,
  size: string,
  fromDate: string,
  onlyFailedQueries: boolean,
  sortingInfo: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&filterDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&onlyFailedQueries=${onlyFailedQueries}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Queries executed
export const getPrestoSummaryQueriesExecutedDrilldown = async (
  page: string,
  size: string,
  fromDate: string,
  onlyQueriesExecuted: boolean,
  sortingInfo: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&filterDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&onlyQueriesExecuted=${onlyQueriesExecuted}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Largest Queries
export const getPrestoSummaryLargestQueriesDrilldown = async (
  page: string,
  size: string,
  fromDate: string,
  onlyLargestQueries: boolean,
  sortingInfo: any
) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=20&filterDate=${moment(fromDate).format(
    'YYYY-MM-DD'
  )}&onlyLargestQueries=${onlyLargestQueries}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// when first coming to dashboard table view
export const getPrestoSummaryQueryDetails = async (page: string, size: string, fromDate: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}&filterDate=${moment(fromDate).format('YYYY-MM-DD')}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.prestoSummaryDrilldown}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
// End presto drildown

// Presto Rule Observation
export const getprestoObsRules = async (page: string, size: string, sortingInfo: any, filterInfo: any, name?: any) => {
  return getTableauObsRules(page, size, sortingInfo, filterInfo, name);
};

// Datagrowth Databricks Storage
export const getStorage = async (page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  let queryString = `page=${page}&size=${size}`;
  if (sortingInfo.sortBy) queryString = `${queryString}&sortBy=${sortingInfo.sortBy}`;
  if (sortingInfo.sortDir) queryString = `${queryString}&orderBy=${sortingInfo.sortDir}`;
  try {
    const res = await axios.get(`${url}${endpoints.datagrowthDatabricksStorage}?${queryString}`, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

// Databricks Rule Observation
export const getDatabricksObsRules = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  name?: any
) => {
  return getTableauObsRules(page, size, sortingInfo, filterInfo, name);
};

export const getSchedulers = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.schedulers}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const ibpJobListing = async (page: string, size: string, sortingInfo: any, filterInfo: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.ibpJobListing}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const ibpJobSearch = async (searchDetails: any, filterInfo: any, columnKey: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 100,
    sortBy: '',
    orderBy: '',
    filter: filterInfo,
    queryText: searchDetails,
    columnKey: columnKey
  };
  try {
    const res = await axios.post(`${url}${endpoints.ibpJobListing}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const ibpJobRunsListing = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  gdoName?: string,
  marketName?: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: Number.parseInt(page),
    size: Number.parseInt(size),
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    fromDateTime: '2024-04-02T12:19:11.104Z',
    toDateTime: '2024-05-02T12:19:11.104Z',
    filter: filterInfo,
    gdoName: gdoName,
    marketName
  };
  try {
    const res = await axios.post(`${url}${endpoints.ibpJobRunsListing}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const ibpMonitorMetrics = async (marketName: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = { filterByMarket: marketName };
  try {
    const res = await axios.post(`${url}${endpoints.ibpMonitorMetrics}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const ibpCostByServiceCall = async (marketName: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterByService: '',
    filterByMarket: marketName
  };
  try {
    const res = await axios.post(`${url}${endpoints.ibpCostByService}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const ibpCostByServiceGrafanaCall = async (serviceName: string, marketName: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterByService: serviceName,
    filterByMarket: marketName
  };
  try {
    const res = await axios.post(`${url}${endpoints.ibpCostGrafana}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchBIApplicationSummary = async (filter: string, sector: any, month: any, startDate?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    sector: sector,
    month: month,
    startDate: startDate || null,
    dateFilter: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.biApplicationSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchCrossEnvData = async (dimension: string, kpiName: string, month: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dimension: dimension,
    vendor: '',
    month: month,
    kpiName: kpiName
  };
  try {
    const res = await axios.post(`${url}${endpoints.crossEnvKPI}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchPerformanceData = async (dimension: string, filterMonth?: string, sectors?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dimension: dimension,
    startDate: filterMonth || null,
    sectors: sectors || [],
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataEstatePerformance}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchAnnualSpendDataSourceIncidents = async (filter: string, filterMonth?: string, sectors?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    startDate: filterMonth || null,
    sectors: [sectors],
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.annualSpendDataSourceIncidents}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDatabricksClusters = async (filter: string, filterMonth?: any, sectors?: any, dateFilter?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    months: filterMonth || [],
    sector: sectors || [],
    dateFilter: dateFilter
  };
  try {
    const res = await axios.post(`${url}${endpoints.databricksClusters}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDataprocessingSummary = async (filterMonth?: string, sectors?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupByFilters: 'KPI_NAME,DIMENSION,MONTH',
    startDate: filterMonth || null,
    sectors: [sectors],
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataEstateSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDataprocessingSynapse = async (filterMonth?: string, sectors?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sectors: [sectors],
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.synapseTime}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDataProcessingTrends = async (kpiName: string, filterMonth?: string, sectors?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    startDate: filterMonth || null,
    sectors: sectors || [],
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataProcessingTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchServiceRequestSummary = async (filter: string, filterMonth?: string, sectors?: any, formattedDate?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    startDate: filterMonth || null,
    sectors: [sectors],
    dateFilter: filterMonth,
    formattedDate
  };
  try {
    const res = await axios.post(`${url}${endpoints.serviceRequestSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchTrends = async (kpiName: string, vendor: string, sector: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    vendor: vendor,
    sector: sector,
    month: month,
    dateFilter: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.biApplicationTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDECostSummary = async (filter: string, dimension: any, sector: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    dimension: dimension,
    sector: sector,
    month: month,
    dateFilter: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.deCostSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const fetchDECostTrends = async (filter: string, dimension: any, sector: any, month: any) => {
  return fetchDECostSummary(filter, dimension, sector, month);
};

export const fetchDatabricksClustersCount = async (startDate?: string, endDate?: string, isCount?: boolean, kpiType?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    startDate,
    endDate,
    isCount,
    kpiType
  };
  try {
    const res = await axios.post(`${url}${endpoints.databricksClustersCount}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const dataEstateOverallSummary = async (sectorBy: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: moment().format('MM') + '/' + moment().format('YY'),
    sectors: [sectorBy]
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataEstateOverallSummary}`, requestBody, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const dataEstateExecutiveSummary = async (sector: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: moment().format('MM') + '/' + moment().format('YY'),
    sectors: [sector]
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataEstateExecutiveSummary}`, requestBody, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchAzureMonthlyCost = async () => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: moment().format('MM') + '/' + moment().format('YY')
  };
  try {
    const res = await axios.post(`${url}${endpoints.azureMonthlyCost}`, requestBody, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDEStorageSummary = async (sector: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupByFilters: 'KPI_NAME,DIMENSION,MONTH',
    sectors: [sector],
    startDate: month || null,
    month: month,
    dateFilter: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.deStorageSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const fetchDEStorageTrends = async (kpiName: string, vendor: any, sector: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    vendor: vendor,
    sector: [sector],
    month: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.deStorageTrends}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDEStorageTable = async (kpiName: string, dimension: any, sector?: any, month?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    dimension: dimension,
    sector: sector || [],
    month: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.summaryTable}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchQuerySummary = async (filter: any, sectors: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    month: month,
    dateFilter: month,
    sectors: sectors || []
  };
  try {
    const res = await axios.post(`${url}${endpoints.queriesSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const fetchDEQueryTrends = async (kpiName: string, vendor: any, sector: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    vendor: vendor,
    sector: sector,
    month: month,
    dateFilter: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.queriesTrends}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDECostTabel = async (kpiName: string, dimension: any, sector?: any, month?: any) => {
  return fetchDEStorageTable(kpiName, dimension, sector, month);
};

export const fetchDataEstateLastSyncData = async (filter: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter
  };
  try {
    const res = await axios.post(`${url}${endpoints.lastSyncDate}`, requestBody, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const FetchFinOpsOverviewCloudCost = async (filter: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsOverviewSummary}`, requestBody, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const FetchFinOpsOverviewCloudGrowth = async (filter: any) => {
  return FetchFinOpsOverviewCloudCost(filter);
};

export const fetchCloudCostTrends = async (filter: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsOverviewSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchCostTableData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/tableau/cost/tableData`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau cost Table data:', err);
    throw err;
  }
};

export const fetchTableauCostExport = async (requestBody: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauCostExport}`, requestBody, { responseType: "blob" });

    const contentDisposition = res.headers["content-disposition"];
    let filename = `Tableau_License_Activity_Details.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    throw err;
  }
};

export const fetchCostBreakdown = async (filter: any) => {
  return fetchCloudCostTrends(filter);
};

export const QuerySummaryTable = async (kpiName: string, dimension: string, sector: any, month: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    dimension: dimension,
    sector: sector,
    month: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.summaryTable}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const dataProcessingTable = async (kpiName: string, dimension?: string, sector?: any, month?: string) => {
  return QuerySummaryTable(kpiName, dimension ?? '', sector, month ?? '');
};

export const programSummaryDetail = async (filter: any, page: string, size: string, sortingInfo: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page,
    size,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filter
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsProgramSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProgramCloudCost = async (filter: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsProgramSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProgramDATeams = async (filter: any) => {
  return fetchProgramCloudCost(filter);
};
export const FetchFinOpsCostKpi = async (filter?: any, lmcg?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    type: 'cost',
    lmcg: lmcg,
    filterData: filter || {}
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsCostKpi}`, requestBody, { headers });
    console.log(res.data);
    return res.data;
  } catch (err) {
    return err;
  }
};
export const FetchFinOpsDetails = async (filter: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsDetailSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const FetchFinOpsDetailsSummary = async (page: any, size: any, groupingInfo: any, sortingInfo?: any, lmcg?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    pageData: {
      page,
      size
    },
    lmcg: lmcg,
    filterData: groupingInfo || {},
    sortingInfo: sortingInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsCostKpi}`, requestBody, { headers });
    console.log(res, 'res details summary');
    return res.data;
  } catch (err) {
    return err;
  }
};
export const FetchFinOpsResourceSummary = async (page: any, size: any, project: any, resourceType: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: '',
    offset: '',
    project: project,
    resourceType: resourceType
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsResourceDetail}`, requestBody, { headers });
    console.log(res, 'res resource summary');
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getDetailSummarySector = async (filter: any, searchQuery: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: [filter],
    searchQuery: searchQuery
  };
  try {
    const res = await axios.post(`${url}${endpoints.filterDetail}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getFinopsResourceMappingList = async ({
  page,
  size,
  totalPages = 0,
  sortingInfo,
  sortBy = '',
  orderBy = '',
  searchField = '',
  searchText = '',
  searchQuery = '',
  sql,
  params,
  groupBy,
  viewType = '',
  isProgram = false,
  signal,
  includeCost = false
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page,
    size,
    totalPages,
    sortingInfo,
    sortBy,
    orderBy,
    searchField,
    searchQuery,
    searchText,
    sql,
    params,
    groupBy,
    viewType,
    isProgram,
    includeCost
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsMappingList}`, requestBody, { headers, signal });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Export
export const exportGetFinopsResourceMappingList = async ({
  page,
  size,
  totalPages = 0,
  sortingInfo,
  sortBy = '',
  orderBy = '',
  searchField = '',
  searchText = '',
  searchQuery = '',
  sql,
  params,
  groupBy,
  viewType = '',
  isProgram = false,
  subscription = '',
  exportColumns,
  formatType,
  tableName,
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    exportColumns: exportColumns,
    formatType: formatType,
    page,
    size,
    totalPages,
    sortingInfo,
    sortBy,
    orderBy,
    searchField,
    searchQuery,
    searchText,
    sql,
    params,
    groupBy,
    viewType,
    isProgram,
    subscription,
    tableName: tableName
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsExportMappingList}`, requestBody, { headers, responseType: "blob" });
    // Extract filename from response headers
    const contentDisposition = res.headers['content-disposition'];
    let filename = `Finops_RMList.${formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exporturl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    return err;
  }
};

export const getFinopsResource = async ({
  groupId,
  groupBy,
  viewType = '',
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupId,
    groupBy,
    viewType,
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsGetResource}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const postResourceAllocationFinops = async ({
  groupId = '',
  resource = '',
  data = [],
  userTimezone = ''
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupId,
    resource,
    data,
    userTimezone
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsResourceAllocation}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getResourceAllocationFinops = async ({
  groupId = '',
  userTimezone = ''
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupId,
    userTimezone
  };
  try {
    const res = await axios.post(`${url}${endpoints.getFinopsResourceAllocation}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const postBulkEditFinopsResourceMappingList = async ({
  groupId = [],
  PepwiseProgram = '',
  PepwiseProject = '',
  PepwiseSector = '',
  PepwiseEnvironment = '',
  override = '',
  skipconflict = '',
  programId = null,
  projectId = null,
  userTimezone = ''
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupId,
    sector: PepwiseSector,
    environment: PepwiseEnvironment,
    project: PepwiseProject,
    program: PepwiseProgram,
    override,
    skipconflict,
    programId,
    projectId,
    userTimezone
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsBulkEdit}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getBulkEditFinopsinputSecEnvOption = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.finopsBulkEditInputSecEnvOptions}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getAllocationProgramProjects = async ({
  searchText = '',
  searchType = '',
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = { searchText, searchType };
  try {
    const res = await axios.post(`${url}${endpoints.getAllocationProgramPojects}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSlaReportData = async (

  fromDateTime?: string,
  toDateTime?: string,

) => {
  const headers = getAuthorazieHeader();
  const requestBody = {

    startDate: fromDateTime,
    endDate: toDateTime,

  };
  try {
    const res = await axios.post(`${url}${endpoints.incidentCriticalSlaReport}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getIncidentDetails = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  incidentType?: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    startDate: fromDateTime,
    endDate: toDateTime,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    incidentType: incidentType
  };
  try {
    const res = await axios.post(`${url}${endpoints.incidentCriticalJobReport}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const incidentSearchForJobExecutions = async (
  searchDetails: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: 1,
    size: 50,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    sortBy: 'scheduledJob',
    orderBy: 'asc',
    filter: filterInfo,
    jobSearch: searchDetails
  };
  try {
    const res = await axios.post(`${url}${endpoints.incidentCriticalJobReport}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const fetchChartData = async (params: any) => {
  const headers = getAuthorazieHeader();
  let { incidentId, startDate, endDate } = params
  const requestBody = {
    startDate: moment(startDate).format('YYYY-MM-DD HH:mm:ss.SSSSSSS'),
    endDate: moment(endDate).format('YYYY-MM-DD HH:mm:ss.SSSSSSS'),
    incidentId
  };
  try {
    const res = await axios.post(`${url}${endpoints.chartDataSla}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getOverviewSummaryMetrics = async (filterBy?: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.overviewSummaryMetrics}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getOverviewSummaryCost = async (filterBy?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterBy: filterBy
  };
  try {
    const res = await axios.post(`${url}${endpoints.overviewSummaryCost}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getServiceReq = async (
  kpiName: string,
  dimension: string,
  sector: string,
  month: string,
  page: string,
  size: string
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    kpiName: kpiName,
    dimension: dimension,
    sector: sector,
    month: month
  };
  let queryString = `page=${page}&size=${size}`;
  try {
    const res = await axios.post(`${url}${endpoints.summaryTable}?${queryString}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const biApplicationSummary = async (kpiName: string, dimension: string, sector?: any, month?: string) => {
  return fetchDEStorageTable(kpiName, dimension, sector, month);
};

export const getDBDatabricks = async (
  page: string,
  size: string,
  sortingInfo: any,
  filterInfo: any,
  fromDateTime?: string,
  toDateTime?: string,
  sector?: any
) => {
  const headers = getAuthorazieHeader();

  const requestBody = {
    page,
    size,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    sector: sector,
    sortBy: sortingInfo.sortBy,
    sortDir: sortingInfo.sortDir,
    filter: filterInfo
  };
  console.log(requestBody, 'requestBody');
  try {
    const res = await axios.post(`${url}${endpoints.databricksSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getProgramSectorList = async (

) => {
  const headers = getAuthorazieHeader();


  const requestBody = {

  };
  try {
    const res = await axios.post(`${urlV2}${endpoints.dataPipelineSectorList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const getNewProgramSectorList = async (

) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
  };
  try {
    const host = getIsV2() == true ? urlV2 : url;
    const res = await axios.post(`${host}${endpoints.dataPipelineNewSectorList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getE2EConfigurationMetadata = async () => {
  const headers = getAuthorazieHeader();

  const requestBody = {};

  try {
    const res = await axios.post(
      `${url}${endpoints.e2eConfigurationMetadata}`,
      requestBody,
      { headers }
    );

    return res.data;
  } catch (err) {
    return err;
  }
};

export const getPepwiseSectorList = async (

) => {
  const headers = getAuthorazieHeader();


  const requestBody = {

  };
  try {
    const res = await axios.post(`${url}${endpoints.pepwiseSectorList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const executiveDashboard = async (data: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.dataPipelineExecutiveDashboard}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const executiveProgramProjects = async (data: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.executiveProgramProjects}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getAlertsApi = async (payload: { Type: string; ProjectId: string }) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/getAlertSlaProjectList`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('getAlertsApi Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const addOrUpdateAlertApi = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/addOrUpdateAlertSlaProject`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('addOrUpdateAlertApi Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const deleteAlertApi = async (payload: { id: number }) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/deleteAlertSlaProject`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('deleteAlertApi Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const createAlertConfigApi = async (payload: any): Promise<any> => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/configs/create`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('createAlertConfigApi Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

// Alert Framework SLA APIs
export const getAlertSlaProjectList = async (payload: { Type: string; ProjectId: string }) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/getAlertSlaProjectList`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('getAlertSlaProjectList Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const addOrUpdateAlertSlaProject = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/addOrUpdateAlertSlaProject`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('addOrUpdateAlertSlaProject Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const deleteAlertSlaProject = async (payload: { id: number }) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/deleteAlertSlaProject`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('deleteAlertSlaProject Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const createAlertConfig = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/alertFramework/configs/create`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('createAlertConfig Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const getAlertConfigsByProgram = async (programName: string) => {
  const headers = getAuthorazieHeader();
  try {
    const payload = {
      pageSize: 100,
      page: 1,
      filter: {
        program: programName
      }
    };
    const res = await axios.post(`${url}/alertFramework/configs`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('getAlertConfigsByProgram Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const updateAlertConfigs = async (alertId: string, payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.put(`${url}/alertFramework/configs/${alertId}`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('updateAlertConfig Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const deleteAlertConfigById = async (alertId: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.delete(`${url}/alertFramework/configs/${alertId}`, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    console.error('deleteAlertConfigById Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};


export const getProgramList = async (sectorId?: string, searchKeyword?: string) => {
  const headers = getAuthorazieHeader();
  const reqBody: any = {};
  if (sectorId) {
    reqBody['sectorId'] = sectorId
  }
  if (searchKeyword) {
    reqBody['searchKeyword'] = searchKeyword
  }
  try {
    const res = await axios.post(`${urlV2}${endpoints.ProgramList}`, reqBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSectorProgramMapping = async (
  searchKeyword?: string,
  version?: 'V1' | 'V2',
  filters?: Record<string, string>,
  isIBP: boolean = false
) => {
  const headers = getAuthorazieHeader();
  const reqBody: any = {};

  if (searchKeyword) {
    reqBody['searchKeyword'] = searchKeyword
  }

  if (filters && Object.keys(filters).length) {
    Object.entries(filters).forEach(([key, value]) => {
      reqBody[key] = value;
    });
  }
  try {
    const endpoint = isIBP ? endpoints.SectorProgramMappingIBP : endpoints.SectorProgramMapping;
    const res = await axios.post(`${urlV2}${endpoint}`, reqBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getProgramById = async (id: any, sectorId?: string, fromDate?: string, toDate?: string) => {
  let body: any = {
    programID: id
  }
  if (sectorId) {
    body['sectorId'] = sectorId
  }
  if (fromDate) {
    body['fromDate'] = fromDate
  }
  if (toDate) {
    body['toDate'] = toDate
  }
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.PgmDetails}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const createDataPipelineProgram = async (body: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.createProgram}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const bulkUploadDataPipelineEntities = async (body: any) => {
  const headers = getAuthorazieHeader();
  try {

    const res = await axios.post(`${urlV2}${endpoints.bulkUploadEntities}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchBulkUploadLookups = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.bulkUploadLookups}`, {}, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const editDataPipelineProgram = async (data: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    programID: data.id,
    // sector: data.sector,
    programName: data.programName
  }
  try {
    const res = await axios.post(`${urlV2}${endpoints.editProgram}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteDataPipelineProgram = async (id: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    programID: id
  }
  try {
    const res = await axios.post(`${urlV2}${endpoints.deleteProgram}`, body, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching summary incident trend:', err);
    throw err;
  }
};

export const deleteDataPipelineProject = async (id: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    projectID: id
  }
  try {
    const res = await axios.post(`${urlV2}${endpoints.deleteProject}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteDataPipelineSchedule = async (data: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.bulkJobDelete}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const editDataPipelineProject = async (data: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    projectID: data.id,
    projectOwner: data.projectOwner,
    projectName: data.projectName,
    projectDescription: data.projectDescription,
    mulSector: data.mulSector,
    friendlyName: data.friendlyName,
    slaDate: data.slaDate,
    slaTime: data.slaTime,
    isSlaConfigured: data?.isSlaConfigured || 0,
    slaType: data?.slaType,
    slaVariation: data.slaVrtn,
    slaAggregation:data.slaAgg,
    slaData: data.slaData || [],
    isSlaChanged: data.isSlaChanged || false
  }
  try {
    const res = await axios.post(`${urlV2}${endpoints.editProject}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

const mapSortData = (sortData: any, body: any) => {
  if (!sortData || !Object.keys(sortData).length) return;
  let sortedByVal = sortData.sortBy;
  if (sortData.sortBy === 'Sectors') {
    sortedByVal = 'sector';
  } else if (sortData.sortBy === 'Project_Name') {
    sortedByVal = 'projectName';
  }
  body['sortBy'] = sortedByVal;
  body['orderBy'] = sortData.sortDir;
};

const mapFilters = (filterInfo: any, currentFilterType: string | undefined, body: any) => {
  const filtersData: any = {};
  let resolvedFilterType = currentFilterType;
  if (filterInfo && Object.keys(filterInfo).length) {
    Object.entries(filterInfo).forEach(([key, val]: [string, any]) => {
      if (key === 'Sectors') {
        filtersData['sector'] = val;
      } else if (key === 'Project_Name') {
        filtersData['projectName'] = val;
      } else if (key === 'Project_SLA_Status' || key === 'SLA_Status') {
        filtersData['projectSlaStatus'] = val.filter(Boolean);
        if (!resolvedFilterType) {
          resolvedFilterType = val.filter(Boolean);
        }
      } else if (key === 'Project_Owner') {
        filtersData['projectOwner'] = val;
      } else if (key === 'Created_Dt') {
        filtersData['createdDate'] = val;
      }
    });
    body['filter'] = filtersData;
  }

  if (resolvedFilterType) {
    body['filter'] = filtersData;
    if (resolvedFilterType === 'Completed Projects') {
      body['filter']['projectSlaStatus'] = ["SLA Met", "SLA Missed"];
    } else {
      body['filter']['projectSlaStatus'] = typeof resolvedFilterType === 'string' ? [resolvedFilterType] : resolvedFilterType;
    }
  }
};

export const getProjectListByProgram = async (
  first: any,
  ...args: any[]
) => {
  let opts: any;
  if (first && typeof first === 'object' && !Array.isArray(first) && first.id !== undefined && args.length === 0) {
    opts = first;
  } else {
    opts = {
      id: first,
      page: args[0],
      pageSize: args[1],
      currentFilterType: args[2],
      filterInfo: args[3],
      startDate: args[4],
      endDate: args[5],
      sortData: args[6] || {},
      sectorId: args[7],
      projectName: args[8],
      version: args[9]
    };
  }

  const {
    id,
    page,
    pageSize,
    currentFilterType,
    filterInfo,
    startDate,
    endDate,
    sortData,
    sectorId,
    projectName,
    version
  } = opts;

  const headers = getAuthorazieHeader();
  let body: any = {
    programID: id,
    page,
    pageSize,
    fromDate: startDate || moment().tz('America/Chicago').format('YYYY-MM-DD'),
    toDate: endDate || moment().tz('America/Chicago').format('YYYY-MM-DD'),
    filter: {},
    projectName: projectName || ''
  };

  mapSortData(sortData, body);

  if (sectorId) {
    body['sectorId'] = sectorId;
  }

  mapFilters(filterInfo, currentFilterType, body);

  try {
    const res = await axios.post(`${urlV2}${endpoints.ProjectJobStatusV2}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Export API for project job status
export const exportProjectListByProgram = async (opts: {
  id: any;
  page: any;
  pageSize: any;
  name: any;
  exportColumns: any;
  formatType: any;
  currentFilterType?: string;
  filterInfo?: any;
  startDate?: string;
  endDate?: string;
  sortData?: any;
  sectorId?: any;
  projectName?: string;
  tableName?: string;
}) => {

  const {
    id,
    page,
    pageSize,
    name,
    exportColumns,
    formatType,
    currentFilterType,
    filterInfo,
    startDate,
    endDate,
    sortData = {},
    sectorId,
    projectName,
    tableName,
  } = opts || {};

  const headers = getAuthorazieHeader();

  const getDateOrToday = (date?: string) =>
    date || moment().tz("America/Chicago").format("YYYY-MM-DD");

  const mapSortBy = (sortBy: string) => {
    const sortMap: Record<string, string> = {
      Sectors: "sector",
      Project_Name: "projectName",
    };
    return sortMap[sortBy] ?? sortBy;
  };

  const buildSortPayload = (sort: any) => {
    if (!sort || !Object.keys(sort).length) return {};
    return {
      sortBy: mapSortBy(sort.sortBy),
      orderBy: sort.sortDir,
    };
  };

  const mapFilterKey = (key: string) => {
    const keyMap: Record<string, string> = {
      Sectors: "sector",
      Project_Name: "projectName",
      Project_SLA_Status: "projectSlaStatus",
      SLA_Status: "projectSlaStatus",
    };
    return keyMap[key];
  };

  const buildFiltersPayload = (filters: any) => {
    if (!filters || !Object.keys(filters).length) return {};

    const mapped: any = {};
    Object.entries(filters).forEach(([key, value]: any) => {
      const mappedKey = mapFilterKey(key);
      if (!mappedKey) return;

      mapped[mappedKey] =
        mappedKey === "projectSlaStatus"
          ? value.filter(Boolean)
          : value;
    });

    return mapped;
  };

  const applyCurrentFilterType = (filter: any, type?: string) => {
    if (!type) return filter;

    const slaStatuses =
      type === "Completed Projects" ? ["SLA Met", "SLA Missed"] : [type];

    return {
      ...filter,
      projectSlaStatus: slaStatuses,
    };
  };

  const getFilenameFromResponse = (res: any) => {
    const contentDisposition = res.headers?.["content-disposition"];
    const fallback = `Program_${name}.${formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (!contentDisposition) return fallback;

    const match = contentDisposition.match(/filename="?([^"]+)"?/);
    return match?.[1] || fallback;
  };

  const downloadBlob = (blobData: any, filename: string) => {
    const exporturl = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const baseFilter = buildFiltersPayload(filterInfo);
  const finalFilter = applyCurrentFilterType(baseFilter, currentFilterType);

  const body: any = {
    programID: id,
    page,
    pageSize,
    name,
    formatType,
    exportColumns,
    tableName,
    fromDate: getDateOrToday(startDate),
    toDate: getDateOrToday(endDate),
    filter: finalFilter,
    projectName: projectName || "",
    ...(sectorId ? { sectorId } : {}),
    ...buildSortPayload(sortData),
  };

  try {
    const res = await axios.post(`${urlV2}${endpoints.exportProjectJobStatus}`, body, { headers, responseType: "blob" });

    const filename = getFilenameFromResponse(res);
    downloadBlob(res.data, filename);

    return true;
  } catch (err) {
    console.error("Error exporting table data:", err);
    throw err;
  }
};

export const fetchDEIncidentDetails = async (sector: string, page: string, size: string, month: any, type?: any, dimension?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: [sector],
    dateFilter: month,
    type: type,
    dimension: dimension
  };
  let queryString = `page=${page}&size=${size}`;
  try {
    const res = await axios.post(`${url}${endpoints.majorIncidentDetail}?${queryString}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// export IncidentDetails
export const exportDEIncidentDetails = async (
  exportColumns: any,
  formatType: any,
  tableName: string,
  sector: string,
  month: any,
  type?: any,
  dimension?: any,
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    exportColumns: exportColumns,
    formatType: formatType,
    sector: [sector],
    dateFilter: month,
    type: type,
    dimension: dimension,
    tableName: tableName
  };
  try {
    const res = await axios.post(`${url}${endpoints.exportMajorIncidentDetail}`, requestBody, { headers, responseType: "blob" });
    // Extract filename from response headers
    const contentDisposition = res.headers['content-disposition'];
    let filename = `Major_Incidents.${formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exporturl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    throw err;
  }
};

export const fetchSummaryMessageDetails = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getSummaryMessageDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error("Error fetching summary message details:", err);
    return err;
  }
};

export const fetchSummaryMessageDetailsCosmos = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getSummaryMessageDetailsCosmos}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error("Error fetching summary message details from Cosmos:", err);
    return err;
  }
};

export const fetchKBArticlePublicationTrend = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getKBArticlePublicationTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error("Error fetching KB Article trend:", err);
    return err;
  }
};

export const fetchIncidentsSummary = async (
  first: any,
  ...args: any[]
) => {
  let opts: any;
  if (first && typeof first === 'object' && !Array.isArray(first) && args.length === 0) {
    opts = first;
  } else {
    opts = {
      selectedDate: first,
      isClosedDate: args[0],
      timezoneFilter: args[1],
      sector: args[2],
      type: args[3],
      area: args[4],
      domain: args[5],
      businessUnit: args[6],
      department: args[7],
      capability: args[8]
    };
  }

  const {
    selectedDate,
    isClosedDate,
    timezoneFilter,
    sector,
    type,
    area,
    domain
  } = opts;
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector
  };

  // Add domain filter if selected
  if (!domain?.includes('all')) {
    requestBody.domain = domain;
  }

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.incidentSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchIncidentTechnologyList = async ({
  page,
  size,
  sortBy = '',
  orderBy = '',
  dateFilter,
  isClosedDate,
  timezoneFilter,
  sector,
  type,
  area
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    page,
    size,
    sortBy,
    orderBy,
    dateFilter,
    isClosedDate,
    timezoneFilter,
    sector
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.incidentTechnologyList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchAppProductDetails = async ({
  appProducts,
  dateFilter
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    appProducts,
    dateFilter
  };
  try {
    const res = await axios.post(`${url}${endpoints.appProducts}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const fetchAppProductMetrics = async ({
  appProducts,
  dateFilter,
  sector,
  isClosedDate
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    appProducts,
    dateFilter,
    sector,
    isClosedDate
  };
  try {
    const res = await axios.post(`${url}${endpoints.appProductsMetrics}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const fetchIncidentTrend = async ({
  metricsType,
  technology,
  chartType,
  sector,
  type,
  area,
  isClosedDate,
  dateFilter
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    metricsType,
    technology,
    chartType,
    sector,
    isClosedDate,
    dateFilter
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await apiRequest({
      method: "post",
      url: `${url}${endpoints.incidentTrend}`,
      data: requestBody,
      headers,
      key: "xops-incidentTrend"
    });
    return res?.data || [];
  } catch (err) {
    return err;
  }
};

export const fetchTechnologyDataList = async ({
  selectedMonth,
  metricsType,
  sector,
  isClosedDate,
  timezoneFilter,
  type,
  area
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedMonth,
    metricsType,
    isClosedDate,
    timezoneFilter,
    sector: sector
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    
    const res = await apiRequest({
      method: "post",
      url: `${url}${endpoints.technologyListData}`,
      data: requestBody,
      headers,
      key: "xops-technologyListData"
    });
    return res?.data || [];
  } catch (err) {
    return err;
  }
};

export const fetchMetricsTechnologyList = async ({
  metricsType,
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    metricsType
  };
  try {
    
    const res = await apiRequest({
      method: "post",
      url: `${url}${endpoints.metricsTechnologyList}`,
      data: requestBody,
      headers,
      key: "xops-metricsTechnologyList"
    });
    return res?.data || [];
  } catch (err) {
    return err;
  }
};

export const getSnowAssignmentGroups = async () => {
  const headers = getAuthorazieHeader(); // Get auth headers
  try {
    const res = await axios.get(`${url}${endpoints.snowAssignmentGroups}`, { headers });
    return res.data; // axios response.data is already parsed JSON
  } catch (err) {
    console.error('Failed to fetch assignment groups:', err);
    throw err; // Re-throw to be caught by the component
  }
};

export const addSnowAssignmentGroup = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    // Make sure 'addAssignmentGroup' is in your 'endpoints' config
    const res = await axios.post(`${url}${endpoints.addAssignmentGroup}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error('Failed to add assignment group:', err);
    throw err;
  }
};

export const updateSnowAssignmentGroup = async (id: number, payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    // Make sure 'updateAssignmentGroup' is in your 'endpoints' config
    const res = await axios.put(`${url}${endpoints.updateAssignmentGroup}/${id}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error('Failed to update assignment group:', err);
    throw err;
  }
};

export const deleteSnowAssignmentGroup = async (id: number) => {
  const headers = getAuthorazieHeader();
  try {
    // Make sure 'deleteAssignmentGroup' is in your 'endpoints' config
    const res = await axios.delete(`${url}${endpoints.deleteAssignmentGroup}/${id}`, { headers });
    return res.data;
  } catch (err) {
    console.error('Failed to delete assignment group:', err);
    throw err;
  }
};

export const fetchChannelMetrics = async (selectedDate: any, isClosedDate: any, timezoneFilter: string, sector: string, type?: string, area?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.channelMetrics}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchResolutionMetrics = async (selectedDate: any, isClosedDate: any, timezoneFilter: string, sector: string, type?: string, area?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.resolutionMetrics}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchL1L2L3Metrics = async (selectedDate: any, isClosedDate: any, timezoneFilter: string, sector: string, type?: string, area?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.L1toL3ResolutionMetrics}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchIncidentsBySectorMetrics = async (selectedDate: any, isClosedDate: any, type?: string, area?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.incidentsBySector}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getTeradataJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchTeradataJobNameList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getADFNameList = async (
  platformName: any,
  schedulerType: string,
  adfName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    adfName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.getADFInstance}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchADFNameList = async (
  platformName: any,
  schedulerType: string,
  adfName?: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    adfName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchADFNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const searchWindowsJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchWindowsJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchSAPBWJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchSAPBWJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchSqlAgentJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchSqlAgentJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSqlAgentJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getsqlAgentJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchIicsJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchIicsJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getIicsJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getIicsJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchDDHJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchDDHJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDDHJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getDDHJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchMDTJobNameList = async (
  platformName: any,
  schedulerType: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchMDTJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getMDTJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getMDTJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const getADBWorkspaceNameList = async (
  platformName: any,
  schedulerType: string,
  workspaceName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    workspaceName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.getADBWorkspace}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchADBJobNameList = async (
  platformName: any,
  schedulerType: string,
  workspaceName: string,
  jobName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    workspaceName,
    jobName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchADBJobNames}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getADBJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getADBJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchDataAsset = async (
  platformName: any,
  schedulerType: string,
  adfName?: string,
  dataAsset?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    adfName,
    dataAsset,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchDataAsset}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchSilverTableName = async (
  platformName: any,
  schedulerType: string,
  adfName?: string,
  silverTable?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    adfName,
    silverTable,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchSilverTable}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getFolderNameList = async (
  platformName: any,
  schedulerType: string,
  folderName?: string,
  projectId?: number
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platformName,
    folderName,
    schedulerType,
    projectId
  };
  try {
    const res = await axios.post(`${urlV2}${endpoints.teradataFolderNameList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSearchScheduleJobNameList = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${urlV2}${endpoints.displayJobMetaData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getTeradataJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getTeradataJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getWindowsJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getwindowsJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSAPBWJobData = async (
  reqBody: any,
  pageNumber: string,
  pageSize: string,
  filterData: any = {}
) => {
  const headers = getAuthorazieHeader();
  const requestBody = reqBody;
  requestBody['page'] = pageNumber;
  requestBody['size'] = pageSize;
  requestBody['filter'] = filterData;
  try {
    const res = await axios.post(`${url}${endpoints.getSAPBWJobData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const linkAddScheduleToProject = async (reqBody: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    Data: reqBody
  };
  try {
    const res = await axios.post(`${urlV2}${endpoints.addSelectedJob}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const linkWinJobToProject = async (reqBody: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    Data: reqBody
  };
  try {
    const res = await axios.post(`${url}${endpoints.linkWinJobToProject}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getMappedJobScheduleList = async (
  first: any,
  ...args: any[]
) => {
  let opts: any;
  if (first && typeof first === 'object' && !Array.isArray(first) && args.length === 0) {
    opts = first;
  } else {
    opts = {
      projectId: first,
      pageNumber: args[0],
      pageSize: args[1],
      startDate: args[2],
      endDate: args[3],
      filterData: args[4],
      is24Hrs: args[5],
      slaTime: args[6],
      slaDate: args[7],
      addedJobsList: args[8],
      sortData: args[9] || {}
    };
  }

  const {
    projectId,
    pageNumber,
    pageSize,
    startDate,
    endDate,
    filterData,
    is24Hrs,
    slaTime,
    slaDate,
    addedJobsList,
    sortData
  } = opts;
  const headers = getAuthorazieHeader();
  const requestBody = {
    projectId: projectId,
    page: pageNumber,
    size: pageSize,
    fromDate: startDate,
    toDate: endDate,
    filterOptions: filterData,
    IS24Hrs: is24Hrs,
    ...sortData,
    slaTime,
    slaDate,
    jobList: addedJobsList
  };

  try {
    const res = await axios.post(`${urlV2}${endpoints.getScheduleExecutionsList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// export
export const exportGetMappedJobScheduleList = async (
  projectId: any,
  project: any,
  exportColumns: string[],
  formatType: string,
  tableName: string,
  startDate: string,
  endDate: string,
  filterData: any,
  is24Hrs: boolean,
  sortData: any,
  slaTime: any,
  slaDate: number,
  jobStatusParam?: any,
  addedJobsList?: any
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    projectId: projectId,
    project: project,
    formatType: formatType,
    exportColumns: exportColumns,
    tableName: tableName,
    fromDate: startDate,
    toDate: endDate,
    filterOptions: filterData,
    IS24Hrs: is24Hrs,
    ...sortData,
    slaTime,
    slaDate,
    jobList: addedJobsList
  };
  if (jobStatusParam) {
    requestBody['filterOptions']['jobStatus'] = jobStatusParam === 'Total Jobs' ? [] : [jobStatusParam];
  }
  try {
    const res = await axios.post(`${urlV2}${endpoints.exportgetScheduleExecutionsList}`, requestBody, { headers, responseType: 'blob' });
    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `Project_${project.replace(/\s+/g, '_')}_JobList.${formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error("Error exporting table data:", err);
    throw err;
  }
};

export const getSlaCountDetail = async (
  projectId: any,
  startDate: string,
  endDate: string,
  is24hrs: boolean
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    projectId: projectId,
    start: startDate,
    end: endDate,
    hours: is24hrs ? 24 : 0
  };
  try {
    const res = await axios.post(`${url}${endpoints.slaCountDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

let cancelTokenSource: any = null;
export const getProjectOwnerList = async (name: any, version?: 'V1' | 'V2') => {
  const headers = getAuthorazieHeader();
  let body = {}
  if (name) {
    body = {
      searchName: name
    }
  }
  //Use token cancel to handle multiple 'search api' calls get hanging
  if (cancelTokenSource) {
    cancelTokenSource.cancel('Operation canceled due to new request.');
  }
  cancelTokenSource = axios.CancelToken.source();
  try {
    
    const res = await axios.post(`${url}${endpoints.getOwnerList}`, body, { headers, cancelToken: cancelTokenSource.tokens });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getClarityProjectList = async (name: any) => {
  const headers = getAuthorazieHeader();
  let body = {}
  if (name) {
    body = {
      searchProject: name
    }
  }
  try {
    
    const res = await axios.post(`${url}${endpoints.getClarityProject}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const createDataPipelineProject = async (body: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.createProject}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getJobPerformanceChartData = async (params: any) => {

  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.dataPipelineProjectSlaMetrics}`, params, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBICapacityMetricsData = async (capacityName?: any, sector?: any, dateLabel?: any) => {
  // Encode the sector query parameter to ensure special characters are handled correctly
  const encodedSector = encodeURIComponent(JSON.stringify(sector));
  const encodedCapacityName = capacityName;
  const requestBody = {
    sector: encodedSector,
    capacityName: encodedCapacityName,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityMetrics}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBIAdoptionMetricsData = async (sector?: any, dateLabel?: any) => {
  // Encode the sector query parameter to ensure special characters are handled correctly
  const encodedSector = encodeURIComponent(JSON.stringify(sector));
  let queryString = `sector=${encodedSector}`;
  if (dateLabel) {
    queryString += `&filterOption=${dateLabel}`;
  }
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIAdoptionMetrics}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBIGatewayMetricsData = async (sector?: any) => {
  // Encode the sector query parameter to ensure special characters are handled correctly
  const encodedSector = encodeURIComponent(JSON.stringify(sector));
  let queryString = `sector=${encodedSector}`;
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIGatewayMetrics}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const powerBIGatewayVisualizationData = async (sector?: any) => {
  // Encode the sector query parameter to ensure special characters are handled correctly
  const encodedSector = encodeURIComponent(JSON.stringify(sector));
  let queryString = `sector=${encodedSector}`;
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIGatewayVisualization}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const powerBICapacityDetailData = async (pageNumber: string, size: string, sector: any, capacityName: any, sortingInfo: any, filterInfo: any, dateLabel?: any) => {
  let queryString = {
    sector: sector,
    capacityName: capacityName,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    page: pageNumber,
    pageSize: size,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityDetails}`, queryString, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const powerBICapacityDetailPagination = async (pageNumber: string, size: string, sector: any, capacityName: any, sortingInfo: any, filterInfo: any, dateLabel?: any) => {
  let queryString = {
    sector: sector,
    capacityName: capacityName,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    page: pageNumber,
    pageSize: size,
    filterBy: filterInfo,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityDetailsPagination}`, queryString, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const powerBICapacityDetailFilters = async (pageNumber: string, size: string, sector: any, capacityName: any, sortingInfo: any, filterInfo: any, dateLabel?: any) => {
  let queryString = {
    sector: sector,
    capacityName: capacityName,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    page: pageNumber,
    pageSize: size,
    filterBy: filterInfo,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityDetailsFilters}`, queryString, { headers });
    if (res.data.data.page && Number.parseInt(res.data.data.page.currentPage) > res.data.data.page.totalPages) {
      const res = await axios.post(`${url}${endpoints.powerBICapacityDetailsFilters}`, queryString, { headers });
      return res.data;
    }
    else {
      return res.data;
    }
  } catch (err) {
    return err;
  }
};
export const powerBIGatewayDetailData = async (pageNumber: string, size: string, sector: any, sortingInfo: any, filterInfo: any) => {
  let queryString = {
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    page: pageNumber,
    pageSize: size
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIGatewayDetails}`, queryString, { headers });
    if (res.data.data.page && Number.parseInt(res.data.data.page.currentPage) > res.data.data.page.totalPages) {
      const res = await axios.post(`${url}${endpoints.powerBIGatewayDetails}`, queryString, { headers });
      return res.data;
    }
    else {
      return res.data;
    }
  } catch (err) {
    console.log('Error fetching Gateway Details Data');
    return err;
  }
};
export const powerBICapacitySearch = async (
  searchQuery: any,
  searchField: any,
  filterInfo: any,
  sector: any,
  drillDownStatusFilter: any,
  dateLabel?: any
) => {
  const headers = getAuthorazieHeader();
  
  const requestBody = {
    page: '1',
    size: '50',
    sector: sector,
    filterBy: filterInfo,
    searchField: searchField,
    searchQuery: searchQuery,
    filter: {},
    filterOption: dateLabel
  };

  if (drillDownStatusFilter && Object.keys(drillDownStatusFilter).length) {
    requestBody['filter'] = drillDownStatusFilter;
  }

  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityDetailsFilters}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const fetchSectors = async (sector?: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIgetSectors}`, {}, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching sectors:', err);
    return err;
  }
};

export const fetchPowerBIKPIData = async (sector?: any, filterOption?: any) => {
  console.info("Sector", sector)
  const req = {
    sector: sector || [],
    filterOption: filterOption
  };
  console.info("Sector1", req)

  const requestBody = req;
  try {
    const res = await apiPost(`${url}/powerbi/userActivity/kpis`, requestBody);
    console.info("res.data", res.data)
    return res.data;
  } catch (err) {
    console.error('Error fetching PowerBI KPI data:', err);
    return err;
  }
};

export const fetchProductSuccessKPIs = async (filterOption?: string, filterMonth?: string) => {
  try {
    const res = await apiPost(`${url}/productSuccess/kpis`, { filterOption, filterMonth });
    return res.data;
  } catch (err) {
    console.error('Error fetching ProductSuccess KPI data:', err);
    return null;
  }
};

export const fetchUserActivityGrowthData = async (sector?: any, filterOption?: any, chartTimePeriod?: any) => {
  const req = {
    sector: sector || [],
    filterOption: filterOption,
    chartTimePeriod: chartTimePeriod
  };

  const headers = getAuthorazieHeader();
  const requestBody = req;
  try {
    const res = await axios.post(`${url}/powerbi/userActivity/growthChart`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching User Activity Growth data:', err);
    return err;
  }
};

export const fetchUserActivityTrendData = async (sector?: any, filterOption?: any) => {
  const req = {
    sector: sector || [],
    filterOption: filterOption
  };

  const headers = getAuthorazieHeader();
  const requestBody = req;
  try {
    const res = await axios.post(`${url}/powerbi/userActivity/trendChart`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching User Activity Trend data:', err);
    return err;
  }
};

export const fetchUserActivityTableData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/powerbi/userActivity/tableData`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching User Activity Table data:', err);
    throw err;
  }
};

export const fetchUserActivityFilterOptions = async (field: any, searchTerm = '', userStatusFilter = 'active') => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/powerbi/userActivity/filter-options`,
      { field, searchTerm, userStatusFilter },
      { headers }
    );
    return res.data;
  } catch (err) {
    console.error('Error fetching User Activity Filter Options:', err);
    throw err;
  }
};

export const fetchTableauFilterOptions = async (field: any, searchTerm = '') => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/tableau/tableau-tableData/filters`,
      { field, searchTerm },
      { headers }
    );
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Filter Options:', err);
    throw err;
  }
};

export const fetchReportsTableauTableData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/tableau/reports-tableau/tableData`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching Reports Tableau Table data:', err);
    throw err;
  }
};

export const fetchTableauReportsFilterOptions = async (field: any, searchTerm = '') => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/tableau/tableau-reports/filters`,
      { field, searchTerm },
      { headers }
    );
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Filter Options:', err);
    throw err;
  }
};


export const fetchUserActivityUsersFilter = async (searchTerm = '', pageSize = 100) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/powerbi/userActivity/users-filter`,
      { searchTerm, pageSize },
      { headers }
    );
    return res.data;
  } catch (err) {
    console.error('Error fetching User Activity Users Filter:', err);
    throw err;
  }
};

export const fetchAzureSectors = async (sector?: any) => {
  let queryString = `sector=${JSON.stringify(sector)}`;
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.azuregetSectors}?${queryString}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBICUUtil = async (
  sector: any,
  capacityName: any,
  timePeriod: any,
  filterOption?: any
) => {
  const requestBody = {
    sector: Array.isArray(sector) ? sector : [],
    capacityName: Array.isArray(capacityName) ? capacityName : [],
    timePeriod,
    filterOption: filterOption
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBICUUtil}`, requestBody, { headers });
    return res.data;

  } catch (err) {
    console.error("Error fetching capacity status:", err);
    throw err;
  }
};

export const powerBICapacityGrowth = async (
  sector: any,
  timePeriod: any,
  filterOption?: any
) => {

  const requestBody = {
    sector: Array.isArray(sector) ? sector : [],
    timePeriod,
    filterOption: filterOption
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityGrowth}`, requestBody, { headers });
    return res.data;

  } catch (err) {
    console.error("Error fetching capacity status:", err);
    throw err;
  }
};

export const powerBIGatewayGrowth = async (
  sector: any,
  timePeriod: any,
  filterOption?: any
) => {

  const requestBody = {
    sector: Array.isArray(sector) ? sector : [],
    timePeriod,
    filterOption: filterOption
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIGatewayGrowth}`, requestBody, { headers });
    return res.data;

  } catch (err) {
    console.error("Error fetching gateway status:", err);
    throw err;
  }
};

export const powerBIReportGrowth = async (
  sector: any,
  timePeriod: any,
  filterOption?: any
) => {

  const requestBody = {
    sector: Array.isArray(sector) ? sector : [],
    timePeriod,
    filterOption: filterOption
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIReportGrowth}`, requestBody, { headers });
    return res.data;

  } catch (err) {
    console.error("Error fetching report status:", err);
    throw err;
  }
};

export const powerBIUserGrowth = async (
  sector: any,
  timePeriod: any,
  filterOption?: any
) => {

  const requestBody = {
    sector: Array.isArray(sector) ? sector : [],
    timePeriod,
    filterOption: filterOption
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIUserGrowth}`, requestBody, { headers });
    return res.data;

  } catch (err) {
    console.error("Error fetching user status:", err);
    throw err;
  }
};

export const powerBICapacityStatus = async (
  pageNumber: string,
  size: string,
  sector: any,
  status: any,
  sortingInfo: any
) => {
  let queryString = {
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: { capacityStatus: status },
    page: pageNumber,
    pageSize: size
  };
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacityStatus}`, queryString, { headers });

    if (res.data.data.page.currentPage > res.data.data.page.totalPages) {
      const res = await axios.post(`${url}${endpoints.powerBICapacityStatus}`, queryString, { headers });
      return res.data;
    } else {
      return res.data;
    }
  } catch (err) {
    console.error("Error fetching capacity status:", err);
    throw err; // Rethrow error for handling in the calling function
  }
};

export const powerBICapacitiesLastRefreshData = async (filter: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
  };
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacitiesLastRefresh}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBICapacitiesGrowthLastRefreshData = async (filter: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
  };
  try {
    const res = await axios.post(`${url}${endpoints.powerBICapacitiesGrowthLastRefresh}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBIGatewayGrowthLastRefreshData = async (filter: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
  };
  try {
    const res = await axios.post(`${url}${endpoints.powerBIGatewayGrowthLastRefresh}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBIReportsGrowthLastRefreshData = async (filter: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
  };
  try {
    const res = await axios.post(`${url}${endpoints.powerBIReportsGrowthLastRefresh}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const powerBIUsersGrowthLastRefreshData = async (filter: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
  };
  try {
    const res = await axios.post(`${url}${endpoints.powerBIUsersGrowthLastRefresh}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const getProjectById = async (data: any) => {

  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.dataPipelineProjectById}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getCustomDashboard = async (queryparams: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(
      `${url}${endpoints.customDashboard}`,
      queryparams,
      { headers }
    );
    return res.data;
  } catch (err) {
    console.error('Error fetching custom dashboard data:', err);
    return err;
  }
};

export const fetchFinOpsExecutiveSummary = async (filter: string, filterBy?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    filterBy: filterBy
  };
  try {
    const res = await axios.post(`${url}${endpoints.finOpsExecutiveSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchFinOpsMonthlySummary = async (
  year: number,
  filters?: {
    programName?: string;
    sectorName?: string;
    platformName?: string;
    type?: string;
    startMonth?: number;
    endMonth?: number;
  }
) => {
  const headers = getAuthorazieHeader();
  const requestBody = { year, ...filters };
  try {
    const res = await axios.post(`${url}/finopsExecutive/summary`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching finops monthly summary:', err);
    throw err;
  }
};

export const fetchFinOpsPrograms = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/finopsExecutive/programs`, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching finops programs:', err);
    throw err;
  }
};

export const fetchFinOpsSectors = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/finopsExecutive/sectors`, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching finops sectors:', err);
    throw err;
  }
};

export const fetchFinOpsPlatforms = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/finopsExecutive/platforms`, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching finops platforms:', err);
    throw err;
  }
};

export const fetchFinOpsSpendingByType = async (type: 'Program' | 'Sector' | 'Platform', year: number = 2025, limit: number = 5) => {
  const headers = getAuthorazieHeader();
  try {
    const body = { type, year, limit };
    const res = await axios.post(`${url}/finopsExecutive/spending-by-type`, body, { headers });
    return res.data;
  } catch (err) {
    console.error(`Error fetching finops spending by ${type}:`, err);
    throw err;
  }
};

export const fetchFinOpsChartSeries = async (
  year: number = 2025,
  filters?: {
    programName?: string;
    sectorName?: string;
    platformName?: string;
    startMonth?: number;
    endMonth?: number;
  }
) => {
  const headers = getAuthorazieHeader();
  try {
    const body = { year, ...filters };
    const res = await axios.post(`${url}/finopsExecutive/chart-series`, body, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching finops chart series:', err);
    throw err;
  }
};

export const getDpProjectKpi = async (id: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    programId: Number(id)
  }
  try {
    const res = await axios.post(`${url}${endpoints.dpProjectKpiCount}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getNormalCompletionTime = async (id: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    jobId: Number(id)
  }
  try {
    const res = await axios.post(`${url}${endpoints.getNormalCompletion}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export interface GetDatabricksClusterImpactDetailsParams {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  fromDateTime?: string;
  toDateTime?: string;
  incidentId?: string;
  databrickIncident?: string;
  mimType?: string;
}

export const getDatabricksClusterImpactDetails = async (
  params: GetDatabricksClusterImpactDetailsParams
) => {
  const {
    page,
    size,
    sortingInfo,
    filterInfo,
    fromDateTime,
    toDateTime,
    incidentId,
    databrickIncident,
    mimType
  } = params;
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    incidentId: incidentId,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    type: databrickIncident,
    mimType: mimType
  };
  try {
    const res = await axios.post(`${url}${endpoints.getDatabricksClusterImpactDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getAddedSchedules = async (data: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${urlV2}${endpoints.displayAddedJobs}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const addIbpPipelines = async (data: any[]) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(
      `${urlV2}${endpoints.ibpGdoAddPipelines}`,
      { Data: data },
      { headers },
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteIbpPipelines = async (data: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpGdoDeletePipelines}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getAddedWinJobs = async (data: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getAddedWinJobs}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const getAllDependentJobs = async (data: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${urlV2}${endpoints.getAllDependentJobs}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getAddedScheduleSearchFilterColumn = async (data: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.getAddedScheduleFilterColumn}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export interface GetTeradataJobExecutionsParams {
  page: string;
  size: string;
  sortingInfo: any;
  filterInfo: any;
  fromDateTime?: string;
  toDateTime?: string;
  isDataestate?: any;
  execution?: string;
}

export const getTeradataJobExecutions = async (
  params: GetTeradataJobExecutionsParams
) => {
  const {
    page,
    size,
    sortingInfo,
    filterInfo,
    fromDateTime,
    toDateTime,
    isDataestate,
    execution
  } = params;
  const headers = getAuthorazieHeader();
  const requestBody = {
    page: page,
    size: size,
    fromDateTime: fromDateTime,
    toDateTime: toDateTime,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filter: filterInfo,
    isDataEstate: isDataestate,
    isExecution: execution
  };
  try {
    const res = await axios.post(`${url}${endpoints.teradataImpactDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchdownTimeRequest = async (filter: string, filterMonth?: string, sectors?: any, formattedDate?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filter,
    startDate: filterMonth || null,
    sectors: [sectors],
    dateFilter: filterMonth,
    formattedDate
  };
  try {
    const res = await axios.post(`${url}${endpoints.getdownTimeRequest}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchJobPerformance = async (fromDate: string, toDate?: string, isExecution?: number, plateform?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    fromDateTime: fromDate,
    toDateTime: toDate,
    type: isExecution,
    plateform: plateform
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataEstateJobPerformance}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDependentJobExeData = async (jobData: any) => {
  const headers = getAuthorazieHeader();

  try {
    let payload = getIsV2() ? { ...jobData } : { jobData }
    const res = await axios.post(`${urlV2}${endpoints.depJobExeDataV2}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const addResourceMappingProgram = async (body: any) => {
  const requestBody = {
    programName: body.programName,
    programOwner: body.programOwner,
    programDescription: body.programDescription
  };
  try {
    return (await apiPost(`${url}${endpoints.addProgram}`, requestBody)).data;
  } catch (err) {
    return err;
  }
};

export const fetchResourceMappingPrograms = async (page: any, size: any, sortingInfo?: any, searchText?: any) => {
  const requestBody = {
    pageNo: page,
    pageSize: size,
    sortingInfo: sortingInfo,
    searchText: searchText || ''
  };
  try {
    return (await apiPost(`${url}${endpoints.getPrograms}`, requestBody)).data;
  } catch (err) {
    return err;
  }
};

export const addResourceMappingProject = async (body: any) => {
  try {
    return (await apiPost(`${url}${endpoints.addProject}`, body)).data
  } catch (err) {
    return err;
  }
};

export const getProgramDetailsById = async (id: any) => {
  let body = {
    programId: id
  }
  try {
    return (await apiPost(`${url}${endpoints.getProgramDetailsById}`, body)).data;
  } catch (err) {
    return err;
  }
};

export const getProjectListByProgramId = async (id: any, pageNo: any, pageSize: any, sortingInfo: any, searchText?: any) => {
  let body = {
    programId: id,
    pageNo,
    pageSize,
    sortingInfo,
    searchText: searchText || ''
  }
  try {
    return (await apiPost(`${url}${endpoints.getProjectListByProgramId}`, body)).data
  } catch (err) {
    return err;
  }
};

// Export
export const exportProjectListByProgramId = async (id: any, exportColumns: any, formatType: any, tableName: any, sortingInfo: any, searchText?: any) => {
  let body = {
    programId: id,
    exportColumns: exportColumns,
    formatType: formatType,
    tableName: tableName,
    sortingInfo,
    searchText: searchText || ''
  }
  try {
    const res = await apiPost(`${url}${endpoints.exportProjectListByProgramId}`, body, { responseType: "blob" });
    // Extract filename from response headers
    const contentDisposition = res.headers['content-disposition'];
    let filename = `Finops_ProgramByProjectList.${formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exporturl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    return err;
  }
};

export const editProgramDetails = async (data: any) => {
  let body = {
    programId: data.id,
    programName: data.programName,
    programDescription: data.programDescription,
    programOwner: data.programOwner
  }
  try {
    return (await apiPost(`${url}${endpoints.editProgramDetails}`, body)).data
  } catch (err) {
    return err;
  }
};

export const deleteProgram = async (id: any) => {
  let body = {
    programId: id
  }
  try {
    return (await apiPost(`${url}${endpoints.deleteProgramDetails}`, body)).data;
  } catch (err) {
    return err;
  }
};

export const editProjectDetails = async (data: any) => {
  let body = {
    projectId: data.id,
    projectOwner: data.projectOwner,
    projectName: data.projectName,
    projectDescription: data.projectDescription,
  }
  try {
    return (await apiPost(`${url}${endpoints.editProjectDetails}`, body)).data
  } catch (err) {
    return err;
  }
};

export const deleteProjectDetails = async (data: any) => {
  let body = {
    projectId: data.projectId,
    programId: data.programId
  }
  try {
    return (await apiPost(`${url}${endpoints.deleteProjectDetails}`, body)).data;
  } catch (err) {
    return err;
  }
};

export const getProjectDetailsById = async (data: any) => {
  try {
    return (await apiPost(`${url}${endpoints.getProjectDetailsById}`, data)).data;
  } catch (err) {
    return err;
  }
};

export const getAllocation = async (groupId: any, isAudit: any, userTimezone: any) => {
  let body = {
    groupId: groupId,
    isAudit: isAudit || false,
    userTimezone: userTimezone
  }
  try {
    return (await apiPost(`${url}${endpoints.getAllocation}`, body)).data;
  } catch (err) {
    return err;
  }
};

export interface GetCyclicExecutionsParams {
  cyclic: any;
  dataAssetName: string;
  silverTableName: string;
  jobId: any;
  fromDate: any;
  platformId: any;
  scheduleInfo: any;
  triggerName?: string;
  startDate?: string;
  endDate?: string;
  pipelineParam?: any;
}

export const getCyclicExecutions = async (params: GetCyclicExecutionsParams) => {
  const {
    cyclic,
    dataAssetName,
    silverTableName,
    jobId,
    fromDate,
    platformId,
    scheduleInfo,
    triggerName,
    startDate,
    endDate,
    pipelineParam
  } = params;
  let body: any = {
    cyclic: cyclic,
    jobId: jobId,
    fromDate: fromDate || moment().format("YYYY-MM-DD"),
    platformId: platformId,
    scheduleInfo,
    startDate: startDate || moment().format("YYYY-MM-DD"),
    endDate: endDate || moment().format("YYYY-MM-DD"),
    triggerName: triggerName,
    dataAssetName,
    silverTableName
  }
  if (platformId === 4 && pipelineParam) {
    body['pipeline_param'] = pipelineParam;
  }
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.cyclicExeData}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getSLAHistory = async (projectId: any) => {
  let body = {
    projectId: projectId
  }
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.slaHistoryData}`, body, { headers });
    return res.data;

  }
  catch (err) {
    return err;
  }
}

export const createKPIConfig = async (config: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.kpiFrameworkConfigList}/create`, config, { headers },);
    return res.data;
  } catch (err: any) {
    return err.response?.data || {};;
  }
};

export const updateKPIConfig = async (config: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.put(`${url}${endpoints.kpiFrameworkConfigList}/${config._id}`, config, { headers },);
    return res.data;
  } catch (err: any) {
    return err.response?.data || {};
  }
};

export const deleteKPIConfig = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.delete(`${url}${endpoints.kpiFrameworkConfigList}/${id}`, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const activateKPIConfig = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.kpiFrameworkConfigList}/${id}/activate`, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deactivateKPIConfig = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.kpiFrameworkConfigList}/${id}/deactivate`, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getKpiFrameworkConfigList = async (body: any) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.kpiFrameworkConfigList}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getKPIFilterOptions = async () => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.kpiFrameworkConfigList}/filterOptions`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const getAlertKpiProgramsSectors = async (isIBP: boolean = false) => {
  let headers = getAuthorazieHeader();
  try {
    const endpoint = isIBP ? 'programsAndSectorsIBP' : 'programsAndSectors';
    const res = await axios.get(`${url}${endpoints.kpiFrameworkConfigList}/${endpoint}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const getKpiFrameworkConfigDetails = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.get(`${url}${endpoints.kpiFrameworkConfigList}/${id}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getKpiFrameworkIngestData = async (body: any, id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.kpiFrameworkIngestList}/${id}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const bulkJobAdditionDp = async (formData: FormData) => {
  let headers = getAuthorazieHeader();
  headers = {
    ...headers,
    'Content-Type': 'multipart/form-data'
  }
  const body = formData
  try {
    const res = await axios.post(`${urlV2}${endpoints.jobUploadDp}`, body, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const saveAlertConfig = async (formData: any) => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.alertFrameworkConfig}/create`, formData, { headers });
    return res.data;
  } catch (err: any) {
    return err.response?.data || {};
  }
}

export const getConfigList = async (body: any) => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.alertFrameworkConfig}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const getConfigDetails = async (id: any, body: any) => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.alertFrameworkConfig}/${id}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const getFilterOptions = async () => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.alertFrameworkConfig}/filterOptions`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const getConfigAlerts = async (id: any, body: any) => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.alertFrameworkConfig}/${id}/alerts`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const updateAlertConfig = async (id: string, formData: any) => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.put(`${url}${endpoints.alertFrameworkConfig}/${id}`, formData, { headers });
    return res.data;
  } catch (err: any) {
    return err.response?.data || {};
  }
}

export const activateAlertConfig = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.alertFrameworkConfig}/${id}/activate`, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deactivateAlertConfig = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.post(`${url}${endpoints.alertFrameworkConfig}/${id}/deactivate`, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteAlertConfig = async (id: string) => {
  const headers = getAuthorazieHeader();

  try {
    const res = await axios.delete(`${url}${endpoints.alertFrameworkConfig}/${id}`, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const postJobComment = async (
  projectId?: any,
  jobExeId?: any,
  jobId?: any,
  comment?: string,
  platformId?: any,
  rowData?: any,
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    projectId: projectId,
    jobExeId: jobExeId,
    jobId: jobId,
    comment: comment,
    startAt: rowData?.actualStartTime,
    endAt: rowData?.actualEndTime,
    platformId,
  };
  try {
    const res = await axios.post(`${urlV2}${endpoints.postComment}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};



// generic export service for table data without transformation
export const exportTableData = async ({ tableName, formatType, exportColumns, sortingInfo }: any) => {

  const fileNameMappings: any = {
    finopsProgramTable: 'Finops_Program_List'
  };

  const headers = getAuthorazieHeader();
  const requestBody = { tableName, formatType, exportColumns, sortingInfo };

  try {
    const res = await axios.post(
      `${url}${endpoints.genericExport}`,
      requestBody,
      {
        headers,
        responseType: "blob",
      }
    );

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `${fileNameMappings[tableName] || tableName}.${formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true; // Indicate success
  } catch (err) {
    console.error("Error exporting table data:", err);
    throw err;
  }
};
export const powerBIActivityDetailData = async (pageNumber: string, size: string, sector: any, sortingInfo: any, filterInfo: any, dateLabel?: any) => {
  let queryString = {
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    filterBy: filterInfo,
    page: pageNumber,
    pageSize: size,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIActivityDetails}`, queryString, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const powerBIActivityDetailPagination = async (pageNumber: string, size: string, sector: any, sortingInfo: any, filterInfo: any, dateLabel?: any) => {
  let queryString = {
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    page: pageNumber,
    pageSize: size,
    filterBy: filterInfo,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIActivityDetailsPagination}`, queryString, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const powerBIActivityDetailFilters = async (pageNumber: string, size: string, sector: any, sortingInfo: any, filterInfo: any, dateLabel?: any) => {
  let queryString = {
    sector: sector,
    sortBy: sortingInfo.sortBy,
    orderBy: sortingInfo.sortDir,
    page: pageNumber,
    pageSize: size,
    filterBy: filterInfo,
    filterOption: dateLabel
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIActivityDetailsFilters}`, queryString, { headers });
    if (res.data.data.page && Number.parseInt(res.data.data.page.currentPage) > res.data.data.page.totalPages) {
      const res = await axios.post(`${url}${endpoints.powerBIActivityDetailsFilters}`, queryString, { headers });
      return res.data;
    }
    else {
      return res.data;
    }
  } catch (err) {
    return err;
  }
};
export const powerBIActivitySearch = async (
  searchQuery: any,
  searchField: any,
  filterInfo: any,
  sector: any,
  drillDownStatusFilter: any,
  dateLabel?: any
) => {
  const headers = getAuthorazieHeader();
  
  const requestBody = {
    page: '1',
    size: '50',
    sector: sector,
    filterBy: filterInfo,
    searchField: searchField,
    searchQuery: searchQuery,
    filter: {},
    filterOption: dateLabel
  };

  if (drillDownStatusFilter && Object.keys(drillDownStatusFilter).length) {
    requestBody['filter'] = drillDownStatusFilter;
  }

  try {
    const res = await axios.post(`${url}${endpoints.powerBIActivityDetailsFilters}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getContactDetails = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getContactDetails}`, {}, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getExecutiveStatusData = async (programId: number, date: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    program_Id: programId,
    selectedDate: date
  }
  try {
    const res = await axios.post(`${url}${endpoints.getExecutiveStatusData}`, requestBody, { headers },);
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getPlatformCostDetails = async (filterBy: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterBy: filterBy
  }
  try {
    const res = await axios.post(`${url}${endpoints.finopsPlatformCostSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getFinopsCostBreakupDetails = async (filterBy: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterBy: filterBy
  }
  try {
    const res = await axios.post(`${url}${endpoints.finopsCostBreakups}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getFinopsTotalCostDetails = async () => {
  const headers = getAuthorazieHeader();
  const requestBody = {}
  try {
    const res = await axios.post(`${url}${endpoints.finopsTotalCostDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getFinopsPPTEmbedUrls = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.finopsPPTEmbedUrl}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const addOrUpdateInsights = async ({
  title,
  xopsType,
  description,
  insight_id,
  technology = null,
  month = ''
}: {
  title: string;
  xopsType: string;
  description: string;
  insight_id?: number;
  technology?: string | null;
  month?: string
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    insight_id: insight_id,
    title: title,
    xopsType: xopsType,
    description: description,
    technology: technology,
    month: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.addOrUpdateInsight}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getInsights = async (xopsType: string, technology: string | null = null, selectedMonth: string = '') => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    xopsType: xopsType,
    technology: technology,
    dateFilter: selectedMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.getInsights}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteInsights = async (insight_id: number) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    insight_id: insight_id
  };
  try {
    const res = await axios.post(`${url}${endpoints.deleteInsights}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// FinOps Executive specific insights functions
export const finopsExecutiveAddOrUpdateInsight = async ({
  title,
  xopsType,
  description,
  insight_id,
  technology = null,
  month = ''
}: {
  title: string;
  xopsType: string;
  description: string;
  insight_id?: number;
  technology?: string | null;
  month?: string
}) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    insight_id: insight_id,
    title: title,
    xopsType: xopsType,
    description: description,
    technology: technology,
    month: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsExecutiveAddOrUpdateInsight}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const finopsExecutiveGetInsights = async (xopsType: string, technology: string | null = null, selectedMonth: string = '') => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    xopsType: xopsType,
    technology: technology,
    dateFilter: selectedMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsExecutiveGetInsights}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const finopsExecutiveDeleteInsight = async (insight_id: number) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    insight_id: insight_id
  };
  try {
    const res = await axios.post(`${url}${endpoints.finopsExecutiveDeleteInsight}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDataprocessingSummaryKpi = async (filterMonth?: string, sectors?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    groupByFilters: 'KPI_NAME,DIMENSION,MONTH',
    startDate: filterMonth || null,
    sectors: [sectors],
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataEstateDataProcessingSummary}`, requestBody, { headers });//
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getDataEstateKpiSector = async (filterInfo: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filter: filterInfo
  };
  try {
    const res = await axios.post(`${url}${endpoints.dataestateKpiSectorDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const fetchCapacities = async (dateSelection?: any) => {
  const reqBody = {
    filterOption: dateSelection
  };
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIgetCapacityName}`, reqBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getExecutiveProjData = async (scheduleDate?: any, programId?: any) => {

  const headers = getAuthorazieHeader();
  let body = { scheduleDate, programId }
  try {
    const res = await axios.post(`${url}${endpoints.getExecutiveProjData}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const saveSearchedQuery = async (body: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    queryName: body.queryName,
    query: body.query,
    queryJson: body.queryJson,
    selectedColumns: body.selectedColumns,
    queryPreference: body.queryPreference,
    selectedPaths: body.selectedPaths,
    groupPaths: body.groupPaths,
    program: body.program ?? '',
    project: body.project ?? '',
    isAllocated: body.isAllocated ?? 0,
    queryId: body.queryId ?? -1,
    isSaveQuery: body.isSaveQuery ?? false,
    commitAll: body.commitAll ?? false,
    isProjectDetails: body.isProjectDetails ?? false,
    isCost: body.isCost ?? false,
    dateRange: body.dateRange ?? '',
    sector: body.sector ?? '',
  };
  try {
    const res = await axios.post(`${url}${endpoints.saveSearchedQuery}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchSavedQueryList = async (searchText?: any, queryPreference?: any, program?: any, project?: any, isCost?: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    searchText: searchText || '',
    queryPreference: queryPreference || '',
    program: program || '',
    project: project || '',
    isCost: isCost || false
  };
  try {
    const res = await axios.post(`${url}${endpoints.getSavedQueryList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteSavedQuery = async (id: any, program: any, project: any, isUnlink: any = false, isCost: any = false) => {
  const headers = getAuthorazieHeader();
  let body = {
    queryId: id,
    program: program,
    project: project,
    isUnlink: isUnlink,
    isCost: isCost
  }
  try {
    const res = await axios.post(`${url}${endpoints.deleteSavedQueryDetails}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const editQueryDetails = async (data: any) => {
  const headers = getAuthorazieHeader();
  let body = {
    queryId: data.queryId,
    queryName: data.queryName,
    isCost: data.isCost ?? false,
  }
  try {
    const res = await axios.post(`${url}${endpoints.editQueryDetails}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getDataEstateSector = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.dataestateSectorDetails}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getIncidentDataByAlertId = async (alertId: any) => {
  const headers = getAuthorazieHeader(); // assuming this returns { Authorization: 'Bearer ...' }
  try {
    const res = await axios.get(
      `${url}/alertFramework/alerts/${alertId}/incidentData`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getBulkEditFinopsinputProgramProjectOption = async ({
  searchText = '',
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = { searchText };
  try {
    const res = await axios.post(`${url}${endpoints.finopsBulkEditInputPrgmProjOptions}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchCopyProject = async (body: any) => {
  try {
    return (await apiPost(`${url}${endpoints.fetchCopyProject}`, body)).data
  } catch (err) {
    return err;
  }
};

export const getStorageGrowthTrend = async ({ projectFilter, sectorFilter }: { projectFilter: any, sectorFilter: any }) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(
      `${url}${endpoints.fetchStorageGrowthTrend}`,
      { headers, data: { projectFilter, sectorFilter } }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchStorageAccountsDetails = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.fetchStorageAccountsDetails}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getContainerGrowthTrend = async ({
  storageAccountName,
  path,
  type
}: {
  storageAccountName: string;
  path: string;
  type: string;
}) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    storageAccountName,
    path,
    type
  };

  try {
    const res = await axios.post(
      `${url}${endpoints.fetchContainerGrowthTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getTopStorageAccounts = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.fetchTopStorageAccounts}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getStorageAccountsOptions = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.fetchStorageAccountOptions}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getContainerOptions = async (storageAccountName: string) => {
  const headers = getAuthorazieHeader();
  let body = {
    storageAccountName: storageAccountName
  }
  try {
    const res = await axios.post(`${url}${endpoints.fetchConatinerOptions}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getProjectOptions = async (sectorList = '') => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.fetchProjectOptions}?sectorList=${sectorList}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getTopContainers = async (storageAccountName: string) => {
  const headers = getAuthorazieHeader();
  let body = {
    storageAccountName: storageAccountName
  }
  try {
    const res = await axios.post(`${url}${endpoints.fetchTopContainers}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getStorageBySector = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(
      `${url}${endpoints.fetchStorageBySector}`,
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getTopLevelFolders = async (storageAccountName: string, containerName: string, parentFolderName: string) => {
  const headers = getAuthorazieHeader();
  let body = {
    storageAccountName: storageAccountName,
    containerName: containerName,
    parentFolderName: parentFolderName
  }
  try {
    const res = await axios.post(`${url}${endpoints.fetchTopLevelFolders}`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchTopStorageAccountsList = async ({
  storageAccountName,
  path,
  level,
  page,
  limit,
  sortBy,
  sortOrder,
  filterBy
}: {
  storageAccountName: string;
  path: string;
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filterBy?: any;
  level?: number;
}) => {
  const headers = getAuthorazieHeader();

  // Construct requestBody without level if undefined
  const requestBody: any = {
    storageAccountName,
    path,
    page,
    limit,
    sortBy,
    sortOrder,
    filterBy
  };

  if (level !== undefined) {
    requestBody.level = level;
  }

  try {
    const res = await axios.post(`${url}${endpoints.fetchTopStorageAccountsList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportTopStorageAccountsList = async ({
  storageAccountName,
  path,
  level,
  page,
  limit,
  sortBy,
  sortOrder,
  filterBy,
  storageUnit
}: {
  storageAccountName: string;
  path: string;
  page: number;
  limit: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filterBy?: any;
  level?: number;
  storageUnit?: 'GB' | 'TB' | 'PB';
}) => {
  const headers = getAuthorazieHeader();

  const requestBody: any = {
    storageAccountName,
    path,
    page,
    limit,
    sortBy,
    sortOrder,
    filterBy,
    storageUnit
  };

  if (level !== undefined) {
    requestBody.level = level;
  }

  try {
    const res = await axios.post(`${url}${endpoints.exportTopStorageAccountsList}`, requestBody, { headers, responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers['content-disposition'];
    let filename = `TopStorageAccounts_${storageAccountName}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exporturl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    return true;
  } catch (err) {
    return err;
  }
};

export const clusterSpendData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.databricksObservabilityClusterSpend}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const runningClusterData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.runClusterCount}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const scheduleCountData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.scheduleCount}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const clusterCountTypeData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.clusterCountType}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const clusterSpndSpc = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.clusterSpendByWorkspace}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const nodeTypeData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.nodeTypeDistribution}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const computeData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.computeTypeByWorkspace}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const jobStatusData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.jobStatusDistribution}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const jobRunSuccessVsFailData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.jobRunSuccessVsFail}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const jobCostData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.jobCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const totalJobData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.totalJobsByWorkspace}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const avgFailureWorkspaceData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.avgFailureWorkspace}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const interactiveClusterData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.interactiveCluster}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}


export const spendByCategoryData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.spendByCategory}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const withoutAutoTerminationData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.withoutAutoTermination}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const autoTerminationData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.autoTermination}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const clusterCountData = async (startDate: any, endDate: any, env: string, sector: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.clusterCount}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const fetchMimsMetrics = async ({
  selectedMonth, sector, isClosedDate
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dateFilter: selectedMonth,
    sector: sector,
    isClosedDate: isClosedDate
  };
  try {
    const res = await axios.post(`${url}${endpoints.mimsMetric}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchMimsMetricTrend = async (sector: string, isClosedDate: any, filterMonth: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: sector,
    isClosedDate: isClosedDate,
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.mimsMetricTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// 08-07-2025 (Workspace)
export const workspaceLastSyn = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.lastSync}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const workspaceGetEnv = async (location: string) => {
  const headers = getAuthorazieHeader();
  try {
    const query = location === 'ALL' ? '' : `?location=${encodeURIComponent(location)}`;
    const res = await axios.get(`${url}${endpoints.getEnv}${query}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const overAllCost = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.overallCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const overAllDbuCount = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.overAllDbuCount}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const activeWorkspacesData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.databricksObservabilityactiveWorkspaces}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const spendWorkspace = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.workspaceSpend}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const costByTypeData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.costType}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const countByTypeData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.countType}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const withoutAUtoTermcountByTypeData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.withoutAutoTermcountType}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const runTimeWorkspace = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.workspaceRunTime}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const costByDateData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.costDate}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const countByDateData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.countDate}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

// ADB_Cluster_Analytics

export const clusterLastSyncData = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.clusterLastSync}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const countByCategoryData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.countByCategory}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const spentByCategoryData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.spentByCategory}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const costByClusterData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.costByCluster}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const clusterSpinsUpData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.clusterSpinsUp}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const computeJobData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.computeJob}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const computeInteractiveData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.computeInteractive}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export interface OverAllCostYTDDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
}

export const overAllCostYTDData = async (params: OverAllCostYTDDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.overAllCostYTD}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export interface OverAllCostTreeParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
}

export const overAllCostTree = async (params: OverAllCostTreeParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.overAllCostTree}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export interface OverAllCostDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  pageSize?: number;
  exportType?: string;
  sortField?: string;
  sortOrder?: string;
}

const downloadBlob = (blob: Blob, filename: string) => {
  const exportUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = exportUrl;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(exportUrl);
};

const escapeCsvValue = (v: any): string => {
  if (v === null || v === undefined) return '';
  const s = typeof v === 'string' ? v : String(v);
  if (s.includes('"') || s.includes(',') || s.includes('\n')) return `"${s.replaceAll('"', '""')}"`;
  return s;
};

const exportJsonRowsToCsv = (rows: any[], filename: string) => {
  const keys = Object.keys(rows[0]);
  const header = keys.join(',');
  const body = rows.map((r: any) => keys.map((k) => escapeCsvValue(r[k])).join(',')).join('\r\n');
  const csv = `${header}\r\n${body}`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  downloadBlob(blob, filename);
};

const exportBufferToXlsx = (bufData: number[], contentType: string, filename: string) => {
  const uint8 = new Uint8Array(bufData);
  const blob = new Blob([uint8], { type: contentType });
  downloadBlob(blob, filename);
};

const handleBlobExport = async (requestBody: any, headers: any, startDate: any, endDate: any) => {
  const resBlob = await axios.post(`${url}${endpoints.overAllCostData}`, requestBody, {
    headers,
    responseType: 'blob'
  });

  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const contentDisposition = resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'];
  let filename = `costTable_${startDate}_to_${endDate}.xlsx`;
  if (contentDisposition) {
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition as string);
    if (fileNameMatch) {
      const rawName = fileNameMatch[1] || fileNameMatch[2];
      try {
        filename = decodeURIComponent(rawName.replace(/['"]/g, ''));
      } catch {
        filename = rawName.replace(/['"]/g, '');
      }
    }
  }
  const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: resBlob.headers });
    return { success: false, error: 'Empty file returned by server' };
  }
  downloadBlob(blob, filename);

  return { success: true };
};

const handleExcelExport = async (requestBody: any, headers: any, startDate: any, endDate: any) => {
  const resJson = await axios.post(`${url}${endpoints.overAllCostData}`, requestBody, { headers });

  if (resJson?.data) {
    const payload = resJson.data;
    const rows = payload?.data?.data;
    if (payload?.success && Array.isArray(rows) && rows.length > 0) {
      const filename = payload?.data?.filename || `costTable${startDate}_to_${endDate}.csv`;
      exportJsonRowsToCsv(rows, filename);
      return { success: true };
    }

    const buf = payload?.data?.buffer;
    if (payload?.success && buf && Array.isArray(buf.data) && buf.data.length > 0) {
      const mime = payload.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const filename = payload.data.filename || `costTable_${startDate}_to_${endDate}.xlsx`;
      exportBufferToXlsx(buf.data, mime, filename);
      return { success: true };
    }
  }

  return await handleBlobExport(requestBody, headers, startDate, endDate);
};

export const overAllCostData = async (params: OverAllCostDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    pageSize,
    exportType,
    sortField,
    sortOrder
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      page: page ?? 1,
      pageSize: pageSize ?? 100,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(exportType && { exportType: exportType }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    };

    if (exportType?.toLowerCase() === 'excel') {
      return await handleExcelExport(requestBody, headers, startDate, endDate);
    }

    const res = await axios.post(`${url}${endpoints.overAllCostData}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export interface WorkspaceWithRunTimeTableParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  filters?: Array<{ operator: string; field: string; value: string }>;
  workspaceName?: string;
  exportType?: string;
  sortField?: string;
  sortOrder?: string;
}

const buildWorkspaceRequestFilters = (
  filters: any,
  sector: string,
  env: string,
  location: string
) => {
  if (filters?.length !== 0) {
    return Array.isArray(filters) ? filters : [];
  }
  const sectorVal = sector && sector !== 'all' ? sector : 'All';
  const envVal = env && env !== 'both' ? env : 'All';
  const locationVal = location && location !== 'ALL' ? location : 'All';
  
  return [
    { operator: 'OR', field: 'Sector', value: sectorVal },
    { operator: 'OR', field: 'Environment', value: envVal },
    { operator: 'OR', field: 'Location', value: locationVal }
  ];
};

const handleWorkspaceJsonPayload = (payload: any, startDate: any, endDate: any): boolean => {
  if (!payload) return false;

  const rows = payload?.data?.data;
  if (payload?.success && Array.isArray(rows) && rows.length > 0) {
    const keys = Object.keys(rows[0]);
    const header = keys.join(',');
    const body = rows.map((r: any) => keys.map((k) => escapeCsvValue(r[k])).join(',')).join('\r\n');
    const csv = `${header}\r\n${body}`;

    const filename = payload?.data?.filename || `workspaceTable_${startDate}_to_${endDate}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);

    return true;
  }

  const buf = payload?.data?.buffer;
  if (payload?.success && buf && Array.isArray(buf.data) && buf.data.length > 0) {
    const mime = payload.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = payload.data.filename || `workspaceTable_${startDate}_to_${endDate}.xlsx`;
    exportBufferToXlsx(buf.data, mime, filename);
    return true;
  }

  return false;
};

const getBlobFilename = (contentDisposition: string | undefined, startDate: any, endDate: any): string => {
  let filename = `workspaceTable_${startDate}_to_${endDate}.xlsx`;
  if (contentDisposition) {
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    if (fileNameMatch) {
      const rawName = fileNameMatch[1] || fileNameMatch[2];
      try {
        filename = decodeURIComponent(rawName.replace(/['"]/g, ''));
      } catch {
        filename = rawName.replace(/['"]/g, '');
      }
    }
  }
  return filename;
};

const handleWorkspaceExcelExport = async (requestBody: any, headers: any, startDate: any, endDate: any) => {
  const resJson = await axios.post(`${url}${endpoints.workspaceWithRunTimeTable}`, requestBody, { headers });

  if (handleWorkspaceJsonPayload(resJson?.data, startDate, endDate)) {
    return { success: true };
  }

  const resBlob = await axios.post(`${url}${endpoints.workspaceWithRunTimeTable}`, requestBody, {
    headers,
    responseType: 'blob'
  });

  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const contentDisposition = resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'];
  const filename = getBlobFilename(contentDisposition, startDate, endDate);
  const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: resBlob.headers });
    return { success: false, error: 'Empty file returned by server' };
  }
  downloadBlob(blob, filename);

  return { success: true };
};

export const workspaceWithRunTimeTable = async (params: WorkspaceWithRunTimeTableParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit,
    filters,
    workspaceName,
    exportType,
    sortField,
    sortOrder
  } = params;
  try {
    const headers = getAuthorazieHeader();

    const requestFilters = buildWorkspaceRequestFilters(filters, sector, env, location);

    const requestBody: any = {
      startDate: `${startDate}`,
      endDate: `${endDate}`,
      filters: requestFilters,
      ...(selectedDateInString && { dateRange: selectedDateInString }),
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { pageSize: limit }),
      ...(exportType && { exportType: exportType }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    };

    if (workspaceName) requestBody.workspaceName = workspaceName;

    if (exportType?.toLowerCase() === 'excel') {
      return await handleWorkspaceExcelExport(requestBody, headers, startDate, endDate);
    }

    const res = await axios.post(`${url}${endpoints.workspaceWithRunTimeTable}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('workspaceWithRunTimeTable API error', err);
    return err;
  }
}

export interface RunTimeByWorkspaceDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  workspaceName?: string;
}

export const runTimeByWorkspaceData = async (params: RunTimeByWorkspaceDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit,
    workspaceName
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      ...(workspaceName && { workspaceName })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.runTimeByWorkspace}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}
export interface ActiveWorkspacesTableDataParams {
  startDate: string;
  endDate: string;
  selectedDateInString: string;
  sector: string;
  location: string;
  page: number;
  pageSize: number;
  filters: Array<{ operator: string; field: string; value: string; }>;
  workspaceName?: string;
  searchText?: any;
  exportType?: string;
  sortField?: string;
  sortOrder?: string;
}

const getActiveWorkspacesBlobFilename = (contentDisposition: string | undefined, startDate: any, endDate: any): string => {
  let filename = `activeWorkspace_${startDate}_to_${endDate}.xlsx`;
  if (contentDisposition) {
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    if (fileNameMatch) {
      const rawName = fileNameMatch[1] || fileNameMatch[2];
      try {
        filename = decodeURIComponent(rawName.replace(/['"]/g, ''));
      } catch {
        filename = rawName.replace(/['"]/g, '');
      }
    }
  }
  return filename;
};

const handleActiveWorkspacesJsonPayload = (payload: any, startDate: any, endDate: any): boolean => {
  if (!payload) return false;

  const rows = payload?.data?.data;
  if (payload?.success && Array.isArray(rows) && rows.length > 0) {
    const keys = Object.keys(rows[0]);
    const header = keys.join(',');
    const body = rows.map((r: any) => keys.map((k) => escapeCsvValue(r[k])).join(',')).join('\r\n');
    const csv = `${header}\r\n${body}`;

    const filename = payload?.data?.filename || `activeWorkspace_${startDate}_to_${endDate}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);

    return true;
  }

  const buf = payload?.data?.buffer;
  if (payload?.success && buf && Array.isArray(buf.data) && buf.data.length > 0) {
    const mime = payload.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = payload.data.filename || `activeWorkspace_${startDate}_to_${endDate}.xlsx`;
    exportBufferToXlsx(buf.data, mime, filename);
    return true;
  }

  return false;
};

const handleActiveWorkspacesExcelExport = async (requestBody: any, headers: any, startDate: any, endDate: any) => {
  const resJson = await axios.post(`${url}${endpoints.activeWorkspacesTable}`, requestBody, { headers });

  if (handleActiveWorkspacesJsonPayload(resJson?.data, startDate, endDate)) {
    return { success: true };
  }

  const resBlob = await axios.post(`${url}${endpoints.activeWorkspacesTable}`, requestBody, {
    headers,
    responseType: 'blob'
  });

  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const contentDisposition = resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'];
  const filename = getActiveWorkspacesBlobFilename(contentDisposition, startDate, endDate);
  const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: resBlob.headers });
    return { success: false, error: 'Empty file returned by server' };
  }
  downloadBlob(blob, filename);

  return { success: true };
};

export const activeWorkspacesTableData = async (params: ActiveWorkspacesTableDataParams) => {
  const {
    startDate,
    endDate,
    selectedDateInString,
    page,
    pageSize,
    filters,
    workspaceName,
    exportType,
    sortField,
    sortOrder
  } = params;
  try {
    const headers = getAuthorazieHeader();

    const requestBody = {
      startDate,
      endDate,
      filters,
      page: page ?? 1,
      pageSize: pageSize ?? 10,
      ...(workspaceName && { workspaceName }),
      ...(selectedDateInString && { dateRange: selectedDateInString }),
      ...(exportType && { exportType: exportType }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    };

    if (exportType?.toLowerCase() === 'excel') {
      return await handleActiveWorkspacesExcelExport(requestBody, headers, startDate, endDate);
    }

    const res = await axios.post(`${url}${endpoints.activeWorkspacesTable}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('API Error:', err);
    return { success: false, error: err };
  }
};

export interface TotalDBUDataParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  page: number;
  pageSize: number;
  filters: Array<{ operator: string; field: string; value: string }>;
  selectedDateInString?: string;
  workspaceName?: string;
}

export const totalDBUData = async (params: TotalDBUDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    page,
    pageSize,
    selectedDateInString,
    workspaceName
  } = params;
  try {
    const headers = getAuthorazieHeader();

    const requestBody = {
      startDate: startDate,
      endDate: endDate,
      env,
      location,
      sector,
      page: page ?? 1,
      pageSize: pageSize ?? 100,
      ...(workspaceName && { workspaceName }),
      ...(selectedDateInString && { dateRange: selectedDateInString })
    };

    console.log('Total DBU Request Body:', requestBody);

    const res = await axios.post(`${url}${endpoints.totalDBU}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('API Error:', err);
    return { success: false, error: err };
  }
};

export interface NewClusterByCategoryAggregateParams {
  startDate: string;
  endDate: string;
  sector?: string;
  env?: string;
  location?: string;
  page?: number;
  pageSize?: number;
  selectedDateInString?: string;
  computeType?: string;
}

export const newClusterByCategoryAggregate = async (params: NewClusterByCategoryAggregateParams) => {
  const {
    startDate,
    endDate,
    sector,
    env,
    location,
    page,
    pageSize,
    selectedDateInString,
    computeType
  } = params;
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    startDate,
    endDate,
    ...(sector && { sector }),
    ...(env && { env }),
    ...(location && { location }),
    ...(page !== undefined && { page }),
    ...(pageSize !== undefined && { pageSize }),
    ...(selectedDateInString && { dateRange: selectedDateInString }),
    ...(computeType && { computeType })
  };

  try {
    const res = await axios.post(`${url}${endpoints.newClusterByCategoryAggregate}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('newClusterByCategoryAggregate error:', err);
    return err;
  }
};

export interface FetchClusterSpinUpsByCategoryAggregateParams {
  startDate: string;
  endDate: string;
  sector?: string;
  env?: string;
  location?: string;
  page?: number;
  pageSize?: number;
  selectedDateInString?: string;
  computeType?: string;
}

export const fetchClusterSpinUpsByCategoryAggregate = async (params: FetchClusterSpinUpsByCategoryAggregateParams) => {
  const {
    startDate,
    endDate,
    sector,
    env,
    location,
    page,
    pageSize,
    selectedDateInString,
    computeType
  } = params;
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    startDate,
    endDate,
    ...(sector && { sector }),
    ...(env && { env }),
    ...(location && { location }),
    ...(page !== undefined && { page }),
    ...(pageSize !== undefined && { pageSize }),
    ...(selectedDateInString && { dateRange: selectedDateInString }),
    ...(computeType && { computeType })
  };

  try {
    const res = await axios.post(`${url}${endpoints.fetchClusterSpinUpsByCategoryAggregate}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('fetchClusterSpinUpsByCategoryAggregate error:', err);
    return err;
  }
};

export interface ClusterByCategoryDataParams {
  startDate: string;
  endDate: string;
  sector?: string;
  env?: string;
  computeType?: string;
  location?: string;
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  exportType?: string;
  workspaceID?: string;
  clusterName?: string;
}

const handleClusterExcelExport = async (
  endpoint: string,
  requestBody: any,
  headers: any,
  startDate: string,
  endDate: string,
  defaultFilenamePrefix: string
) => {
  const res = await axios.post(`${url}${endpoint}`, requestBody, {
    headers,
    responseType: 'arraybuffer'
  });

  const contentType = (res.headers['content-type'] || res.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json')) {
    const text = new TextDecoder('utf-8').decode(res.data as ArrayBuffer);
    try {
      const json = JSON.parse(text);
      const buf = json?.data?.buffer?.data || json?.buffer?.data;
      const filenameFromJson = json?.data?.filename || json?.filename;
      const contentTypeFromJson = json?.data?.contentType || json?.contentType || 'application/octet-stream';
      if (Array.isArray(buf) && buf.length) {
        const uint8 = new Uint8Array(buf);
        const blob = new Blob([uint8], { type: contentTypeFromJson });
        const filename = filenameFromJson || `${defaultFilenamePrefix}_${startDate}_to_${endDate}.xlsx`;
        downloadBlob(blob, filename);
        return { success: true };
      }
      return json;
    } catch (e) {
      console.error('Failed to parse JSON export response', e);
    }
  }

  const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition'] || '';
  let filename = `${defaultFilenamePrefix}_${startDate}_to_${endDate}.xlsx`;
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
    if (filenameMatch) {
      const raw = filenameMatch[1] || filenameMatch[2];
      try {
        filename = decodeURIComponent(raw.replace(/['"]/g, ''));
      } catch {
        filename = raw.replace(/['"]/g, '');
      }
    }
  }

  const blob = new Blob([res.data], { type: contentType || 'application/octet-stream' });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: res.headers });
    return { success: false, error: 'Empty file returned by server' };
  }
  downloadBlob(blob, filename);
  return { success: true };
};

const buildNewClusterRequestBody = (params: ClusterByCategoryDataParams) => {
  const {
    startDate,
    endDate,
    sector,
    env,
    computeType,
    location,
    sortField,
    sortOrder,
    exportType,
    workspaceID,
    clusterName,
    page,
    pageSize
  } = params;
  const requestBody: any = {
    startDate: `${startDate}`,
    endDate: `${endDate}`
  };
  if (sector) requestBody.sector = sector;
  if (env) requestBody.env = env;
  if (computeType) requestBody.computeType = computeType;
  if (location) requestBody.location = location;
  if (sortField) requestBody.sortField = sortField;
  if (sortOrder) requestBody.sortOrder = sortOrder;
  if (exportType) requestBody.exportType = exportType;
  if (workspaceID && workspaceID !== 'All') requestBody.workspaceID = workspaceID;
  if (clusterName && clusterName !== 'All') requestBody.clusterName = clusterName;
  if (page !== undefined) requestBody.page = page;
  if (pageSize !== undefined) requestBody.pageSize = pageSize;
  return requestBody;
};

const buildSpinUpsRequestBody = (params: ClusterByCategoryDataParams) => {
  const {
    startDate,
    endDate,
    sector,
    env,
    computeType,
    location,
    sortField,
    sortOrder,
    exportType,
    workspaceID,
    clusterName,
    page,
    pageSize
  } = params;
  const requestBody: any = {
    startDate: `${startDate}`,
    endDate: `${endDate}`
  };
  if (sector) requestBody.sector = sector;
  if (env) requestBody.env = env;
  if (computeType) requestBody.computeType = computeType;
  if (location) requestBody.location = location;
  if (sortField) requestBody.sortField = sortField;
  if (sortOrder) requestBody.sortOrder = sortOrder;
  if (exportType) requestBody.exportType = exportType;
  if (workspaceID && workspaceID !== 'All') requestBody.workspaceID = workspaceID;
  if (clusterName) requestBody.clusterName = clusterName;
  if (page !== undefined) requestBody.page = page;
  if (pageSize !== undefined) requestBody.pageSize = pageSize;
  return requestBody;
};

export const newClusterByCategoryData = async (params: ClusterByCategoryDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildNewClusterRequestBody(params);

    if (params.exportType?.toLowerCase() === 'excel') {
      return await handleClusterExcelExport(endpoints.newClusterByCategoryData, requestBody, headers, params.startDate, params.endDate, 'new_cluster');
    }

    const res = await axios.post(`${url}${endpoints.newClusterByCategoryData}`, requestBody, { headers });
    return res.data;
  } catch (err: any) {
    console.error('newClusterByCategoryData error:', err?.response || err);
    if (err?.response?.data) return err.response.data;
    return { success: false, error: err?.message || err };
  }
};

export const fetchClusterSpinUpsByCategoryData = async (params: ClusterByCategoryDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildSpinUpsRequestBody(params);

    if (params.exportType?.toLowerCase() === 'excel') {
      return await handleClusterExcelExport(endpoints.fetchClusterSpinUpsByCategoryData, requestBody, headers, params.startDate, params.endDate, 'new_cluster');
    }

    const res = await axios.post(`${url}${endpoints.fetchClusterSpinUpsByCategoryData}`, requestBody, { headers });
    return res.data;
  } catch (err: any) {
    console.error('fetchClusterSpinUpsByCategoryData error:', err?.response || err);
    if (err?.response?.data) return err.response.data;
    return { success: false, error: err?.message || err };
  }
};

export const getWorkspaceOptions = async (
  startDate: string,
  endDate: string,
  env: string,
  sector: string,
  location: string,
  dateRange: string,
) => {
  try {
    const headers = getAuthorazieHeader();

    const requestBody: any = {
      startDate: `${startDate}`,
      endDate: `${endDate}`,
      ...(dateRange && { dateRange }),
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
    };

    const res = await axios.post(`${url}${endpoints.workspaceOptions}`, requestBody, { headers });
    return res.data;
  } catch (error) {
    console.error('Error fetching workspace and compute type options:', error);
    throw error;
  }
};


export const fetchClusterNameBasedOnWorkspace = async (
  workspaceName: string,
) => {
  try {
    const headers = getAuthorazieHeader();

    const requestBody: any = {
      workspaceName
    };

    const res = await axios.post(`${url}${endpoints.fetchClusterNameBasedOnWorkspace}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching cluster names by workspace:', err);
    return err;
  }
};


export interface FetchClusterDetailsParams {
  startDate: string;
  endDate: string;
  sector?: string;
  env?: string;
  computeType?: string;
  location?: string;
  autoTermination?: string | null;
  workspaceID?: string;
  jobName?: string;
  clusterName?: string;
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: string;
  exportType?: string;
  workspaceName?: string;
}

const buildClusterDetailsRequestBody = (params: FetchClusterDetailsParams) => {
  const {
    startDate,
    endDate,
    sector,
    env,
    computeType,
    location,
    autoTermination,
    workspaceID,
    workspaceName,
    jobName,
    clusterName,
    sortField,
    sortOrder,
    exportType,
    page,
    pageSize
  } = params;
  const requestBody: any = {
    startDate: `${startDate}`,
    endDate: `${endDate}`
  };
  if (sector) requestBody.sector = sector;
  if (env) requestBody.env = env;
  if (computeType) requestBody.computeType = computeType;
  if (location) requestBody.location = location;
  if (autoTermination !== undefined) requestBody.autoTermination = autoTermination;
  if (workspaceID !== undefined) requestBody.workspaceID = workspaceID;
  if (workspaceName !== undefined) requestBody.workspaceName = workspaceName;
  if (jobName !== undefined) requestBody.jobName = jobName;
  if (clusterName !== undefined) requestBody.clusterName = clusterName;
  if (sortField) requestBody.sortField = sortField;
  if (sortOrder) requestBody.sortOrder = sortOrder;
  if (exportType) requestBody.exportType = exportType;
  if (page !== undefined) requestBody.page = page;
  if (pageSize !== undefined) requestBody.pageSize = pageSize;
  return requestBody;
};

export const fetchClusterDetails = async (params: FetchClusterDetailsParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildClusterDetailsRequestBody(params);

    if (params.exportType?.toLowerCase() === 'excel') {
      return await handleClusterExcelExport(endpoints.fetchClusterDetails, requestBody, headers, params.startDate, params.endDate, 'clusterWithoutAutoTerm');
    }

    const res = await axios.post(`${url}${endpoints.fetchClusterDetails}`, requestBody, { headers });
    return res.data;
  } catch (err: any) {
    console.error('fetchClusterDetails error:', err?.response || err);
    if (err?.response?.data) return err.response.data;
    return { success: false, error: err?.message || err };
  }
};



export interface FetchDBUDataWithFiltersDataParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  page: number;
  pageSize: number;
  filters: Array<{ operator: string; field: string; value: string }>;
  selectedDateInString?: string;
  workspaceName?: string;
  exportType?: string;
  sortField?: string;
  sortOrder?: string;
}

const handleDbuJsonPayload = (payload: any, startDate: string, endDate: string): boolean => {
  if (!payload) return false;

  const rows = payload?.data?.data;
  if (payload?.success && Array.isArray(rows) && rows.length > 0) {
    const keys = Object.keys(rows[0]);
    const header = keys.join(',');
    const body = rows.map((r: any) => keys.map((k) => escapeCsvValue(r[k])).join(',')).join('\r\n');
    const csv = `${header}\r\n${body}`;

    const filename = payload?.data?.filename || `dbuTable_${startDate}_to_${endDate}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);

    return true;
  }

  const buf = payload?.data?.buffer;
  if (payload?.success && buf && Array.isArray(buf.data) && buf.data.length > 0) {
    const uint8 = new Uint8Array(buf.data);
    const mime = payload.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = payload.data.filename || `dbu_${startDate}_to_${endDate}.xlsx`;
    const blob = new Blob([uint8], { type: mime });
    downloadBlob(blob, filename);
    return true;
  }

  return false;
};

const getDbuBlobFilename = (contentDisposition: string | undefined, startDate: string, endDate: string): string => {
  let filename = `dbu_${startDate}_to_${endDate}.xlsx`;
  if (contentDisposition) {
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    if (fileNameMatch) {
      const rawName = fileNameMatch[1] || fileNameMatch[2];
      try {
        filename = decodeURIComponent(rawName.replace(/['"]/g, ''));
      } catch {
        filename = rawName.replace(/['"]/g, '');
      }
    }
  }
  return filename;
};

const handleDbuExcelExport = async (requestBody: any, headers: any, startDate: string, endDate: string) => {
  const resJson = await axios.post(`${url}${endpoints.fetchDBUDataWithFilters}`, requestBody, { headers });

  if (handleDbuJsonPayload(resJson?.data, startDate, endDate)) {
    return { success: true };
  }

  const resBlob = await axios.post(`${url}${endpoints.fetchDBUDataWithFilters}`, requestBody, {
    headers,
    responseType: 'blob'
  });

  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const contentDisposition = resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'];
  const filename = getDbuBlobFilename(contentDisposition, startDate, endDate);
   const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: resBlob.headers });
    return { success: false, error: 'Empty file returned by server' };
  }
  downloadBlob(blob, filename);

  return { success: true };
};

export const fetchDBUDataWithFiltersData = async (params: FetchDBUDataWithFiltersDataParams) => {
  const {
    startDate,
    endDate,
    page,
    pageSize,
    filters,
    selectedDateInString,
    workspaceName,
    exportType,
    sortField,
    sortOrder
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      startDate: startDate,
      endDate: endDate,
      filters: filters,
      page: page ?? 1,
      pageSize: pageSize ?? 100,
      ...(workspaceName && { workspaceName }),
      ...(selectedDateInString && { dateRange: selectedDateInString }),
      ...(exportType && { exportType: exportType }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    };

    if (exportType?.toLowerCase() === 'excel') {
      return await handleDbuExcelExport(requestBody, headers, startDate, endDate);
    }

    const res = await axios.post(`${url}${endpoints.fetchDBUDataWithFilters}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      console.error('Request config:', err.config);
      console.error('Response data:', err.response?.data);
    }
    return { success: false, error: err };
  }
};

export const getWorkspaceAndComputeTypeOptions = async (
  startDate: string,
  endDate: string,
  env: string,
  sector: string,
  location: string,
  dateRange: string,
) => {
  try {
    const headers = getAuthorazieHeader();

    const response = await axios.post(`${url}${endpoints.DBUFilterOptions}`, {
      headers,
      // params: {
      // 	startDate,
      // 	endDate,
      // 	env,
      // 	sector,
      // 	location,
      // 	dateRange
      // }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching workspace and compute type options:', error);
    throw error;
  }
};

export interface DetailedComputeCostByTypeDataParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  computeType: string;
  page: number;
  pageSize: number;
  filters: Array<{ operator: string; field: string; value: string }>;
  selectedDateInString?: string;
  workspaceName?: string;
}

export const detailedComputeCostByTypeData = async (params: DetailedComputeCostByTypeDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    computeType,
    page,
    pageSize,
    selectedDateInString,
    workspaceName
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody: any = {
      startDate: startDate,
      endDate: endDate,
      page: page ?? 1,
      pageSize: pageSize ?? 10
    };
    if (env && env !== 'both') {
      requestBody.env = env;
    }
    if (sector && sector !== 'all' && sector !== 'All' && sector !== '') {
      requestBody.sector = sector;
    }
    if (location && location !== 'ALL' && location !== '') {
      requestBody.location = location;
    }
    if (selectedDateInString) {
      requestBody.dateRange = selectedDateInString;
    }
    if (workspaceName) {
      requestBody.workspaceName = workspaceName;
    }
    if (computeType) {
      requestBody.computeType = computeType;
    }
    const res = await axios.post(`${url}${endpoints.detailedComputeCostByType}`, requestBody, { headers });

    return res.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      console.error(err.response?.data);
    }
    return { success: false, error: err };
  }
};

export interface DetailedComputeCountByTypeDataParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  computeType: string;
  page: number;
  pageSize: number;
  filters: Array<{ operator: string; field: string; value: string }>;
  selectedDateInString?: string;
  workspaceName?: string;
}

export const detailedComputeCountByTypeData = async (params: DetailedComputeCountByTypeDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    computeType,
    page,
    pageSize,
    selectedDateInString,
    workspaceName
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody: any = {
      startDate: startDate,
      endDate: endDate,
      page: page ?? 1,
      pageSize: pageSize ?? 10
    };
    if (env && env !== 'both') {
      requestBody.env = env;
    }
    if (sector && sector !== 'all' && sector !== 'All' && sector !== '') {
      requestBody.sector = sector;
    }
    if (location && location !== 'ALL' && location !== '') {
      requestBody.location = location;
    }
    if (selectedDateInString) {
      requestBody.dateRange = selectedDateInString;
    }
    if (workspaceName) {
      requestBody.workspaceName = workspaceName;
    }
    if (computeType) {
      requestBody.computeType = computeType;
    }
    const res = await axios.post(`${url}${endpoints.detailedComputeCountByType}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      console.error(err.response?.data);
    }
    return { success: false, error: err };
  }
};

export interface GetComputeDetailsDataParams {
  startDate: string;
  endDate: string;
  env: string;
  sector: string;
  location: string;
  page: number;
  pageSize: number;
  filters: Array<{ operator: string; field: string; value: string }>;
  selectedDateInString?: string;
  workspaceName?: string;
  exportType?: string;
  sortField?: string;
  sortOrder?: string;
}

const handleComputeDetailsJsonPayload = (payload: any, startDate: string, endDate: string): boolean => {
  if (!payload) return false;

  const rows = payload?.data?.data;
  if (payload?.success && Array.isArray(rows) && rows.length > 0) {
    const keys = Object.keys(rows[0]);
    const header = keys.join(',');
    const body = rows.map((r: any) => keys.map((k) => escapeCsvValue(r[k])).join(',')).join('\r\n');
    const csv = `${header}\r\n${body}`;

    const filename = payload?.data?.filename || `costTable${startDate}_to_${endDate}.csv`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(blob, filename);
    return true;
  }

  const buf = payload?.data?.buffer;
  if (payload?.success && buf && Array.isArray(buf.data) && buf.data.length > 0) {
    const uint8 = new Uint8Array(buf.data);
    const mime = payload.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = payload.data.filename || `costTable_${startDate}_to_${endDate}.xlsx`;
    const blob = new Blob([uint8], { type: mime });
    downloadBlob(blob, filename);
    return true;
  }

  return false;
};

const getComputeDetailsBlobFilename = (contentDisposition: string | undefined, startDate: string, endDate: string): string => {
  let filename = `costTable_${startDate}_to_${endDate}.xlsx`;
  if (contentDisposition) {
    const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
    if (fileNameMatch) {
      const rawName = fileNameMatch[1] || fileNameMatch[2];
      try {
        filename = decodeURIComponent(rawName.replace(/['"]/g, ''));
      } catch {
        filename = rawName.replace(/['"]/g, '');
      }
    }
  }
  return filename;
};

const handleComputeDetailsExcelExport = async (requestBody: any, headers: any, startDate: string, endDate: string) => {
  const resJson = await axios.post(`${url}${endpoints.detailedComputeDetailsData}`, requestBody, { headers });

  if (handleComputeDetailsJsonPayload(resJson?.data, startDate, endDate)) {
    return { success: true };
  }

  const resBlob = await axios.post(`${url}${endpoints.detailedComputeDetailsData}`, requestBody, {
    headers,
    responseType: 'blob'
  });

  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const contentDisposition = resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'];
  const filename = getComputeDetailsBlobFilename(contentDisposition, startDate, endDate);
  const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: resBlob.headers });
    return { success: false, error: 'Empty file returned by server' };
  }
  downloadBlob(blob, filename);

  return { success: true };
};

export const getComputeDetailsData = async (params: GetComputeDetailsDataParams) => {
  const {
    startDate,
    endDate,
    page,
    pageSize,
    filters,
    selectedDateInString,
    workspaceName,
    exportType,
    sortField,
    sortOrder
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      startDate: startDate,
      endDate: endDate,
      filters: filters,
      page: page ?? 1,
      pageSize: pageSize ?? 100,
      ...(workspaceName && { workspaceName }),
      ...(selectedDateInString && { dateRange: selectedDateInString }),
      ...(exportType && { exportType: exportType }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder })
    };

    if (exportType?.toLowerCase() === 'excel') {
      return await handleComputeDetailsExcelExport(requestBody, headers, startDate, endDate);
    }
    const res = await axios.post(`${url}${endpoints.detailedComputeDetailsData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    if (axios.isAxiosError(err)) {
      console.error(err.config?.url);
      console.error(err.config?.data);
      console.error(err.response?.status);
      console.error(err.response?.data);
    }
    return { success: false, error: err };
  }
};

// ADB_Job_Analytics
export const jobLastSyncData = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.jobLastSync}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const statusOverviewData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.statusOverview}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const failureReasonData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.failureReason}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export interface ExpensiveJobDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: string;
  exportType?: string;
}

export const expensiveFailureData = async (params: ExpensiveJobDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page = 1,
    limit = 100,
    sortField,
    sortOrder,
    exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody: any = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder }),
      ...(exportType !== undefined && { exportType })
    };
    if (exportType?.toLowerCase() === 'excel') {
      const filename = `expensiveFailure_${startDate}_to_${endDate}.xlsx`;
      return await handleExportResponse(`${url}${endpoints.expensiveFailure}`, requestBody, headers, filename);
    }

    const res = await axios.post(`${url}${endpoints.expensiveFailure}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
};

export const expensiveSuccessData = async (params: ExpensiveJobDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page = 1,
    limit = 100,
    sortField,
    sortOrder,
    exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody: any = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      ...(sortField && { sortField }),
      ...(sortOrder && { sortOrder }),
      ...(exportType !== undefined && { exportType })
    };
    if (exportType?.toLowerCase() === 'excel') {
      const filename = `expensiveSuccess_${startDate}_to_${endDate}.xlsx`;
      return await handleExportResponse(`${url}${endpoints.expensiveSuccess}`, requestBody, headers, filename);
    }

    const res = await axios.post(`${url}${endpoints.expensiveSuccess}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
};

export const expensiveFailureCostData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.expensiveFailureCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}
export interface GetTotalFailedJobCostParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
}

export const getTotalFailedJobCost = async (params: GetTotalFailedJobCostParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.totalFailedJobCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
};
export interface GetonlyWorkspacesParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  exportPage: number;
  exportPageSize: number;
  filters: any[];
  selectedDateInString: string;
  page?: number;
  limit?: number;
}

export const getonlyWorkspaces = async (params: GetonlyWorkspacesParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit
  } = params;
  try {
    const headers = getAuthorazieHeader();

    // Build request body dynamically
    const requestBody: any = {
      startDate: `${startDate}`,
      endDate: `${endDate}`
    };

    // Add dateRange if provided
    if (selectedDateInString) {
      requestBody.dateRange = selectedDateInString;
    }

    // Add env only if it's not 'both'
    if (env && env !== 'both') {
      requestBody.env = env;
    }

    // Add sector only if it's not 'all'
    if (sector && sector !== 'all') {
      requestBody.sector = sector;
    }

    // Add location only if it's not 'ALL'
    if (location && location !== 'ALL') {
      requestBody.location = location;
    }

    // Add pagination parameters if provided
    if (page !== undefined) {
      requestBody.page = page;
    }

    if (limit !== undefined) {
      requestBody.limit = limit;
    }
    const res = await axios.post(`${url}${endpoints.onlyWorkspaces}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return err;
  }
};
export interface DatabricksCommonServiceParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: string;
  filters?: Array<{ operator: string; field: string; value: string }>;
  exportType?: string;
}

export type GetTotalFailedJobCostDetailsParams = DatabricksCommonServiceParams;
export type GetStatusTreeDataParams = DatabricksCommonServiceParams;
export type GetStatusDetailedDataParams = DatabricksCommonServiceParams;

const buildDatabricksCommonRequestBody = (params: DatabricksCommonServiceParams) => {
  const requestBody: any = {
    startDate: `${params.startDate}`,
    endDate: `${params.endDate}`,
  };

  if (params.exportType) requestBody.exportType = params.exportType;
  if (params.selectedDateInString) requestBody.dateRange = params.selectedDateInString;
  if (params.env && params.env !== 'both') requestBody.env = params.env;
  if (params.sector && params.sector !== 'all') requestBody.sector = params.sector;
  if (params.location && params.location !== 'ALL') requestBody.location = params.location;
  if (params.page !== undefined) requestBody.page = params.page;
  if (params.limit !== undefined) requestBody.limit = params.limit;
  if (params.sortField) requestBody.sortField = params.sortField;
  if (params.sortOrder) requestBody.sortOrder = params.sortOrder;
  if (params.filters?.length) requestBody.filters = params.filters;

  return requestBody;
};

export const getTotalFailedJobCostDetails = async (params: GetTotalFailedJobCostDetailsParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    console.log('📤 getTotalFailedJobCostDetails Request:', requestBody);

    // Export requested -> call backend and handle whichever shape it returns
    if (params.exportType?.toLowerCase() === 'excel') {
      const filename = `failedJobsCost_${params.startDate}_to_${params.endDate}.xlsx`;
      return await handleExportResponse(`${url}${endpoints.totalFailedJobCostDetailedData}`, requestBody, headers, filename);
    }

    const res = await axios.post(`${url}${endpoints.totalFailedJobCostDetailedData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return err;
  }
};

export const getStatusTreeData = async (params: GetStatusTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.statusTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};

const tryGenericJsonExport = async (
  endpointUrl: string,
  requestBody: any,
  headers: any,
  getDefaultFilename: (ext: string) => string
): Promise<boolean> => {
  const resJson = await axios.post(endpointUrl, requestBody, { headers });
  const payload = resJson?.data;
  if (!payload?.success) return false;

  const rows = payload.data?.data || payload.data?.docs;
  if (Array.isArray(rows) && rows.length > 0) {
    const filename = payload.data?.filename || getDefaultFilename('csv');
    exportJsonRowsToCsv(rows, filename);
    return true;
  }

  const buf = payload.data?.buffer;
  if (buf && Array.isArray(buf.data) && buf.data.length > 0) {
    const mime = payload.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = payload.data.filename || getDefaultFilename('xlsx');
    exportBufferToXlsx(buf.data, mime, filename);
    return true;
  }

  return false;
};

const parseFilenameFromDisposition = (contentDisposition: string | undefined, defaultFilename: string): string => {
  if (!contentDisposition) return defaultFilename;
  const fileNameMatch = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(contentDisposition);
  if (!fileNameMatch) return defaultFilename;
  const rawName = fileNameMatch[1] || fileNameMatch[2];
  try {
    return decodeURIComponent(rawName.replace(/['"]/g, ''));
  } catch {
    return rawName.replace(/['"]/g, '');
  }
};

const tryGenericBlobExport = async (
  endpointUrl: string,
  requestBody: any,
  headers: any,
  getDefaultFilename: (ext: string) => string
) => {
  const resBlob = await axios.post(endpointUrl, requestBody, {
    headers,
    responseType: 'blob'
  });

  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const filename = parseFilenameFromDisposition(
    resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'],
    getDefaultFilename('xlsx')
  );

  const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  if (blob.size === 0) {
    console.error('Received empty blob from export endpoint', { requestBody, headers: resBlob.headers });
    return { success: false, error: 'Empty file returned by server' };
  }

  downloadBlob(blob, filename);
  return { success: true };
};

const handleGenericDetailedExport = async (
  endpointUrl: string,
  requestBody: any,
  headers: any,
  getDefaultFilename: (ext: string) => string
) => {
  const handledByJson = await tryGenericJsonExport(endpointUrl, requestBody, headers, getDefaultFilename);
  if (handledByJson) {
    return { success: true };
  }
  return await tryGenericBlobExport(endpointUrl, requestBody, headers, getDefaultFilename);
};

export const getStatusDetailedData = async (params: GetStatusDetailedDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    if (params.exportType?.toLowerCase() === 'excel') {
      return await handleGenericDetailedExport(
        `${url}${endpoints.statusDetailedData}`,
        requestBody,
        headers,
        (ext) => `jobStatus_${params.startDate}_to_${params.endDate}.${ext}`
      );
    }

    const res = await axios.post(`${url}${endpoints.statusDetailedData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};

export type GetFailureReasonsTreeDataParams = DatabricksCommonServiceParams;

export const failureReasonsTreeData = async (params: GetFailureReasonsTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    console.log('📤 failureReasonsTreeData Request:', requestBody);

    const res = await axios.post(`${url}${endpoints.failureReasonsTreeData}`, requestBody, { headers });

    console.log('📦 failureReasonsTreeData Response:', res.data);

    return res.data;
  } catch (err) {
    console.error('❌ failureReasonsTreeData Error:', err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};

export type GetFailureReasonsDetailedDataParams = DatabricksCommonServiceParams;

export const failureReasonsDetailedData = async (params: GetFailureReasonsDetailedDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    console.log('📤 failureReasonsTreeData Request:', requestBody);

    if (params.exportType?.toLowerCase() === 'excel') {
      return await handleGenericDetailedExport(
        `${url}${endpoints.failureReasonsDetailedData}`,
        requestBody,
        headers,
        (ext) => `Failure_Reasons_Details_${moment().format('YYYY-MM-DD_HH-mm-ss')}.${ext}`
      );
    }

    const res = await axios.post(`${url}${endpoints.failureReasonsDetailedData}`, requestBody, { headers });

    console.log('📦 failureReasonsTreeData Response:', res.data);

    return res.data;
  } catch (err) {
    console.error('❌ failureReasonsTreeData Error:', err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetPeakHourDetailedDataParams = DatabricksCommonServiceParams;

export const getPeakHourDetailedData = async (params: GetPeakHourDetailedDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    if (params.exportType?.toLowerCase() === 'excel') {
      return await handleGenericDetailedExport(
        `${url}${endpoints.peakHourDetailedData}`,
        requestBody,
        headers,
        (ext) => `Peak_Hour_Details_${moment().format('YYYY-MM-DD_HH-mm-ss')}.${ext}`
      );
    }

    const res = await axios.post(`${url}${endpoints.peakHourDetailedData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
//ADB Job_Analysis - Peak_Hours

export type GetPeakHourTreeDataParams = DatabricksCommonServiceParams;

export const getPeakHourTreeData = async (params: GetPeakHourTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.peakHourTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const peakHourData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.peakHour}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const activeWorkspaceData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.activeWorkspace}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const hourByJobCountData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.hourByJobCount}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topWorkspacesData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topWorkspaces}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

//ADB_DBUSQl_Dashboard
export type GetFailedQueryCostTreeDataParams = DatabricksCommonServiceParams;

export const getFailedQueryCostTreeData = async (params: GetFailedQueryCostTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.failedQueryCostTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetCatalogCostTreeDataParams = DatabricksCommonServiceParams;

export const getCatalogCostTreeData = async (params: GetCatalogCostTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.catalogCostTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetCatalogCountTreeDataParams = DatabricksCommonServiceParams;

export const getCatalogCountTreeData = async (params: GetCatalogCountTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.catalogCountTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetCostGroupTreeDataParams = DatabricksCommonServiceParams;

export const getCostGroupTreeData = async (params: GetCostGroupTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.costGroupTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetDbuGroupTreeDataParams = DatabricksCommonServiceParams;

export const getDbuGroupTreeData = async (params: GetDbuGroupTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.dbuGroupTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetDbuByWarehouseTreeDataParams = DatabricksCommonServiceParams;

export const getDbuByWarehouseTreeData = async (params: GetDbuByWarehouseTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.dbuByWarehouseTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetQueryTrendTreeDataParams = DatabricksCommonServiceParams;

export const getQueryTrendTreeData = async (params: GetQueryTrendTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.queryTrendTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetNewCountByCategoryTreeDataParams = DatabricksCommonServiceParams;

export const getNewCountByCategoryTreeData = async (params: GetNewCountByCategoryTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.newCountByCategoryTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export type GetCostByCategoryTreeDataParams = DatabricksCommonServiceParams;

export const getCostByCategoryTreeData = async (params: GetCostByCategoryTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.costByCategoryTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
const tryJsonExportResponse = async (urlPath: string, payload: any, headers: any, defaultFilename: string): Promise<boolean> => {
  const resJson = await axios.post(urlPath, payload, { headers });
  if (!resJson?.data) return false;

  const respData = resJson.data;
  if (!respData?.success) return false;

  const rows = respData.data?.data || respData.data?.docs || (Array.isArray(respData.data) ? respData.data : null);
  if (Array.isArray(rows) && rows.length > 0) {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const filename = respData.data?.filename || defaultFilename;
    downloadBlob(new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
    return true;
  }

  const buf = respData.data?.buffer;
  if (buf && Array.isArray(buf.data) && buf.data.length > 0) {
    const mime = respData.data.contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    const filename = respData.data.filename || defaultFilename;
    exportBufferToXlsx(buf.data, mime, filename);
    return true;
  }

  return false;
};

const tryBlobExportResponse = async (urlPath: string, payload: any, headers: any, defaultFilename: string) => {
  const resBlob = await axios.post(urlPath, payload, { headers, responseType: 'blob' });
  const contentType = (resBlob.headers['content-type'] || resBlob.headers['Content-Type'] || '').toLowerCase();

  if (contentType.includes('application/json') || contentType.includes('text/')) {
    try {
      const text = await resBlob.data.text();
      const parsed = JSON.parse(text);
      console.error('Export failed: server returned JSON instead of file', parsed);
      return { success: false, error: parsed };
    } catch (e) {
      const txt = await resBlob.data.text();
      console.error('Export failed, server returned text:', txt, e);
      return { success: false, error: txt };
    }
  }

  const filename = parseFilenameFromDisposition(
    resBlob.headers['content-disposition'] || resBlob.headers['Content-Disposition'],
    defaultFilename
  );

  const mime = contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  const blob = new Blob([resBlob.data], { type: mime });
  downloadBlob(blob, filename);

  return { success: true };
};

export const handleExportResponse = async (urlPath: string, payload: any, headers: any, defaultFilename: string) => {
  const handledByJson = await tryJsonExportResponse(urlPath, payload, headers, defaultFilename);
  if (handledByJson) {
    return { success: true };
  }
  return await tryBlobExportResponse(urlPath, payload, headers, defaultFilename);
};

export const getPeakQueryDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.peakQueryDetailedData}`, payload, headers, `Peak_Concurrency_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.peakQueryDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};

export const getFailedQueryDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.failedQueryDetailedData}`, payload, headers, `Failed_Queries_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.failedQueryDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const getCatalogDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.catalogDetailedData}`, payload, headers, `Catalog_Details_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.catalogDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const getDbuGroupDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.dbuGroupDetailedData}`, payload, headers, `DBU_Group_Details_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.dbuGroupDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const getQueryTrendDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.queryTrendDetailedData}`, payload, headers, `Query_Trend_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.queryTrendDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const getCostByCategoryDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.costByCategoryDetailedData}`, payload, headers, `Cost_By_Category_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.costByCategoryDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const getNewCountByCategoryDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.newCountByCategoryDetailedData}`, payload, headers, `Warehouse_Details_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.newCountByCategoryDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const getCountByCategoryDetailedData = async (payload: any) => {
  try {
    const headers = getAuthorazieHeader();
    if (payload?.exportType === 'excel') {
      return await handleExportResponse(`${url}${endpoints.countByCategoryDetailedData}`, payload, headers, `Count_By_Category_${moment().format('YYYY-MM-DD')}.xlsx`);
    }
    const res = await axios.post(`${url}${endpoints.countByCategoryDetailedData}`, payload, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};

export type GetCountByCategoryTreeDataParams = DatabricksCommonServiceParams;

export const getCountByCategoryTreeData = async (params: GetCountByCategoryTreeDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.countByCategoryTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error(err);
    return {
      success: false,
      statusCode: 500,
      error: err
    };
  }
};
export const sqllastFetchData = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.sqllastFetch}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const totalSqlData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.totalSql}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const totalCostData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.totalCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const newWarehouseData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.newWarehouse}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const costTrendData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.costTrend}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topDBUData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topDBU}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export type GetLonRunDataParams = DatabricksCommonServiceParams;

export const lonRunData = async (params: GetLonRunDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    if (params.exportType?.toLowerCase() === 'excel') {
      const filename = `longRunningQueries_${params.startDate}_to_${params.endDate}.xlsx`;
      return await handleExportResponse(`${url}${endpoints.lonRun}`, requestBody, headers, filename);
    }
    const res = await axios.post(`${url}${endpoints.lonRun}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const failedMetricsData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.failedMetrics}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const peakCountData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.peakCount}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topCostData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}


export const fetchHappySignals = async (selectedDate: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dateFilter: selectedDate
  };
  try {
    const res = await axios.post(`${url}${endpoints.happySignals}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const queryTrendData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.queryTrend}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topGroupDbuData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topGroupDbu}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topGroupCostData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topGroupCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topUsageCatalogsData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topUsageCatalogs}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const topUsageCost = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.topUsageCatlgsCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

//Custom_Analysis_Dashboard

export const customlastFetchData = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.customlastFetch}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export interface FetchStableDBRDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  exportType?: string;
}

export const fetchEodSupportData = async (params: FetchStableDBRDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit,
    exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      ...(exportType !== undefined && { exportType })
    };
    const res = await axios.post(`${url}${endpoints.eodSupport}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
};

export const fetchStableDBRData = async (params: FetchStableDBRDataParams) => {
  const {
    startDate,
    endDate,
    env,
    sector,
    location,
    selectedDateInString,
    page,
    limit,
    exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(limit !== undefined && { limit }),
      ...(exportType !== undefined && { exportType })
    };
    const res = await axios.post(`${url}${endpoints.stableDBR}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
};

export const fetchStarterWarehousesData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.starterWarehouses}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export type GetStarterWarehouseListParams = DatabricksCommonServiceParams;

export const fetchStarterWarehouseList = async (params: GetStarterWarehouseListParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.starterWarehouseList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const maxAutoTermData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.maxAutoTerm}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export type GetAutoTermTableDataParams = DatabricksCommonServiceParams;

export const autoTermTableData = async (params: GetAutoTermTableDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.autoTermDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const longRunData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.longRun}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const longRunDetailsData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.longRunDetails}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export type GetLongRunTableDataParams = DatabricksCommonServiceParams;

export const longRunTableData = async (params: GetLongRunTableDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.longRunDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const overProvisionData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.overProvision}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const untagggedCostData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.untagggedCost}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export const dbrVersionData = async (startDate: any, endDate: any, env: string, sector: string, location: string, selectedDateInString: string, exportType?: string) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(exportType !== undefined && { exportType })
      // "env": "non-prod"
    };
    const res = await axios.post(`${url}${endpoints.dbrVersion}`, requestBody, { headers });
    return res.data;
  }
  catch (err) {
    return err;
  }
}

export type GetCustomConfigDataParams = DatabricksCommonServiceParams;

export const customConfigData = async (params: GetCustomConfigDataParams) => {
  try {
    const headers = getAuthorazieHeader();
    const requestBody = buildDatabricksCommonRequestBody(params);

    const res = await axios.post(`${url}${endpoints.customConfigList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export interface GetOverProvisionListDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  type?: string;
  sortField?: string;
  sortOrder?: string;
  exportType?: string;
}

export const overProvisionListData = async (params: GetOverProvisionListDataParams) => {
  const {
    startDate, endDate, env, sector, location, selectedDateInString,
    page, limit, type, sortField, sortOrder, exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(sortField !== undefined && { sortField }),
      ...(sortOrder !== undefined && { sortOrder }),
      ...(limit !== undefined && { limit }),
      ...(type && { type }),
      ...(exportType !== undefined && { exportType })
    };
    const res = await axios.post(`${url}${endpoints.overProvisionList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export interface GetUntaggedListDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  type?: string;
  sortField?: string;
  sortOrder?: string;
  exportType?: string;
}

export const untaggedListData = async (params: GetUntaggedListDataParams) => {
  const {
    startDate, endDate, env, sector, location, selectedDateInString,
    page, limit, type, sortField, sortOrder, exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(sortField !== undefined && { sortField }),
      ...(sortOrder !== undefined && { sortOrder }),
      ...(limit !== undefined && { limit }),
      ...(type && { type }),
      ...(exportType !== undefined && { exportType })
    };
    const res = await axios.post(`${url}${endpoints.untaggedList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export interface GetDbrVersionListDataParams {
  startDate: any;
  endDate: any;
  env: string;
  sector: string;
  location: string;
  selectedDateInString: string;
  page?: number;
  limit?: number;
  version?: string;
  computeType?: string;
  sortField?: string;
  sortOrder?: string;
  exportType?: string;
}

export const dbrVersionListData = async (params: GetDbrVersionListDataParams) => {
  const {
    startDate, endDate, env, sector, location, selectedDateInString,
    page, limit, version, computeType, sortField, sortOrder, exportType
  } = params;
  try {
    const headers = getAuthorazieHeader();
    const requestBody = {
      "startDate": `${startDate}`,
      "endDate": `${endDate}`,
      "dateRange": selectedDateInString,
      ...(env !== 'both' && { env }),
      ...(sector !== 'all' && { sector }),
      ...(location !== 'ALL' && { location }),
      ...(page !== undefined && { page }),
      ...(sortField !== undefined && { sortField }),
      ...(sortOrder !== undefined && { sortOrder }),
      ...(limit !== undefined && { limit }),
      ...(version && { version }),
      ...(computeType && { compute_type: computeType }),
      ...(exportType !== undefined && { exportType })
    };
    const res = await axios.post(`${url}${endpoints.dbrVersionList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalOverview = async (selectedDate: any, platform: any, technology: any, sector: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dateFilter: selectedDate,
    platform: platform,
    technology: technology,
    sector: sector
  };

  try {
    const res = await axios.post(`${url}${endpoints.happySignalsOverview}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalOverviewTrend = async (platform: any, technology: any, sector: any, filterMonth: any, businessUnitName: any = ['all'], departmentName: any = ['all'], capabilityName: any = ['all']) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platform: platform,
    technology: technology,
    sector: sector,
    businessUnitName: businessUnitName.includes('all') ? null : businessUnitName || null,
    departmentName: departmentName.includes('all') ? null : departmentName || null,
    capabilityName: capabilityName.includes('all') ? null : capabilityName || null,
    dateFilter: filterMonth
  };

  try {
    const res = await axios.post(`${url}${endpoints.happySignalsOverviewTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalBySector = async (filterMonth: string) => {
  const headers = getAuthorazieHeader();
  try {
    const requestBody = {
      dateFilter: filterMonth
    };
    const res = await axios.post(`${url}${endpoints.happySignalsSectors}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalByTechnology = async (filterMonth: string) => {
  const headers = getAuthorazieHeader();
  try {
    const requestBody = {
      dateFilter: filterMonth
    };
    const res = await axios.post(`${url}${endpoints.happySignalsTechnology}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalTrend = async () => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dateFilter: ""
  };
  try {
    const res = await axios.post(`${url}${endpoints.happySignalsTrends}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalCapabilityList = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.happySignalsCapabilityList}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchPowerBIExportReportActivityDetails = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.powerBIReportActivityDetailsExport}`, requestBody, { headers, responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `PowerBI_Reports_Activity_Details.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    throw err;
  }
};

export const fetchPowerBIReportsKpi = async (filtersParam: any) => {
  console.log(filtersParam, "filters param")
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIReportsKPI}`, filtersParam, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchPowerBIReportDatasetDetails = async (filtersParam: any) => {
  console.log(filtersParam, "filters param")
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIReportsDetails}`, filtersParam, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchReportGrowthData = async (filtersParam: any) => {
  console.log(filtersParam, "filters param")
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIReportsGrowth}`, filtersParam, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchReportUsageData = async (filtersParam: any) => {
  console.log(filtersParam, "filters param")
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.powerBIReportsUsage}`, filtersParam, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchPowerBICapacityKpi = async (filtersParam: any) => {
  console.log(filtersParam, "filters param")
  try {
    return (await apiPost(`${url}${endpoints.powerBICapacityKPI}`, filtersParam)).data
  } catch (err) {
    return err;
  }
};

export const fetchPowerBICapacityGrowthData = async (filtersParam: any) => {
  console.log(filtersParam, "capacity growth filters param")
  try {
    return (await apiPost(`${url}${endpoints.powerBICapacityGrowthData}`, filtersParam)).data
  } catch (err) {
    return err;
  }
};

export const fetchPowerBICapacityTableData = async (filtersParam: any) => {
  console.log(filtersParam, "capacity table filters param")
  try {
    return (await apiPost(`${url}${endpoints.powerBICapacityData}`, filtersParam)).data
  } catch (err) {
    return err;
  }
};

export const fetchPowerBIExportCapacityDetails = async (requestBody: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.powerBICapacityReportData}`, requestBody, { responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `PowerBI_Reports_Capacity_Details.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    return err;
  }
};

export const fetchCapacityNames = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.powerBICapacityList}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchResourceCostSummaryKpi = async ({
  page,
  size,
  totalPages = 0,
  sortingInfo,
  sortBy = '',
  orderBy = '',
  searchField = '',
  searchText = '',
  searchQuery = '',
  sql,
  sqlParams,
  viewType = '',
  dateFilter = '',
  sectorFilter = '',
  isDetailView = false,
  detailFilter = '',
  signal
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    page,
    size,
    totalPages,
    sortingInfo,
    sortBy,
    orderBy,
    searchField,
    searchQuery,
    searchText,
    sql,
    sqlParams,
    viewType,
    dateFilter,
    sectorFilter,
    isDetailView,
    detailFilter,
  };
  try {
    const res = await axios.post(`${url}${endpoints.resourceCostSummaryKpi}`, requestBody, { headers, signal });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProblemTicketsMetricTrend = async (sector: string, isClosedDate: any, filterMonth: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: sector,
    isClosedDate: isClosedDate,
    dateFilter: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.problemTicketsMetricTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProblemTecketsMetrics = async ({
  selectedMonth, sector, isClosedDate
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    dateFilter: selectedMonth,
    sector: sector,
    isClosedDate: isClosedDate
  };
  try {
    const res = await axios.post(`${url}${endpoints.problemTicketsMetric}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchHappySignalPlatformList = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.happySignalsPlatformList}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const fetchHappySignalTechnologyList = async (platform: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    platform: platform
  };
  try {

    const res = await axios.post(`${url}${endpoints.happySignalsTechnologyList}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getRuleTypeOptions = async () => {
  let headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.alertFrameworkConfig}/ruleTypeOptions`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
}

export const fetchTableauSummary = async (sector: any, month: any, startDate?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: sector,
    month: month,
    startDate: startDate || null,
    dateFilter: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.tableauSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportUserActivityDetails = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.powerBiUserActivityDetailsExport}`, requestBody, { headers, responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `PowerBI_User_Activity_Details.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    throw err;
  }
};

export const fetchP3IncidentsSummary = async (selectedDate: any, isClosedDate: any, timezoneFilter: string, type?: string, area?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.p3IncidentsSummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportCostSummaryTableData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.resourceCostSummaryExport}`, requestBody, { headers, responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `Resource-Cost-Summary.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error("Error exporting table data:", err);
    throw err;
  }
};


export const fetchSummaryIncidentTrend = async ({
  isClosedDate,
  timezoneFilter,
  sector,
  type,
  area,
  filterMonth
}: any) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    isClosedDate,
    timezoneFilter,
    sector,
    dateFilter: filterMonth
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.getSummaryMetricTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchAllIncidentsSummaryTrend = async (selectedDate: any, isClosedDate: any, timezoneFilter: string, sector?: string, type?: string, area?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector
  };

  // Add new filters using type/area format
  if (type && area) {
    requestBody.metricsType = type;
    requestBody.technology = area;
  }

  try {
    const res = await axios.post(`${url}${endpoints.getAllIncidentsTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const addNewPipelineParams = async (data: any) => {
  const headers = getAuthorazieHeader();

  try {
    const host = getIsV2() ? urlV2 : url;
    const res = await axios.post(`${host}${endpoints.addNewPipelineParams}`, data, { headers });
    return res.data;
  } catch (err) {
    console.error("addNewPipelineParams error:", err);
    throw err;
  }
};

//**Configure Pipeline End */
export const fetchTopSpendingTreeList = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.fetchTopSpendingTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchRunTimeTreeList = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.fetchRunTimeTreeData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDbuTreeList = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.totalDBU}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchSectorList = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.sectorLookup}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchAggregateClusterDataByDate = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.fetchAggregateClusterDataByDate}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchUserActivityTableauTableData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}/tableau/userActivity/tableData`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching User Activity Table data:', err);
    throw err;
  }
};

export const deleteDpExecutiveDashboard = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.deleteExecutiveProgram}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchExecutiveDashboardData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.fetchExecutiveProgramProjects}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

async function apiPost(endpoint: string, body: any = {}, options: any = {}) {
  try {
    const headers = {
      ...getAuthorazieHeader(),
      ...options.headers
    };

    const response = await axios.post(endpoint, body, {
      ...options,
      headers
    });

    return response;

  } catch (err) {
    console.error("API POST Error:", err);
    throw err;
  }
}

export const fetchTableauTotalLicenses = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauTotalLicenses}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Total Licenses data:', err);
    throw err;
  }
};

export const fetchTableauActivityTrendChart = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauLicenseTrendChart}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Activity Trend Chart data:', err);
    throw err;
  }
};

export const fetchTableauLicenseUtilization = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauLicenseUtilization}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau License Utilization data:', err);
    throw err;
  }
};

export const fetchTableauLicenseUtilizationKpi = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauLicenseUtilizationKpi}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau License Utilization kpi data:', err);
    throw err;
  }
};

export const fetchTableauExportActivityDetails = async (requestBody: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauLicenseExportActivity}`, requestBody, { responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    let filename = `Tableau_License_Activity_Details.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    throw err;
  }
};

export const fetchTableauTotalReports = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauTotalReports}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Total Reports data:', err);
    throw err;
  }
};

export const fetchTableauReportUsage = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauReportUsage}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Report Usage data:', err);
    throw err;
  }
};

export const fetchTableauTopPublisherDetails = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauTopPublisher}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Top Publisher Details data:', err);
    throw err;
  }
};

export const fetchTableauReportsTrendData = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauReportsTrend}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Reports Trend data:', err);
    throw err;
  }
};

export const fetchTableauTotalCost = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauTotalCost}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Total Cost data:', err);
    throw err;
  }
};

export const fetchTableauCostByRole = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauCostByRole}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Cost By Role data:', err);
    throw err;
  }
};

export const fetchTableauCostBySector = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.tableauCostBySector}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Tableau Cost By Sector data:', err);
    throw err;
  }
};

export const executiveProgramProjectsUpdate = async (data: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.updateExecutiveProgram}`, data, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchPowerBIUserTrends = async (sector: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: sector
  };
  try {
    const res = await axios.post(`${url}${endpoints.deBiAppUserTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDEPowerBIDataset = async (sector: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: sector
  };
  try {
    const res = await axios.post(`${url}${endpoints.dePowerBIDatasetData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDEPowerBIUserAndReport = async (sector: any, kpiType: any, month: any) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    sector: sector,
    kpiField: kpiType,
    month: month
  };
  try {
    const res = await axios.post(`${url}${endpoints.dePowerBIUserAndReport}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const searchADBGoldTableNameList = async (
  workspaceName: string,
  goldTableName?: string,
) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    workspaceName,
    goldTableName
  };
  try {
    const res = await axios.post(`${url}${endpoints.searchADBGoldTableName}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProductCatalogOverview = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.productCatalogOverview}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Product Catalog Overview Data:', err);
    throw err;
  }
};

export const fetchUpcomingRenewals = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.upcomingRenewals}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Upcoming Renewals:', err);
    throw err;
  }
};

export const fetchBudgetVariance = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.budgetVariance}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Budget Variance:', err);
    throw err;
  }
};

export const fetchRenewalCost = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.renewalCost}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Renewal Cost:', err);
    throw err;
  }
};

export const fetchTotalCurrentSpend = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.totalCurrentSpend}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Total Current Spend:', err);
    throw err;
  }
};

export const fetchBillingCycleDistribution = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.billingCycleDistribution}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Total Current Spend:', err);
    throw err;
  }
};

export const fetchExpiredContracts = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.expiredContracts}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Expired Contracts:', err);
    throw err;
  }
};

export const fetchdataQualityBreakDown = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.dataQualityBreakDown}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching data quality breakdown:', err);
    throw err;
  }
};

export const fetchRenewalsTableData = async (params: any) => {
  try {
    const response = await axios.post(`${url}${endpoints.upcomingRenewalsTable}`, params);
    return response.data;
  } catch (error) {
    console.error('Error fetching renewals table data:', error);
    throw error;
  }
};

export const fetchProductDetailsCatalog = async (productId: string, params?: any) => {
  try {
    const response = await axios.get(`${url}${endpoints.catalogProductDetails}/${productId}`, params);
    return response.data;
  } catch (error) {
    console.error('Error fetching Product Details Catalog:', error);
    throw error;
  }
};

export const fetchRenewalsFilterOptions = async (field: string, searchTerm?: string, limit?: number) => {
  try {
    const params: any = {};
    if (searchTerm) params.search = searchTerm;
    if (limit) params.limit = limit;

    const response = await axios.get(`${url}${endpoints.upcomingRenewalsFilter}/${field}`, { params });
    return response.data.data;
  } catch (error) {
    console.error(`Error fetching ${field} filter options:`, error);
    throw error;
  }
};

export const fetchRenewalCategoryCounts = async () => {
  try {
    const response = await axios.get(`${url}${endpoints.upcomingCategoryCounts}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching renewal category counts:', error);
    throw error;
  }
};


export const exportRenewalsByOwner = async (requestBody: any) => {
  try {
    const res = await axios.post(`${url}${endpoints.renewalsByOwner}`, requestBody, {
      responseType: 'blob'
    });

    const contentDispositioning = res.headers['content-disposition'];
    let filename = `Renewals_By_Owner_Export.${requestBody.formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDispositioning) {
      const match = contentDispositioning.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }
    const exporturlRenwal = window.URL.createObjectURL(new Blob([res.data]));
    const linkForRenwal = document.createElement('a');
    linkForRenwal.href = exporturlRenwal;
    linkForRenwal.setAttribute('download', filename);
    document.body.appendChild(linkForRenwal);
    linkForRenwal.click();
    linkForRenwal.remove();

    return true;
  } catch (err) {
    console.error('Error exporting renewals data:', err);
    throw err;
  }
};


export const exportRenewalsData = async (requestBody: any) => {
  try {
    const res = await axios.post(`${url}${endpoints.upcomingRenewalsExport}`, requestBody, {
      responseType: 'blob'
    });

    const contentDisposition = res.headers['content-disposition'];
    let filename = `Renewals_Export.${requestBody.formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exporturl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting renewals data:', err);
    throw err;
  }
};

export interface FetchIncidentsByPrioritySummaryTrendParams {
  selectedDate: any;
  isClosedDate: any;
  timezoneFilter: string;
  sector?: string;
  type?: string;
  area?: string;
  domain?: string[];
  businessUnit?: string[];
  department?: string[];
  capability?: string[];
}

export const fetchIncidentsByPrioritySummaryTrend = async (params: FetchIncidentsByPrioritySummaryTrendParams) => {
  const { selectedDate, isClosedDate, timezoneFilter, sector, type, area, domain } = params;
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector
  };

  // Add domain filter if selected
  if (!domain?.includes('all')) {
    requestBody.domain = domain;
  }

  // Add new filters using type/area format
  if (type && area) {
    requestBody.type = type;
    requestBody.area = area;
  }
  try {
    const res = await axios.post(`${url}${endpoints.incidentsByPrioritySummary}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// Unified Alerts Dashboard
export const getUnifiedAlertsSummary = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/unifiedAlerts/summary`, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const getUnifiedAlertsList = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/unifiedAlerts/alerts`, payload, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const getUnifiedAlertsTrendBySector = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/unifiedAlerts/trend/sector`, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const getUnifiedAlertsTrendByProgram = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/unifiedAlerts/trend/program`, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const getUnifiedAlertsSources = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/unifiedAlerts/sources`, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};

export const getUnifiedAlertsFilterOptions = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}/unifiedAlerts/filterOptions`, { headers });
    return { success: true, statusCode: res.status, data: res.data };
  } catch (err: any) {
    return { success: false, statusCode: err.response?.status || 500, error: err.message };
  }
};


type StreamHandlers = {
  onConnected?: (msg: string) => void;
  onUpdate: (payload: any) => void;
  onCycleComplete?: (msg: string) => void;
  onError?: (err: any) => void;
};
const getSseHeaders = () => {
  const h = getAuthorazieHeader(); // includes authorization
  const headers: Record<string, string> = {
    Authorization: h.authorization, // normalize to proper case
  };

  // add id_token if your app has it somewhere (cookie/localStorage/etc.)
  const idToken = getCookie("id_token"); // replace source
  if (idToken) headers["id_token"] = idToken;

  return headers;
};

const parseAndCollectChunk = (rawText: string, chunks: any[], onChunk?: (chunk: any) => void) => {
  if (!rawText.trim()) return;
  try {
    const chunk = JSON.parse(rawText);
    onChunk?.(chunk);
    chunks.push(chunk);
  } catch (e) {
    console.error("Failed to parse chunk:", rawText, e);
  }
};

export const getPlatformHealthSummary = async (body: any, onChunk?: (chunk: any) => void): Promise<any> => {
  const headers: any = getAuthorazieHeader();
  try {
    const response = await fetch(`${url}/dataops/platformHealth/summary`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=UTF-8",
        "Authorization": headers.authorization,  // Capital A + use the value directly
        "id_token": getCookie(sessionCookie) || "",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error("ReadableStream not supported");

    const decoder = new TextDecoder();
    let chunks: any[] = [];
    let buffer = "";

    while (true) {
      const { value, done } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";

      for (const line of lines) {
        parseAndCollectChunk(line, chunks, onChunk);
      }
    }

    // flush remaining buffer
    parseAndCollectChunk(buffer, chunks, onChunk);

    if (chunks.length === 1) {
      return { success: true, data: chunks[0] };
    }

    return { success: true, data: chunks };
  } catch (err) {
    console.error("getPlatformHealthSummary streaming error:", err);
    return err;
  }
};

export const openPlatformHealthSummaryStream = (handlers: StreamHandlers) => {
  const headers: any = getAuthorazieHeader(); // Returns { authorization: "Bearer ..." }
  const jwtToken = getCookie(sessionCookie);
  if (jwtToken) {
    headers['id_token'] = jwtToken;
  }
  const es = new EventSourcePolyfill(`${url}/dataOps/platformHealth/monitor`, {
    headers: getSseHeaders(),
  });

  es.addEventListener("connected", (e: MessageEvent) => {
    handlers.onConnected?.(String(e.data));
  });

  es.addEventListener("update", (e: MessageEvent) => {
    try {
      const payload = JSON.parse(String(e.data));
      handlers.onUpdate(payload);
    } catch (err) {
      console.error("SSE update parse error:", err, e.data);
    }
  });

  es.addEventListener("cycle-complete", (e: MessageEvent) => {
    handlers.onCycleComplete?.(String(e.data));
  });

  es.onerror = (err: any) => {
    console.error("SSE error:", err);
    handlers.onError?.(err);
  };
  return () => es.close();
};

export const fetchProductSuccessUsersRoleUsageTrend = async (filterOption?: string, filterMonth?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterOption: filterOption,
    filterMonth: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.getUserRoleUsageTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getPlatformHealthDetail = async (body: any): Promise<any> => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/dataOps/platformHealth/getDetail`, body, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getPlatformHealthColumnFilterValues = async (body: any): Promise<any[]> => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/dataOps/platformHealth/columnFilter`, body, { headers });
    return res.data?.data?.[body.field] ?? [];
  } catch (err) {
    console.error("Failed to load platform health column values:", err);
    return [];
  }
};

export const fetchProductSuccessUserAvgTimeInPlatform = async (filterOption?: string, filterMonth?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterOption: filterOption,
    filterMonth: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.getUserAvgTimeInPlatform}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProductSuccessUserAdoptionRate = async (filterOption?: string, filterMonth?: string) => {
  const headers = getAuthorazieHeader();
  const requestBody = {
    filterOption: filterOption,
    filterMonth: filterMonth
  };
  try {
    const res = await axios.post(`${url}${endpoints.getUserAdoptionRate}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProductSuccessAdoptionRateTrend = async () => {
  try {
    const res = await apiPost(`${url}/productSuccess/adoptionRateTrend`);
    return res.data;
  } catch (err) {
    console.error('Error fetching ProductSuccess Adoption Rate Trend data:', err);
    return null;
  }
};

export const fetchSummaryMetricsTrend = async (selectedDate: any, isClosedDate: any, timezoneFilter: string, sector?: string, type?: string, area?: string, domain?: string[], businessUnit?: string[], department?: string[], capability?: string[], program?: string[], assignmentGroupList?: string[]) => {
  const headers = getAuthorazieHeader();
  const requestBody: any = {
    dateFilter: selectedDate,
    isClosedDate: isClosedDate,
    timezoneFilter: timezoneFilter,
    sector: sector,
    domain: domain,
    businessUnit: businessUnit,
    department: department,
    capability: capability,
    program: program,
    assignmentGroupList: assignmentGroupList
  };

  // Add domain filter if selected
  if (!domain?.includes('all')) {
    requestBody.domain = domain;
  }

  try {
    const res = await axios.post(`${url}${endpoints.getAllSummaryMetricTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchProductSuccessMostVisitedPage = async (filterOption?: string, filterMonth?: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(
      `${url}${endpoints.getMostVisitedPage}`,
      { filterOption, filterMonth },
      { headers }
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchDataQualityAndRiskKpis = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.dataQualityAndRiskKpis}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Data Quality and Risk KPIs:', err);
    throw err;
  }
};

export const fetchCatalogueDataCompleteness = async (requestBody?: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.catalogueDataCompleteness}`, requestBody || {});
    return res.data;
  } catch (err) {
    console.error('Error fetching Catalogue Data Completeness:', err);
    throw err;
  }
};

export const fetchFilterDetails = async (params: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.filterDetails}`, params, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchSummary = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.summaryData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchScheduleKpi = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.scheduleKpiData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchCustomStatusCountKpi = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.customStatusKpiData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchJobsList = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const isScheduledFilter = requestBody?.pageFilters?.jobStatus?.includes('Scheduled');
    const endpoint = isScheduledFilter ? endpoints.jobsListScheduled : endpoints.jobsList;

    const res = await axios.post(`${url}${endpoint}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchJobStatusByType = async (requestBody: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.jobStatusbyType}`, requestBody);
    return res.data;
  } catch (err) {
    console.error('Error fetching job status by type:', err);
    throw err;
  }
};

export const fetchJobScheduleByHour = async (requestBody: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.jobScheduleByHour}`, requestBody);
    return res.data;
  } catch (err) {
    console.error('Error fetching job schedule by hour:', err);
    throw err;
  }
};

export const exportJobsList = async (requestBody: any): Promise<any> => {
  try {
    const headers = getAuthorazieHeader();
    const isScheduledFilter = requestBody?.pageFilters?.jobStatus?.includes('Scheduled');
    const endpoint = isScheduledFilter ? endpoints.jobsListScheduledExport : endpoints.jobsListExport;

    const res = await axios.post(`${url}${endpoint}`, requestBody, { headers, responseType: "blob" });

    // Extract filename from response headers
    const contentDisposition = res.headers["content-disposition"];
    const name = FILE_LABELS(requestBody.activeJobStatus);
    let filename = `${name}_${new Date().toISOString()}.${requestBody.formatType === "EXCEL" ? "xlsx" : "csv"}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }
    if (requestBody?.dataAssetMode) {
      if (!filename.startsWith('Integrated_Job_Monitoring_Data_Assets')) {
        filename = `Integrated_Job_Monitoring_Data_Assets_${filename}`;
      }
    } else if (
      !isScheduledFilter &&
      !filename.startsWith('Integrated_Job_Monitoring_')
    ) {
      filename = `Integrated_Job_Monitoring_${filename}`;
    }

    // Create a download link for the file
    const exporturl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exporturl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();

    return true;
  } catch (err) {
    console.error('Error exporting table data:', err);
    throw err;
  }
};

export const getRunDistribution = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.runDistribution}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Failed to run distribution:', err);
    throw err;
  }
};

export const getRunTrend = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.runTrend}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Failed to run trend:', err);
    throw err;
  }
};

export const getRunTrendAvg = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.runTrendAvg}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Failed to run trend avg:', err);
    throw err;
  }
};

export const fetchMissingProductDetailsTableData = async (params: any) => {
  try {
    const response = await axios.post(`${url}${endpoints.missingDetails}`, params);
    return response.data;
  } catch (error) {
    console.error('Error fetching Missing table data:', error);
    throw error;
  }
};

export const exportMissingDetails = async (requestBody: any) => {
  try {
    const res = await axios.post(`${url}${endpoints.exportMissingData}`, requestBody, {
      responseType: 'blob'
    });

    const contentDispositioningForMissing = res.headers['content-disposition'];
    let filename = `missing_details_Export.${requestBody.formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDispositioningForMissing) {
      const match = contentDispositioningForMissing.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }
    const exporturlMissing = window.URL.createObjectURL(new Blob([res.data]));
    const linkForMissing = document.createElement('a');
    linkForMissing.href = exporturlMissing;
    linkForMissing.setAttribute('download', filename);
    document.body.appendChild(linkForMissing);
    linkForMissing.click();
    linkForMissing.remove();

    return true;
  } catch (err) {
    console.error('Error exporting Missing data:', err);
    throw err;
  }
};

export const getADFMetricMonitor = async (): Promise<any> => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}/dataops/platformHealth/adfMonitor`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};


export const loadDashboardFilters = async (
  config: DashboardFilterConfig
): Promise<DashboardFilterResponse | null> => {
  try {
    const response = await axios.post(`${url}/dashboard-filters/loadDefaultFilter`, config, {
      headers: getAuthorazieHeader()
    });
    return response.data.data;
  } catch (err) {
    console.error(`Error loading filters for ${config.dashboardId}:`, err);
    return null;
  }
};

export const saveDashboardFilters = async (
  config: DashboardFilterConfig,
  filters: any
): Promise<any> => {
  try {
    const response = await axios.post(`${url}/dashboard-filters/saveDefaultFilter`, {
      ...config,
      filters
    }, {
      headers: getAuthorazieHeader()
    });
    return response.data.data;
  } catch (err) {
    console.error(`Error saving filters for ${config.dashboardId}:`, err);
    throw err;
  }
};


export const deleteDashboardFilters = async (dashboardId: string): Promise<any> => {
  try {
    const response = await axios.post(`${url}/dashboard-filters/deleteDefaultFilter`, {
      dashboardId
    }, {
      headers: getAuthorazieHeader()
    });
    return response.data.data;
  } catch (err) {
    console.error(`Error deleting filters for ${dashboardId}:`, err);
    throw err;
  }
};

export const fetchGroupChildren = async (params: any) => {
  try {
    const response = await axios.post(`${url}${endpoints.pipelineGroupByChildren}`, params);
    return response.data;
  } catch (error) {
    console.error('Error fetching group children:', error);
    throw error;
  }
};

export const fetchPlatformHealthGroupChildren = async (params: any) => {
  try {
    const response = await axios.post(`${url}${endpoints.platformHealthGroupChildren}`, params);
    return response.data;
  } catch (error) {
    console.error('Error fetching group children:', error);
    throw error;
  }
};

export const copyProjectToProgram = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.copyProjectToProgram}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};
export const getCrossFlowData = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.crossflowData}`, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching crossflow data:', err);
    return { assets: [], edges: [] };
  }
};
export const fetchExportCount = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.exportCount}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const uploadJobMetadata = async (formData: any) => {
  try {
    return (await apiPost(`${url}${endpoints.uploadJobMetadata}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })).data;
  } catch (err) {
    console.error('Error uploading job metadata:', err);
    throw err;
  }
};

export const fetchJobMetadataTableData = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.JobMetaDataTable}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching job metadata table data:', err);
    throw err;
  }
};

export const fetchJobMetadataFilterOptions = async (requestBody: any) => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${url}${endpoints.JobMetaDataFilterOptions}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching job metadata filter options:', err);
    throw err;
  }
};

export const fetchJobMetadataFields = async () => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.get(`${url}${endpoints.JobMetaDataFields}`, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching job metadata fields:', err);
    throw err;
  }
};

export const fetchJobMetadataExport = async (requestBody: any) => {
  try {
    const res = await apiPost(`${url}${endpoints.JobMetaDataExport}`, requestBody, {
      responseType: "blob"
    });

    const contentDisposition = res.headers["content-disposition"];
    let filename = `Job_Metadata_Export_${new Date().toISOString().slice(0, 10)}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const exportUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement("a");
    link.href = exportUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(exportUrl);

    return true;
  } catch (err) {
    console.error('Error exporting job metadata:', err);
    throw err;
  }
};

export const fetchJobListSLAConfig = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.jobListSlaConfig}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching job list SLA config:', err);
    throw err;
  }
};

export const saveJobSlaConfig = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.saveJobSlaConfig}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error saving job SLA config:', err);
    return err;
  }
};

export const deleteJobSLAConfig = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.deleteJobSlaConfig}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error deleting job SLA config:', err);
    return err;
  }
};

// ── Session / conversation history ────────────────────────────────────────────

/**
 * Fetch conversation history for a session.
 * Called on chat popup open to reload previous turns.
 * Returns turns in chronological order (oldest first).
 */
export const getSessionHistory = async (sessionId: string, limit = 50): Promise<any[]> => {
  const headers = getAuthorazieHeader();
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return [];

  try {
    const res = await fetch(
      `${aiPilotBaseUrl}/sessions/${encodeURIComponent(sessionId)}/history?limit=${limit}`,
      { method: 'GET', headers }
    );
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
};

/**
 * List all sessions for the current user, newest first.
 * Used to populate the sessions sidebar in the expanded chat popup.
 * Each item: { session_id, created_at, updated_at, preview }
 */
export const listSessions = async (limit = 20): Promise<any[]> => {
  const headers = getAuthorazieHeader();
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return [];

  try {
    const res = await fetch(
      `${aiPilotBaseUrl}/sessions?limit=${limit}`,
      { method: 'GET', headers }
    );
    if (!res.ok) return [];
    return await res.json();
  } catch {
    return [];
  }
};

/**
 * Delete a session and its conversation history.
 * Called when the user clicks 'Clear Chat'.
 * After this, the frontend should generate a fresh session_id.
 */
export const deleteSession = async (sessionId: string): Promise<void> => {
  const headers = getAuthorazieHeader();
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return;

  try {
    await fetch(
      `${aiPilotBaseUrl}/sessions/${encodeURIComponent(sessionId)}`,
      { method: 'DELETE', headers }
    );
  } catch {
    // Silently ignore — Clear Chat should always succeed from the user's perspective
  }
};

/**
 * Pin or unpin a session.
 */
export const pinSession = async (sessionId: string, isPinned: boolean): Promise<void> => {
  const headers = getAuthorazieHeader();
  headers['Content-Type'] = 'application/json';
  const aiPilotBaseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  if (!aiPilotBaseUrl) return;

  try {
    await fetch(
      `${aiPilotBaseUrl}/sessions/${encodeURIComponent(sessionId)}/pin`,
      { 
        method: 'PATCH', 
        headers,
        body: JSON.stringify({ is_pinned: isPinned })
      }
    );
  } catch {
    // Ignore errors for now
  }
};


export const fetchIbpAdvanceFilters = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpAdvanceFilters}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getIbpGdoInfo = async (programId: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpGdoInfo}`, { programId }, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchActiveIbpGdoList = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.activeIbpGdoList}`, {}, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const editIbpGdo = async (programId: string, updateData: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(
      `${urlV2}${endpoints.ibpGdoEdit}`,
      { programId, updateData },
      { headers },
    );
    return res.data;
  } catch (err) {
    return err;
  }
};

export const deleteIbpGdo = async (programId: string) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpGdoDelete}`, { programId }, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const addIbpGdoDependency = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.addProgramDependency}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const removeIbpGdoDependency = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.removeProgramDependency}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const fetchIbpGdoList = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpGdoList}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportIbpGdoList = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpGdoListExport}`, payload, {
      headers,
      responseType: 'blob',
    });

    const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
    let filename = `gdo-list-export-${moment().tz('America/Chicago').format('DD-MM-YYYY HH:mm:ss')}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const exportUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exportUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(exportUrl);

    return true;
  } catch (err) {
    console.error('Error exporting IBP GDO list:', err);
    throw err;
  }
};

export const getGdoKpi = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpGdoKpi}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getStageList = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpStageList}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const upsertIbpStage = async (payload: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpStageEdit}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportIbpStageList = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpStageListExport}`, payload, {
      headers,
      responseType: 'blob',
    });

    const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
    let filename = `stage-list-export-${moment().tz('America/Chicago').format('DD-MM-YYYY HH:mm:ss')}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const exportUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exportUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(exportUrl);

    return true;
  } catch (err) {
    console.error('Error exporting IBP stage list:', err);
    throw err;
  }
};

export const getStageKpi = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpStageKpi}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getIbpJobExecutionData = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpJobExecution}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportIbpJobExecution = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpJobExecutionExport}`, payload, {
      headers,
      responseType: 'blob',
    });

    const contentDisposition = res.headers['content-disposition'] || res.headers['Content-Disposition'];
    let filename = `stage-job-execution-export-${moment().tz('America/Chicago').format('DD-MM-YYYY HH:mm:ss')}.xlsx`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
      if (match) {
        filename = match[1];
      }
    }

    const exportUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exportUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(exportUrl);

    return true;
  } catch (err) {
    console.error('Error exporting IBP job executions:', err);
    throw err;
  }
};

export const getIbpAlertProgramHierarchy = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.ibpAlertProgramHierarchy}`, {}, { headers });
    return { success: true, statusCode: res.status, data: res.data?.data ?? res.data ?? [] };
  } catch (err: any) {
    console.error('getIbpAlertProgramHierarchy Error:', err);
    return { success: false, statusCode: err.response?.status || 500, error: err.message, data: [] };
  }
};

//Cloud spend finops reports
export const getCloudSpendTotalByService = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.cloudSpendTotalByService}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch total cloud spend by service:',
			err,
		);

		throw err;
	}
};

export const getCloudSpendServiceCostByMonth = async (
	requestBody: {
		program?: string;
		env?: string;
		division?: string;
    sortBy?: string;
	  sortOrder?: 'asc' | 'desc';
	},
) => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.post(
			`${url}${endpoints.cloudSpendServiceCostByMonth}`,
			requestBody,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch cloud spend service cost by month:',
			err,
		);

		throw err;
	}
};

export const getCloudSpendFilters = async (program: string | null, market: string | null) => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.cloudSpendFilters}`,
			{ headers,
        params: { program, market }
       },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch cloud spend filter options:',
			err,
		);

		throw err;
	}
};

export const getProgramCostTrendTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.ProgramCostTrendTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch program cost trend table data:',
			err,
		);

		throw err;
	}
};

export const getProgramCostLine = async (
	requestBody: {
		env?: string;
		division?: string;
    market?: string;
	},
) => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.ProgramCostLine}`,
			{
				headers,
				params: requestBody,
			},
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch program cost line data:',
			err,
		);

		throw err;
	}
};

export const getServiceCostTable = async (
	requestBody: {
		program?: string;
		env?: string;
		division?: string;
    market?: string;
		sortBy?: string;
		sortOrder?: 'asc' | 'desc';
	},
) => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.ServiceCostTable}`,
			{
				headers,
				params: requestBody,
			},
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch service cost table data:',
			err,
		);

		throw err;
	}
};

export const getEnvCostPie = async (
	requestBody: {
		program?: string;
		division?: string;
    market?: string;
	},
) => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.EnvCostPie}`,
			{
				headers,
				params: requestBody,
			},
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch environment cost pie data:',
			err,
		);

		throw err;
	}
};

export const getConsumptionMetricTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.ConsumptionMetricTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch consumption metric table data:',
			err,
		);

		throw err;
	}
};

export const getCostAvoidanceTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.CostAvoidanceTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch cost avoidance table data:',
			err,
		);

		throw err;
	}
};

export const getPreAopCostSummaryTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.PreAopCostSummaryTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch pre-AOP cost summary table data:',
			err,
		);

		throw err;
	}
};

export const getPreAopConsumptionMetricsTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.PreAopConsumptionMetricsTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch pre-AOP consumption metrics table data:',
			err,
		);

		throw err;
	}
};

export const getPreAopDetailsTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.PreAopDetailsTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch pre-AOP details table data:',
			err,
		);

		throw err;
	}
};

export const getDissectionTable = async () => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.get(`${url}${endpoints.dissectionTable}`, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getEdfBenefitsTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.edfBenefitsTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch EDF benefits table data:',
			err,
		);

		throw err;
	}
};

export const getEdfCatalogingTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.edfCatalogingTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch EDF cataloging table data:',
			err,
		);

		throw err;
	}
};

export const getEdfCostOverviewTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.edfCostOverviewTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch EDF cost overview table data:',
			err,
		);

		throw err;
	}
};

export const getEdfHeatMapTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.edfHeatMapTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch EDF heat map table data:',
			err,
		);

		throw err;
	}
};

export const getEdfPriorityMarketsTable = async () => {
	const headers = getAuthorazieHeader();

	try {
		const res = await axios.get(
			`${url}${endpoints.edfPriorityMarketsTable}`,
			{ headers },
		);

		return res.data;
	} catch (err) {
		console.error(
			'Failed to fetch EDF priority markets table data:',
			err,
		);

		throw err;
	}
};

export const fetchE2EFilterDetails = async (payload: any = {}) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.dataPipelineE2EFilterDetails}`, payload, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const getE2EDashboardData = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.getExecutiveDashboardData}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

// ──────────────────── XOps Intelligence ────────────────────

export const fetchXopsIntelligenceDashboards = async () => {
	const headers = getAuthorazieHeader();
	try {
		const res = await axios.get(`${url}${endpoints.xopsIntelligenceDashboards}`, { headers });
		return res.data;
	} catch (err) {
		console.error('Error fetching XOps Intelligence dashboards:', err);
		throw err;
	}
};

// PepIris
export const fetchDataReconciliation = async (requestBody: any) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${urlV2}${endpoints.dataReconciliation}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    return err;
  }
};

export const exportDataReconciliation = async (requestBody: any): Promise<any> => {
  try {
    const headers = getAuthorazieHeader();
    const res = await axios.post(`${urlV2}${endpoints.dataReconciliationExport}`, requestBody, {
      headers,
      responseType: 'blob',
    });

    const contentDisposition = res.headers['content-disposition'];
    const keyName = Array.isArray(requestBody?.key) ? requestBody.key[0] : requestBody?.key;
    const name = keyName === 'Master' ? 'Master Data Recon' : 'Transactional Data Recon';
    let filename = `${name}_${new Date().toISOString()}.${requestBody.formatType === 'EXCEL' ? 'xlsx' : 'csv'}`;

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^\"]+)"?/);
      if (match) {
        filename = match[1];
      }
    }

    const exportUrl = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = exportUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(exportUrl);

    return true;
  } catch (err) {
    console.error('Error exporting Data Reconciliation table:', err);
    throw err;
  }
};

// ===================== Genie AI Chat =====================
// Routes to pepwise-aipilot-cloud-service-db-genie

export const genieStartConversation = async (question: string, spaceId?: string) => {
  const headers = getAuthorazieHeader();
  const baseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  try {
    const res = await fetch(`${baseUrl}/genie/ask`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ question, ...(spaceId ? { space_id: spaceId } : {}) }),
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  } catch (err) {
    return err;
  }
};

export const genieGetMessage = async (conversationId: string, messageId: string, spaceId?: string) => {
  const headers = getAuthorazieHeader();
  const baseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  const spaceQuery = spaceId ? `?space_id=${encodeURIComponent(spaceId)}` : '';
  try {
    const res = await fetch(
      `${baseUrl}/genie/messages/${encodeURIComponent(conversationId)}/${encodeURIComponent(messageId)}${spaceQuery}`,
      { method: 'GET', headers }
    );
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  } catch (err) {
    return err;
  }
};

export const genieSendFollowup = async (conversationId: string, question: string, spaceId?: string) => {
  const headers = getAuthorazieHeader();
  const baseUrl = (aiPilotUrl || '').trim().replace(/\/$/, '');
  try {
    const res = await fetch(`${baseUrl}/genie/followup`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ conversation_id: conversationId, question, ...(spaceId ? { space_id: spaceId } : {}) }),
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  } catch (err) {
    return err;
  }
};

export const fetchBanner = async (requestBody: { dashboardId?: number; dashboardName?: string; dashboardUrl?: string }) => {
  const headers = getAuthorazieHeader();
  try {
    const res = await axios.post(`${url}${endpoints.bannerDetails}`, requestBody, { headers });
    return res.data;
  } catch (err) {
    console.error('Error fetching banner:', err);
    return err;
  }
};
